

/**
 * DataNode - 通用数据节点组件
 * 用于以 HTML 元素形式表示嵌套的数据结构
 */
class DataNode extends HTMLElement {
  constructor() {
    super();
    // 这个组件只是数据容器，不需要渲染任何内容
  }
  
  /**
   * 递归解析数据节点及其子节点
   * @returns {Object|Array} 解析后的数据对象或数组
   */
  parseData() {
    // 检查是否为数组类型
    const isArray = this.getAttribute('data-type') === 'array';
    
    // 如果是数组，创建空数组，否则创建空对象
    const result = isArray ? [] : {};
    
    // 解析属性（键值对）
    if (!isArray) {
      Array.from(this.attributes).forEach(attr => {
        // 排除特殊属性
        if (!attr.name.startsWith('data-')) {
          result[attr.name] = this.parseValue(attr.value);
        }
      });
    }
    
    // 解析子节点
    const childNodes = this.querySelectorAll(':scope > data-node');
    
    childNodes.forEach(childNode => {
      const childData = childNode.parseData();
      
      if (isArray) {
        // 如果当前节点是数组，直接添加子节点数据
        result.push(childData);
      } else {
        // 否则，使用键名添加
        const key = childNode.getAttribute('key');
        if (key) {
          // 检查子节点是否为数组
          const childIsArray = childNode.getAttribute('data-type') === 'array';
          
          // 如果子节点有键名，将其解析的数据添加到结果对象中
          result[key] = childData;
        } else {
          // 如果没有键名，尝试合并对象（适用于扁平化数据）
          Object.assign(result, childData);
        }
      }
    });
    
    return result;
  }
  
  /**
   * 解析字符串值为适当的类型
   * @param {string} value 要解析的字符串值
   * @returns {any} 解析后的值
   */
  parseValue(value) {
    // 尝试解析布尔值
    if (value === 'true') return true;
    if (value === 'false') return false;
    
    // 尝试解析数字
    if (!isNaN(value) && value.trim() !== '') {
      // 检查是否为整数
      if (parseInt(value).toString() === value) {
        return parseInt(value);
      }
      // 检查是否为浮点数
      return parseFloat(value);
    }
    
    // 尝试解析 JSON
    try {
      return JSON.parse(value);
    } catch (e) {
      // 如果不是有效的 JSON，返回原始字符串
      return value;
    }
  }
}
customElements.define('data-node', DataNode);

class GenericSection extends DataNode {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // 创建模板
    this.shadowRoot.innerHTML = `
      <style>
        /* 重置样式 */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        /* 主容器样式 */
        .generic-section {
          max-width: var(--section-max-width, 1200px);
          margin: 0 auto;
          padding: var(--section-padding, 40px 20px);
          text-align: var(--section-text-align, center);
          background-color: var(--section-bg-color, transparent);
        }
        
        /* 标题样式 */
        .section-title {
          font-size: var(--title-font-size, 32px);
          font-weight: var(--title-font-weight, 500);
          color: var(--theme-color, #FFA200);
          margin-bottom: var(--title-margin-bottom, 10px);
          display: var(--title-display, block);
        }
        
        /* 小标题样式 */
        .section-subtitle {
          font-size: var(--subtitle-font-size, 16px);
          font-weight: var(--subtitle-font-weight, 400);
          color: var(--subtitle-color, #666);
          margin-bottom: var(--subtitle-margin-bottom, 30px);
          display: var(--subtitle-display, block);
        }
        
        /* 内容容器 */
        .section-content {
          margin-bottom: var(--content-margin-bottom, 40px);
        }
        
        /* 按钮样式 */
        .section-button {
          background-color: var(--theme-color, #FFA200);
          color: var(--button-text-color, white);
          border: none;
          padding: var(--button-padding, 12px 30px);
          font-size: var(--button-font-size, 16px);
          font-weight: var(--button-font-weight, 500);
          border-radius: var(--button-border-radius, 5px);
          cursor: pointer;
          text-transform: var(--button-text-transform, uppercase);
          letter-spacing: var(--button-letter-spacing, 1px);
          transition: background-color 0.3s;
          display: var(--button-display, inline-block);
        }
        
        .section-button:hover {
          background-color: var(--button-hover-color, #D98600);
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .generic-section {
            padding: 10px 5px;
          }
          .section-title {
            font-size: var(--title-font-size-mobile, 28px);
          }
          
          .section-subtitle {
            font-size: var(--subtitle-font-size-mobile, 14px);
          }
          
          .section-button {
            padding: var(--button-padding-mobile, 10px 25px);
            font-size: var(--button-font-size-mobile, 14px);
            width: var(--button-width-mobile, auto);
            max-width: var(--button-max-width-mobile, none);
          }
        }
        
        @media (max-width: 480px) {
          .section-title {
            font-size: var(--title-font-size-small, 24px);
          }
          
          .section-subtitle {
            font-size: var(--subtitle-font-size-small, 14px);
          }
          
          .section-button {
            padding: var(--button-padding-small, 10px 20px);
          }
        }
      </style>
      <section class="generic-section">
        <h2 class="section-title"></h2>
        <p class="section-subtitle"></p>
        <div class="section-content">
          <slot></slot>
        </div>
        <button class="section-button"></button>
      </section>
    `;
    
    // 获取元素
    this.sectionElement = this.shadowRoot.querySelector('.generic-section');
    this.titleElement = this.shadowRoot.querySelector('.section-title');
    this.subtitleElement = this.shadowRoot.querySelector('.section-subtitle');
    this.buttonElement = this.shadowRoot.querySelector('.section-button');
  }
  
  connectedCallback() {
    this.updateStyles();
    this.updateContent();
    
    // 设置按钮链接
    const buttonLink = this.getAttribute('button-link');
    if (buttonLink) {
      this.buttonElement.addEventListener('click', () => {
        window.location.href = buttonLink;
      });
    }
    
    // 防止重复渲染
    this.setAttribute('rendered', 'true');
  }
  
  updateStyles() {
    // 设置主题色
    const themeColor = this.getAttribute('theme-color') || '#FFA200';
    this.sectionElement.style.setProperty('--theme-color', themeColor);
    
    // 设置背景色
    const bgColor = this.getAttribute('bg-color');
    if (bgColor) {
      this.sectionElement.style.setProperty('--section-bg-color', bgColor);
    }
    
    // 设置最大宽度
    const maxWidth = this.getAttribute('max-width');
    if (maxWidth) {
      this.sectionElement.style.setProperty('--section-max-width', maxWidth);
    }
    
    // 设置内边距
    const padding = this.getAttribute('padding');
    if (padding) {
      this.sectionElement.style.setProperty('--section-padding', padding);
    }
    
    // 设置文本对齐
    const textAlign = this.getAttribute('text-align');
    if (textAlign) {
      this.sectionElement.style.setProperty('--section-text-align', textAlign);
    }
    
    // 设置小标题颜色
    const subtitleColor = this.getAttribute('subtitle-color');
    if (subtitleColor) {
      this.sectionElement.style.setProperty('--subtitle-color', subtitleColor);
    }
    
    // 按钮悬停颜色
    const buttonHoverColor = this.getAttribute('button-hover-color') || this.calculateDarkerColor(themeColor);
    this.sectionElement.style.setProperty('--button-hover-color', buttonHoverColor);
    
    // 隐藏元素
    if (this.getAttribute('hide-title') === 'true') {
      this.sectionElement.style.setProperty('--title-display', 'none');
    }
    
    if (this.getAttribute('hide-subtitle') === 'true') {
      this.sectionElement.style.setProperty('--subtitle-display', 'none');
    }
    
    if (this.getAttribute('hide-button') === 'true') {
      this.sectionElement.style.setProperty('--button-display', 'none');
    }
    
    // 应用自定义CSS变量
    this.applyCustomCSSVariables();
  }
  
  updateContent() {
    // 设置标题
    const title = this.getAttribute('title') || '';
    this.titleElement.textContent = title;
    
    // 设置小标题
    const subtitle = this.getAttribute('subtitle') || '';
    this.subtitleElement.textContent = subtitle;
    
    // 设置按钮文本
    const buttonText = this.getAttribute('button-text') || 'Shop Now';
    this.buttonElement.textContent = buttonText;
  }
  
  // 应用自定义CSS变量
  applyCustomCSSVariables() {
    // 获取所有属性
    const attributes = this.attributes;
    
    // 遍历属性，查找以"css-"开头的属性
    for (let i = 0; i < attributes.length; i++) {
      const attr = attributes[i];
      if (attr.name.startsWith('css-')) {
        // 将属性名转换为CSS变量名（例如：css-title-color -> --title-color）
        const cssVarName = '--' + attr.name.substring(4);
        this.sectionElement.style.setProperty(cssVarName, attr.value);
      }
    }
  }
  
  // 计算按钮悬停颜色（默认比主题色暗15%）
  calculateDarkerColor(hex) {
    // 移除#号并转换为RGB
    hex = hex.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // 使颜色暗15%
    r = Math.max(0, Math.floor(r * 0.85));
    g = Math.max(0, Math.floor(g * 0.85));
    b = Math.max(0, Math.floor(b * 0.85));
    
    // 转回十六进制
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
  
  // 监听属性变化
  static get observedAttributes() {
    return [
      'theme-color', 'bg-color', 'max-width', 'padding', 'text-align',
      'title', 'subtitle', 'subtitle-color', 'button-text', 'button-link',
      'hide-title', 'hide-subtitle', 'hide-button'
    ];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    if (this.hasAttribute('rendered')) {
      if (name.startsWith('css-') || ['theme-color', 'bg-color', 'max-width', 'padding', 'text-align', 'subtitle-color', 'hide-title', 'hide-subtitle', 'hide-button'].includes(name)) {
        this.updateStyles();
      }
      
      if (['title', 'subtitle', 'button-text'].includes(name)) {
        this.updateContent();
      }
    }
  }
}
customElements.define('generic-section', GenericSection);




class NewGenericSection extends DataNode {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // 创建模板
    this.shadowRoot.innerHTML = `
      <style>
        /* 重置样式 */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        /* 主容器样式 */
        .generic-section {
          max-width: var(--section-max-width, 1200px);
          margin: 0 auto;
          padding: var(--section-padding, 40px 20px);
          text-align: var(--section-text-align, center);
          background-color: var(--section-bg-color, transparent);
        }
        
        /* 标题样式 */
        .section-title {
          font-size: var(--title-font-size, 32px);
          font-weight: var(--title-font-weight, 500);
          color: var(--theme-color, #FFA200);
          margin-bottom: var(--title-margin-bottom, 10px);
          display: var(--title-display, block);
        }
        
        /* 小标题样式 */
        .section-subtitle {
          font-size: var(--subtitle-font-size, 16px);
          font-weight: var(--subtitle-font-weight, 400);
          color: var(--subtitle-color, #666);
          margin-bottom: var(--subtitle-margin-bottom, 30px);
          display: var(--subtitle-display, block);
        }
        
        /* 内容容器 */
        .section-content {
          margin-bottom: var(--content-margin-bottom, 40px);
        }
        
        /* 按钮容器 */
        .buttons-container {
          display: flex;
          flex-wrap: wrap;
          gap: var(--buttons-gap, 15px);
          justify-content: var(--buttons-justify, center);
          margin-bottom: var(--buttons-margin-bottom, 0);
        }
        
        /* 按钮样式 */
        .section-button {
          background-color: var(--theme-color, #FFA200);
          color: var(--button-text-color, white);
          border: none;
          padding: var(--button-padding, 12px 30px);
          font-size: var(--button-font-size, 16px);
          font-weight: var(--button-font-weight, 500);
          border-radius: var(--button-border-radius, 5px);
          cursor: pointer;
          text-transform: var(--button-text-transform, uppercase);
          letter-spacing: var(--button-letter-spacing, 1px);
          transition: background-color 0.3s, transform 0.2s;
          display: var(--button-display, inline-block);
          text-decoration: none;
          text-align: center;
        }
        
        .section-button:hover {
          background-color: var(--button-hover-color, #D98600);
          transform: translateY(-2px);
        }
        
        /* 次要按钮样式 */
        .section-button.secondary {
          background-color: var(--secondary-button-bg, transparent);
          color: var(--secondary-button-color, #FFA200);
          border: 2px solid var(--secondary-button-border, #FFA200);
        }
        
        .section-button.secondary:hover {
          background-color: var(--secondary-button-hover-bg, rgba(255, 162, 0, 0.1));
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .generic-section {
            padding: var(--section-padding-mobile, 30px 15px);
          }
          
          .section-title {
            font-size: var(--title-font-size-mobile, 28px);
          }
          
          .section-subtitle {
            font-size: var(--subtitle-font-size-mobile, 14px);
          }
          
          .buttons-container {
            flex-direction: var(--buttons-direction-mobile, row);
            gap: var(--buttons-gap-mobile, 10px);
          }
          
          .section-button {
            padding: var(--button-padding-mobile, 10px 25px);
            font-size: var(--button-font-size-mobile, 14px);
            width: var(--button-width-mobile, auto);
            max-width: var(--button-max-width-mobile, none);
            flex: var(--button-flex-mobile, 0 1 auto);
          }
        }
        
        @media (max-width: 480px) {
          .section-title {
            font-size: var(--title-font-size-small, 24px);
          }
          
          .section-subtitle {
            font-size: var(--subtitle-font-size-small, 14px);
          }
          
          .buttons-container {
            flex-direction: var(--buttons-direction-small, column);
            width: var(--buttons-width-small, 100%);
            max-width: var(--buttons-max-width-small, 300px);
            margin-left: auto;
            margin-right: auto;
          }
          
          .section-button {
            padding: var(--button-padding-small, 10px 20px);
            width: var(--button-width-small, 100%);
          }
        }
      </style>
      <section class="generic-section">
        <h2 class="section-title"></h2>
        <p class="section-subtitle"></p>
        <div class="section-content">
          <slot></slot>
        </div>
        <div class="buttons-container">
          <!-- 按钮将在JS中动态添加 -->
        </div>
      </section>
    `;
    
    // 获取元素
    this.sectionElement = this.shadowRoot.querySelector('.generic-section');
    this.titleElement = this.shadowRoot.querySelector('.section-title');
    this.subtitleElement = this.shadowRoot.querySelector('.section-subtitle');
    this.buttonsContainer = this.shadowRoot.querySelector('.buttons-container');
    
    // 存储按钮数据
    this.buttons = [];
  }
  
  connectedCallback() {
    this.updateStyles();
    this.updateContent();
    this.renderButtons();
    
    // 防止重复渲染
    this.setAttribute('rendered', 'true');
  }
  
  updateStyles() {
    // 设置主题色
    const themeColor = this.getAttribute('theme-color') || '#FFA200';
    this.sectionElement.style.setProperty('--theme-color', themeColor);
    
    // 设置背景色
    const bgColor = this.getAttribute('bg-color');
    if (bgColor) {
      this.sectionElement.style.setProperty('--section-bg-color', bgColor);
    }
    
    // 设置最大宽度
    const maxWidth = this.getAttribute('max-width');
    if (maxWidth) {
      this.sectionElement.style.setProperty('--section-max-width', maxWidth);
    }
    
    // 设置内边距
    const padding = this.getAttribute('padding');
    if (padding) {
      this.sectionElement.style.setProperty('--section-padding', padding);
    }
    
    // 设置文本对齐
    const textAlign = this.getAttribute('text-align');
    if (textAlign) {
      this.sectionElement.style.setProperty('--section-text-align', textAlign);
    }
    
    // 设置小标题颜色
    const subtitleColor = this.getAttribute('subtitle-color');
    if (subtitleColor) {
      this.sectionElement.style.setProperty('--subtitle-color', subtitleColor);
    }
    
    // 按钮悬停颜色
    const buttonHoverColor = this.getAttribute('button-hover-color') || this.calculateDarkerColor(themeColor);
    this.sectionElement.style.setProperty('--button-hover-color', buttonHoverColor);
    
    // 设置按钮对齐方式
    const buttonsJustify = this.getAttribute('buttons-justify') || 'center';
    this.sectionElement.style.setProperty('--buttons-justify', buttonsJustify);
    
    // 设置按钮间距
    const buttonsGap = this.getAttribute('buttons-gap');
    if (buttonsGap) {
      this.sectionElement.style.setProperty('--buttons-gap', buttonsGap);
    }
    
    // 设置移动端按钮方向
    const buttonsDirectionMobile = this.getAttribute('buttons-direction-mobile');
    if (buttonsDirectionMobile) {
      this.sectionElement.style.setProperty('--buttons-direction-mobile', buttonsDirectionMobile);
    }
    
    // 设置小屏幕按钮方向
    const buttonsDirectionSmall = this.getAttribute('buttons-direction-small') || 'column';
    this.sectionElement.style.setProperty('--buttons-direction-small', buttonsDirectionSmall);
    
    // 隐藏元素
    if (this.getAttribute('hide-title') === 'true') {
      this.sectionElement.style.setProperty('--title-display', 'none');
    }
    
    if (this.getAttribute('hide-subtitle') === 'true') {
      this.sectionElement.style.setProperty('--subtitle-display', 'none');
    }
    
    if (this.getAttribute('hide-buttons') === 'true') {
      this.buttonsContainer.style.display = 'none';
    }
    
    // 应用自定义CSS变量
    this.applyCustomCSSVariables();
  }
  
  updateContent() {
    // 设置标题
    const title = this.getAttribute('title') || '';
    this.titleElement.textContent = title;
    
    // 设置小标题
    const subtitle = this.getAttribute('subtitle') || '';
    this.subtitleElement.textContent = subtitle;
    
    // 解析按钮数据
    this.parseButtonsData();
  }
  
  // 解析按钮数据
  parseButtonsData() {
    this.buttons = [];
    
    // 获取按钮JSON数据
    const buttonsData = this.getAttribute('buttons');
    if (buttonsData) {
      try {
        this.buttons = JSON.parse(buttonsData);
      } catch (e) {
        console.error('按钮数据格式错误:', e);
      }
    } else {
      // 兼容旧版单按钮
      const buttonText = this.getAttribute('button-text');
      const buttonLink = this.getAttribute('button-link');
      
      if (buttonText) {
        this.buttons.push({
          text: buttonText,
          link: buttonLink || '#',
          type: 'primary'
        });
      }
    }
  }
  
  // 渲染按钮
  renderButtons() {
    // 清空按钮容器
    this.buttonsContainer.innerHTML = '';
    
    // 没有按钮数据则隐藏容器
    if (this.buttons.length === 0) {
      this.buttonsContainer.style.display = 'none';
      return;
    }
    
    // 显示按钮容器
    this.buttonsContainer.style.display = 'flex';
    
    // 添加按钮
    this.buttons.forEach((buttonData, index) => {
      const button = document.createElement('a');
      button.href = buttonData.link || '#';
      button.className = `section-button ${buttonData.type === 'secondary' ? 'secondary' : ''}`;
      button.textContent = buttonData.text || `Button ${index + 1}`;
      
      // 设置按钮属性
      if (buttonData.id) {
        button.id = buttonData.id;
      }
      
      if (buttonData.target) {
        button.target = buttonData.target;
      }
      
      if (buttonData.ariaLabel) {
        button.setAttribute('aria-label', buttonData.ariaLabel);
      }
      
      if (buttonData.onClick) {
        button.setAttribute('data-onclick', buttonData.onClick);
        button.addEventListener('click', (e) => {
          if (buttonData.onClick === 'prevent-default') {
            e.preventDefault();
          }
          
          // 触发自定义事件
          const event = new CustomEvent('button-click', {
            detail: {
              buttonIndex: index,
              buttonData: buttonData
            },
            bubbles: true,
            composed: true
          });
          this.dispatchEvent(event);
        });
      }
      
      this.buttonsContainer.appendChild(button);
    });
  }
  
  // 应用自定义CSS变量
  applyCustomCSSVariables() {
    // 获取所有属性
    const attributes = this.attributes;
    
    // 遍历属性，查找以"css-"开头的属性
    for (let i = 0; i < attributes.length; i++) {
      const attr = attributes[i];
      if (attr.name.startsWith('css-')) {
        // 将属性名转换为CSS变量名（例如：css-title-color -> --title-color）
        const cssVarName = '--' + attr.name.substring(4);
        this.sectionElement.style.setProperty(cssVarName, attr.value);
      }
    }
  }
  
  // 计算按钮悬停颜色（默认比主题色暗15%）
  calculateDarkerColor(hex) {
    // 移除#号并转换为RGB
    hex = hex.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // 使颜色暗15%
    r = Math.max(0, Math.floor(r * 0.85));
    g = Math.max(0, Math.floor(g * 0.85));
    b = Math.max(0, Math.floor(b * 0.85));
    
    // 转回十六进制
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
  
  // 监听属性变化
  static get observedAttributes() {
    return [
      'theme-color', 'bg-color', 'max-width', 'padding', 'text-align',
      'title', 'subtitle', 'subtitle-color', 'button-text', 'button-link',
      'hide-title', 'hide-subtitle', 'hide-buttons', 'buttons',
      'buttons-justify', 'buttons-gap', 'buttons-direction-mobile', 'buttons-direction-small'
    ];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    if (this.hasAttribute('rendered')) {
      if (name.startsWith('css-') || 
          ['theme-color', 'bg-color', 'max-width', 'padding', 'text-align', 
           'subtitle-color', 'hide-title', 'hide-subtitle', 'hide-buttons',
           'buttons-justify', 'buttons-gap', 'buttons-direction-mobile', 
           'buttons-direction-small'].includes(name)) {
        this.updateStyles();
      }
      
      if (['title', 'subtitle', 'buttons', 'button-text', 'button-link'].includes(name)) {
        this.updateContent();
        if (name === 'buttons' || name === 'button-text' || name === 'button-link') {
          this.renderButtons();
        }
      }
    }
  }
}
customElements.define('new-generic-section', NewGenericSection);



class BenefitItem extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }
  
  connectedCallback() {
    // 获取属性
    const index = this.getAttribute('index');
    const title = this.getAttribute('title');
    const mediaType = this.getAttribute('media-type');
    const media = this.getAttribute('media');
    const previewImage = this.getAttribute('preview-image'); // 新增预览图属性
    // 构建基础结构
    this.shadowRoot.innerHTML = `
      <style>
        /* 基础样式，与BenefitsSection保持一致 */
        :host {
          display: block;
        }
        
        .benefit-item {
          display: flex;
          flex-direction: column;
          padding: 10px;
          max-width: 100%;
        }
        
        .benefit-media {
          width: 100%;
          height: auto;
          border-radius: 8px;
          margin-bottom: 25px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 1/1;
        }
        
        .benefit-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        
        .benefit-media img:hover {
          transform: scale(1.03);
        }
        
        .benefit-media video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .youtube-container {
          width: 100%;
          height: 100%;
          position: relative;
        }
        
        .youtube-container iframe {
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          border: none;
        }
        
        .benefit-number {
          font-size: 22px;
          font-weight: 600;
          margin-right: 4px;
          color: var(--theme-color, #FFA200);
        }
        
        .benefit-title-container {
          font-size: 22px;
          font-weight: 600;
          margin-bottom: 15px;
          display: flex;
          align-items: flex-start;
        }
        
        .benefit-title-text {
          flex: 1;
          line-height: 1.3;
          color: var(--theme-color, #FFA200);
          display: flex;
          justify-content: flex-start;
          transform: translateY(2px);
        }
        
        .benefit-description {
          font-size: 16px;
          line-height: 1.6;
          color: #333;
          margin-bottom: 15px;
        }
        
        /* 视频加载指示器 */
        .video-loading {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 14px;
          background: rgba(0, 0, 0, 0.5);
          padding: 8px 16px;
          border-radius: 4px;
        }
        
        /* 播放按钮悬停效果 */
        .play-button {
          transition: background-color 0.3s ease, transform 0.2s ease;
        }
        
        .youtube-preview:hover .play-button,
        .youtube-placeholder:hover .play-button {
          background-color: rgba(255, 0, 0, 0.9) !important;
          transform: translate(-50%, -50%) scale(1.1);
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .benefit-title-container {
            font-size: 20px;
          }
          
          .benefit-number {
            font-size: 20px;
          }
          
          .benefit-description {
            font-size: 15px;
          }
          
          .benefit-media {
            aspect-ratio: 1/1;
            margin-bottom: 20px;
          }
        }
        
        /* 小屏幕手机适配 */
        @media (max-width: 480px) {
          .benefit-title-container {
            font-size: 18px;
          }
          
          .benefit-number {
            font-size: 18px;
          }
          
          .benefit-description {
            font-size: 14px;
          }
        }
      </style>
      
      <div class="benefit-item">
        <div class="benefit-media">
          ${this.renderMedia(mediaType, media, previewImage)}
        </div>
        
        <div class="benefit-title-container">
          <span class="benefit-number">${index}.</span>
          <span class="benefit-title-text">${title}</span>
        </div>
        
        <div class="benefit-description">
          <slot name="description"></slot>
        </div>
        
        <slot name="points"></slot>
      </div>
    `;
    
    // 如果是视频，初始化视频功能
    if (mediaType === 'video') {
      this.setupVideoObserver();
      this.initVideo(media, previewImage);
    } else if (mediaType === 'youtube') {
      // 初始化YouTube视频
      this.setupYouTubeVideo();
    }
  }
  
  renderMedia(mediaType, media, previewImage) {
    if (!media) return '';
    
    if (mediaType === 'image') {
      return `<img src="${this.ensureProtocol(media)}" alt="">`;
    } else if (mediaType === 'video') {
      // 检查是否是HLS格式
      const isHLS = media.includes('.m3u8');
      
      // 创建视频容器
      let videoHtml = `
        <div class="video-loading">Loading...</div>
        <video loop muted playsinline ${previewImage ? `poster="${this.ensureProtocol(previewImage)}"` : ''}>
          <source src="${this.ensureProtocol(media)}" type="${isHLS ? 'application/x-mpegURL' : 'video/mp4'}">
        </video>
      `;
      
      return videoHtml;
    } else if (mediaType === 'youtube') {
      // 提取YouTube视频ID
      const videoId = this.getYouTubeId(media);
      
      if (videoId) {
        // 判断是否为shorts视频
        const isShorts = media.includes('/shorts/');
        
        // 创建自定义的YouTube播放器容器
        return `
          <div class="youtube-container" data-video-id="${videoId}" data-is-shorts="${isShorts}">
            ${previewImage ? 
              `<div class="youtube-preview" style="position:absolute; top:0; left:0; width:100%; height:100%; z-index:2; cursor:pointer;">
                <img src="${this.ensureProtocol(previewImage)}" alt="" style="width:100%; height:100%; object-fit:cover;">
                <div class="play-button" style="position:absolute; top:50%; left:50%; transform:translate(-50%, -50%); width:68px; height:48px; background-color:rgba(0,0,0,0.7); border-radius:10px; display:flex; justify-content:center; align-items:center;">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5V19L19 12L8 5Z" fill="white"/>
                  </svg>
                </div>
              </div>` : 
              `<div class="youtube-placeholder" style="position:absolute; top:0; left:0; width:100%; height:100%; background:#000; display:flex; justify-content:center; align-items:center; cursor:pointer; z-index:2;">
                <div class="play-button" style="width:68px; height:48px; background-color:rgba(255,0,0,0.8); border-radius:10px; display:flex; justify-content:center; align-items:center;">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5V19L19 12L8 5Z" fill="white"/>
                  </svg>
                </div>
              </div>`
            }
          </div>
        `;
      }
    }
    
    return '';
  }
  
  setupYouTubeVideo() {
    const container = this.shadowRoot.querySelector('.youtube-container');
    if (!container) return;
    
    const videoId = container.getAttribute('data-video-id');
    const isShorts = container.getAttribute('data-is-shorts') === 'true';
    
    // 添加点击事件处理
    const previewElement = container.querySelector('.youtube-preview') || 
                           container.querySelector('.youtube-placeholder');
    
    if (previewElement) {
      previewElement.addEventListener('click', () => {
        // 创建iframe元素
        const iframe = document.createElement('iframe');
        
        // 设置适当的嵌入URL
        if (isShorts) {
          // 对于shorts视频，使用特殊的嵌入参数
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&widgetid=1&controls=0`;
        } else {
          // 标准视频嵌入
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
        }
        
        // 设置iframe属性
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.style.position = 'absolute';
        iframe.style.top = '0';
        iframe.style.left = '0';
        iframe.style.zIndex = '1';
        iframe.style.border = 'none';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        
        // 添加iframe到容器
        container.appendChild(iframe);
        
        // 移除预览元素
        previewElement.remove();
      });
    }
  }
  
  setupVideoObserver() {
    // 创建Intersection Observer来控制视频播放
    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          // 视频可见时播放
          video.play().catch(e => console.log('Video play error:', e));
        } else {
          // 不可见时暂停
          video.pause();
        }
      });
    }, {
      threshold: 0.1 // 10% 的视频可见时触发
    });
  }
  
  initVideo(mediaUrl, previewImage) {
    // 获取视频元素
    const video = this.shadowRoot.querySelector('video');
    if (!video) return;
    
    // 检查是否是HLS格式
    const isHLS = mediaUrl.includes('.m3u8');
    
    // 设置预览图（如果有）
    if (previewImage && !video.hasAttribute('poster')) {
      video.setAttribute('poster', this.ensureProtocol(previewImage));
    }
    
    if (isHLS) {
      // 加载HLS.js并初始化视频
      this.loadHLSScript().then(() => {
        // 移除加载指示器
        const loadingIndicator = this.shadowRoot.querySelector('.video-loading');
        if (loadingIndicator) {
          loadingIndicator.remove();
        }
        
        if (window.Hls && window.Hls.isSupported()) {
          const hls = new window.Hls({
            maxBufferLength: 30,
            maxMaxBufferLength: 60
          });
          hls.loadSource(this.ensureProtocol(mediaUrl));
          hls.attachMedia(video);
          hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
            // 视频准备好后，添加到观察器
            video.muted = true;
            this.videoObserver.observe(video);
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari原生支持HLS
          video.src = this.ensureProtocol(mediaUrl);
          video.muted = true;
          this.videoObserver.observe(video);
        }
      });
    } else {
      // 标准视频格式，直接添加到观察器
      this.videoObserver.observe(video);
      
      // 移除加载指示器
      const loadingIndicator = this.shadowRoot.querySelector('.video-loading');
      if (loadingIndicator) {
        loadingIndicator.remove();
      }
      
      // 添加加载事件
      video.addEventListener('loadeddata', () => {
        console.log('视频已加载:', mediaUrl);
        if (video.paused) {
          video.play().catch(e => {
            console.log('自动播放失败，尝试静音播放:', e);
            video.muted = true;
            video.play().catch(e2 => console.log('静音播放也失败:', e2));
          });
        }
      });
      
      // 添加错误处理
      video.addEventListener('error', (e) => {
        console.error('视频加载失败:', e, video.error);
        // 如果有预览图，在视频加载失败时显示预览图
        if (previewImage) {
          const parent = video.parentNode;
          if (parent) {
            const errorImg = document.createElement('img');
            errorImg.src = this.ensureProtocol(previewImage);
            errorImg.alt = '视频无法播放';
            parent.appendChild(errorImg);
            video.style.display = 'none';
          }
        } else {
          // 没有预览图时显示默认错误图片
          const parent = video.parentNode;
          if (parent) {
            const errorImg = document.createElement('img');
            errorImg.src = 'https://via.placeholder.com/400x400?text=Video+Unavailable';
            errorImg.alt = '视频无法播放';
            parent.appendChild(errorImg);
            video.style.display = 'none';
          }
        }
      });
    }
  }
  
  // 确保URL有协议
  ensureProtocol(url) {
    if (!url) return '';
    
    if (url.startsWith('//')) {
      return `https:${url}`;
    }
    
    // 尝试修复不完整的URL
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/')) {
      return `https://${url}`;
    }
    
    return url;
  }
  
  // 加载HLS.js脚本
  loadHLSScript() {
    return new Promise((resolve) => {
      if (window.Hls) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }
  
  // 从YouTube URL提取视频ID，支持更多格式
  getYouTubeId(url) {
    if (!url) return null;
    
    try {
      // 处理短链接 (youtu.be)
      if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理标准链接 (youtube.com/watch?v=)
      if (url.includes('youtube.com/watch')) {
        // 确保URL有协议，以便可以创建URL对象
        const fullUrl = this.ensureProtocol(url);
        try {
          const urlObj = new URL(fullUrl);
          return urlObj.searchParams.get('v') || null;
        } catch (e) {
          console.error('解析YouTube URL失败:', e);
        }
      }
      
      // 处理嵌入链接 (youtube.com/embed/)
      if (url.includes('/embed/')) {
        const id = url.split('/embed/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理短视频链接 (youtube.com/shorts/)
      if (url.includes('/shorts/')) {
        const id = url.split('/shorts/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理直接带有视频ID的链接
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    } catch (error) {
      console.error('获取YouTube ID时出错:', error);
      return null;
    }
  }
  
  disconnectedCallback() {
    // 清理资源
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
  }
}
customElements.define('benefit-item', BenefitItem);


class OldBenefitItemHorizontal extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    // 保存组件唯一标识，用于区分事件来源
    this.componentId = Math.random().toString(36).substring(2, 15);
    
    // 绑定方法到实例，以便正确解绑事件
    this.handleVideoPlayStarted = this.handleVideoPlayStarted.bind(this);
    
    // 标记是否是用户交互触发的播放
    this.userInitiatedPlay = false;
  }
  
  connectedCallback() {
    // 获取属性
    const index = this.getAttribute('index') || '';
    const title = this.getAttribute('title');
    const mediaType = this.getAttribute('media-type');
    const media = this.getAttribute('media');
    const previewImage = this.getAttribute('preview-image');
    const autoplay = this.hasAttribute('autoplay');
    const loop = this.hasAttribute('loop');
    
    // 构建基础结构
    this.shadowRoot.innerHTML = `
      <style>
        /* 基础样式，与BenefitsSection保持一致 */
        :host {
          display: block;
        }
        
        .benefit-item {
          display: flex;
          flex-direction: column;
          padding: 10px;
          max-width: 100%;
        }
        
        .benefit-media {
          width: 100%;
          height: auto;
          border-radius: 8px;
          margin-bottom: 25px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 9/12;
        }
        
        .benefit-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        
        .benefit-media img:hover {
          transform: scale(1.03);
        }
        
        .video-container {
          position: relative;
          width: 100%;
          height: 100%;
        }
        
        .benefit-media video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .youtube-container {
          width: 100%;
          height: 100%;
          position: relative;
        }
        
        .youtube-container iframe {
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 8px;
        }
        
        .benefit-number {
          font-size: 22px;
          font-weight: 600;
          margin-right: 4px;
          color: var(--theme-color, #FFA200);
        }
        
        .benefit-title-container {
          font-size: 22px;
          font-weight: 600;
          margin-bottom: 15px;
          display: flex;
          justify-content: center;
        }
        
        .benefit-title-text {
          line-height: 1.3;
          color: var(--theme-color, #FFA200);
          display: flex;
          justify-content: flex-start;
          transform: translateY(2px);
        }
        
        .benefit-description {
          font-size: 16px;
          line-height: 1.6;
          color: #333;
          margin-bottom: 15px;
        }
        
        /* 视频控制按钮 - 右下角，默认可见 */
        .play-pause-btn {
          position: absolute;
          bottom: 15px;
          right: 15px;
          background: rgba(0, 0, 0, 0.5);
          border: none;
          color: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.8; /* 默认可见但略微透明 */
          transition: opacity 0.3s ease, background-color 0.2s ease;
        }
        
        .video-container:hover .play-pause-btn {
          opacity: 1; /* 鼠标悬停时完全不透明 */
        }
        
        .play-pause-btn:hover {
          background: rgba(0, 0, 0, 0.7);
        }
        
        .play-pause-btn svg {
          width: 24px;
          height: 24px;
        }
        
        /* 循环指示器 */
        .loop-indicator {
          position: absolute;
          bottom: 15px;
          left: 15px;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 12px;
          padding: 4px 8px;
          color: white;
          font-size: 12px;
          opacity: 0.8;
          display: flex;
          align-items: center;
        }
        
        .loop-indicator svg {
          width: 14px;
          height: 14px;
          margin-right: 4px;
        }
        
        /* 水平布局样式 */
        @media (min-width: 769px) {
          .benefit-item {
            flex-direction: row;
            align-items: center;
            gap: 40px;
          }
          
          .benefit-media {
            flex: 0 0 50%;
            margin-bottom: 0;
            max-width: 50%;
          }
          
          .benefit-content {
            flex: 0 0 45%;
          }
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .benefit-title-container {
            font-size: 20px;
          }
          
          .benefit-number {
            font-size: 20px;
          }
          
          .benefit-description {
            font-size: 15px;
          }
          
          .benefit-media {
            aspect-ratio: 9/12;
            margin-bottom: 20px;
          }
          
          .play-pause-btn {
            width: 36px;
            height: 36px;
            bottom: 12px;
            right: 12px;
          }
          
          .play-pause-btn svg {
            width: 20px;
            height: 20px;
          }
        }
        
        /* 小屏幕手机适配 */
        @media (max-width: 480px) {
          .benefit-title-container {
            font-size: 18px;
          }
          
          .benefit-number {
            font-size: 18px;
          }
          
          .benefit-description {
            font-size: 14px;
          }
          
          .play-pause-btn {
            width: 32px;
            height: 32px;
            bottom: 10px;
            right: 10px;
          }
        }
      </style>
      
      <div class="benefit-item">
        <div class="benefit-media">
          ${this.renderMedia(mediaType, media, previewImage, autoplay, loop)}
        </div>
        
        <div class="benefit-content">
          <div class="benefit-title-container">
            ${index ? `<span class="benefit-number">${index}.</span>` : ''}
            <span class="benefit-title-text">${title}</span>
          </div>
          
          <div class="benefit-description">
            <slot name="description"></slot>
          </div>
          
          <slot name="points"></slot>
        </div>
      </div>
    `;
    
    // 存储循环属性和媒体类型
    this._loopEnabled = loop;
    this._mediaType = mediaType;
    
    // 如果是视频，初始化视频控制功能
    if (mediaType === 'video') {
      this.initVideoControls();
    } else if (mediaType === 'youtube') {
      this.initYouTubeControls();
    }
    
    // 添加自定义事件监听器
    this.addCustomEventListeners();
  }
  
  // 添加自定义事件监听器
  addCustomEventListeners() {
    // 监听其他视频播放事件
    document.addEventListener('video-play-started', this.handleVideoPlayStarted);
  }
  
  // 处理视频播放事件
  handleVideoPlayStarted(event) {
    // 确保不是自己发出的事件
    if (event.detail.componentId !== this.componentId) {
      // 仅暂停媒体，不发送新事件
      this.pauseMedia(false);
    }
  }
  
  // 发送视频开始播放事件
  dispatchPlayEvent() {
    // 只在用户交互触发的播放时发送事件
    if (this.userInitiatedPlay) {
      const event = new CustomEvent('video-play-started', {
        bubbles: true,
        composed: true, // 确保事件可以穿越Shadow DOM边界
        detail: {
          componentId: this.componentId,
          timestamp: Date.now()
        }
      });
      this.dispatchEvent(event);
      
      // 重置标记
      this.userInitiatedPlay = false;
    }
  }
  
  // 暂停当前媒体
  // shouldDispatch参数控制是否需要发送事件
  pauseMedia(shouldDispatch = true) {
    if (this._mediaType === 'video') {
      const video = this.shadowRoot.querySelector('video');
      if (video && !video.paused) {
        // 如果需要发送事件，设置标记
        this.userInitiatedPlay = shouldDispatch;
        video.pause();
      }
    } else if (this._mediaType === 'youtube') {
      const iframe = this.shadowRoot.querySelector('iframe.js-youtube');
      if (iframe) {
        try {
          // 向YouTube iframe发送暂停命令
          iframe.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: 'pauseVideo',
            args: ''
          }), '*');
        } catch (e) {
          console.warn('无法暂停YouTube视频:', e);
        }
      }
    }
  }
  
  renderMedia(mediaType, media, previewImage, autoplay, loop) {
    if (!media) return '';
    
    if (mediaType === 'image') {
      return `<img src="${this.ensureProtocol(media)}" alt="">`;
    } else if (mediaType === 'video') {
      // 检查是否是HLS格式
      const isHLS = media.includes('.m3u8');
      
      // 创建视频容器，添加loop属性
      let videoHtml = `
        <div class="video-container">
          <video ${autoplay ? 'autoplay' : ''} ${autoplay ? 'muted' : ''} ${loop ? 'loop' : ''} playsinline ${previewImage ? `poster="${this.ensureProtocol(previewImage)}"` : ''}>
            <source src="${this.ensureProtocol(media)}" type="${isHLS ? 'application/x-mpegURL' : 'video/mp4'}">
          </video>
          <button class="play-pause-btn">
            <svg viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </button>
          ${loop ? `
            <div class="loop-indicator">
              <svg viewBox="0 0 24 24" fill="white">
                <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
              </svg>
              循环播放
            </div>
          ` : ''}
        </div>
      `;
      
      return videoHtml;
    } else if (mediaType === 'youtube') {
      // 提取YouTube视频ID
      const videoId = this.getYouTubeId(media);
      
      if (videoId) {
        // 为YouTube iframe添加loop参数
        const loopParam = loop ? '&loop=1&playlist=' + videoId : '';
        
        return `
          <div class="youtube-container">
            <iframe 
              class="js-youtube"
              src="https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1${autoplay ? '&autoplay=1&mute=1' : ''}${loopParam}" 
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen>
            </iframe>
            ${loop ? `
              <div class="loop-indicator">
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                </svg>
                循环播放
              </div>
            ` : ''}
          </div>
        `;
      }
    }
    
    return '';
  }
  
  initVideoControls() {
    const video = this.shadowRoot.querySelector('video');
    const playPauseBtn = this.shadowRoot.querySelector('.play-pause-btn');
    
    if (!video || !playPauseBtn) return;
    
    // 检查是否是HLS格式
    const source = video.querySelector('source');
    const isHLS = source && source.src.includes('.m3u8');
    
    // 如果是HLS格式，加载HLS.js
    if (isHLS) {
      this.loadHLSScript().then(() => {
        this.setupHLS(video, source.src);
      });
    }
    
    // 更新播放/暂停按钮图标
    const updatePlayPauseIcon = () => {
      if (video.paused) {
        playPauseBtn.innerHTML = `
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        `;
      } else {
        playPauseBtn.innerHTML = `
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
          </svg>
        `;
      }
    };
    
    // 播放/暂停按钮点击事件
    playPauseBtn.addEventListener('click', () => {
      if (video.paused) {
        // 标记这是用户交互触发的播放
        this.userInitiatedPlay = true;
        video.play();
        // 发送播放事件通知其他组件
        this.dispatchPlayEvent();
      } else {
        video.pause();
      }
      updatePlayPauseIcon();
    });
    
    // 视频播放状态变化事件
    video.addEventListener('play', updatePlayPauseIcon);
    video.addEventListener('pause', updatePlayPauseIcon);
    video.addEventListener('ended', () => {
      // 如果没有设置循环，视频结束时更新图标
      if (!this._loopEnabled) {
        updatePlayPauseIcon();
      }
    });
    
    // 初始化按钮状态
    updatePlayPauseIcon();
  }
  
  initYouTubeControls() {
    // 为YouTube iframe添加事件监听
    const iframe = this.shadowRoot.querySelector('iframe.js-youtube');
    if (!iframe) return;
    
    // 确保iframe加载完成后再添加事件监听
    iframe.addEventListener('load', () => {
      // 监听来自iframe的消息
      this.youtubeMessageHandler = (event) => {
        try {
          // 尝试解析消息数据
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          
          // 检查是否是来自当前iframe的YouTube事件
          if (data.event === 'onStateChange' && event.source === iframe.contentWindow) {
            // 状态1表示正在播放
            if (data.info === 1 && this.userInitiatedPlay) {
              // 发送播放事件通知其他组件
              this.dispatchPlayEvent();
            }
          }
        } catch (e) {
          // 忽略无法解析的消息
        }
      };
      
      window.addEventListener('message', this.youtubeMessageHandler);
      
      // 当iframe被点击时
      iframe.addEventListener('click', () => {
        // 标记这是用户交互触发的播放
        this.userInitiatedPlay = true;
      });
    });
  }
  
  setupHLS(video, src) {
    if (window.Hls && window.Hls.isSupported()) {
      const hls = new window.Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari原生支持HLS
      video.src = src;
    }
  }
  
  // 确保URL有协议
  ensureProtocol(url) {
    if (!url) return '';
    
    if (url.startsWith('//')) {
      return `https:${url}`;
    }
    
    // 尝试修复不完整的URL
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/')) {
      return `https://${url}`;
    }
    
    return url;
  }
  
  // 加载HLS.js脚本
  loadHLSScript() {
    return new Promise((resolve) => {
      if (window.Hls) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }
  
  // 从YouTube URL提取视频ID
  getYouTubeId(url) {
    if (!url) return null;
    
    try {
      // 处理短链接 (youtu.be)
      if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理标准链接 (youtube.com/watch?v=)
      if (url.includes('youtube.com/watch')) {
        const fullUrl = this.ensureProtocol(url);
        try {
          const urlObj = new URL(fullUrl);
          return urlObj.searchParams.get('v') || null;
        } catch (e) {
          console.error('解析YouTube URL失败:', e);
        }
      }
      
      // 处理嵌入链接 (youtube.com/embed/)
      if (url.includes('/embed/')) {
        const id = url.split('/embed/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理短视频链接 (youtube.com/shorts/)
      if (url.includes('/shorts/')) {
        const id = url.split('/shorts/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理直接带有视频ID的链接
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    } catch (error) {
      console.error('获取YouTube ID时出错:', error);
      return null;
    }
  }
  
  // 组件断开连接时清理事件监听器
  disconnectedCallback() {
    // 移除事件监听器，避免内存泄漏
    document.removeEventListener('video-play-started', this.handleVideoPlayStarted);
    
    // 清理YouTube消息监听器
    if (this.youtubeMessageHandler) {
      window.removeEventListener('message', this.youtubeMessageHandler);
    }
  }
}

customElements.define('old-benefit-item-horizontal', OldBenefitItemHorizontal);




class BenefitItemHorizontal extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }
  
  connectedCallback() {
    // 获取属性
    const index = this.getAttribute('index');
    const title = this.getAttribute('title');
    const mediaType = this.getAttribute('media-type');
    const media = this.getAttribute('media');
    const previewImage = this.getAttribute('preview-image'); // 新增预览图属性
    // 构建基础结构
    this.shadowRoot.innerHTML = `
      <style>
        /* 基础样式，与BenefitsSection保持一致 */
        :host {
          display: block;
        }
        
        .benefit-item {
          display: flex;
          flex-direction: column;
          padding: 10px;
          max-width: 100%;
        }
        
        .benefit-media {
          width: 100%;
          height: auto;
          border-radius: 8px;
          margin-bottom: 25px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 9/12;
        }
        
        .benefit-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        
        .benefit-media img:hover {
          transform: scale(1.03);
        }
        
        .benefit-media video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .youtube-container {
          width: 100%;
          height: 100%;
          position: relative;
        }
        
        .youtube-container iframe {
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          border: none;
        }
        
        .benefit-number {
          font-size: 22px;
          font-weight: 600;
          margin-right: 4px;
          color: var(--theme-color, #FFA200);
        }
        
        .benefit-title-container {
          font-size: 22px;
          font-weight: 600;
          margin-bottom: 15px;
          display: flex;
          justify-content: center;
        }
        
        .benefit-title-text {
          line-height: 1.3;
          color: var(--theme-color, #FFA200);
          display: flex;
          justify-content: flex-start;
          transform: translateY(2px);
        }
        
        .benefit-description {
          font-size: 16px;
          line-height: 1.6;
          color: #333;
          margin-bottom: 15px;
        }
        
        /* 视频加载指示器 */
        .video-loading {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 14px;
          background: rgba(0, 0, 0, 0.5);
          padding: 8px 16px;
          border-radius: 4px;
        }
        
        /* 播放按钮悬停效果 */
        .play-button {
          transition: background-color 0.3s ease, transform 0.2s ease;
        }
        
        .youtube-preview:hover .play-button,
        .youtube-placeholder:hover .play-button {
          background-color: rgba(255, 0, 0, 0.9) !important;
          transform: translate(-50%, -50%) scale(1.1);
        }
        
        /* 水平布局样式 */
        @media (min-width: 769px) {
          .benefit-item {
            flex-direction: row;
            align-items: center;
            gap: 40px;
          }
          
          .benefit-media {
            flex: 0 0 50%;
            margin-bottom: 0;
            max-width: 50%;
          }
          
          .benefit-content {
            flex: 0 0 45%;
          }
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .benefit-title-container {
            font-size: 20px;
          }
          
          .benefit-number {
            font-size: 20px;
          }
          
          .benefit-description {
            font-size: 15px;
          }
          
          .benefit-media {
            aspect-ratio: 9/12;
            margin-bottom: 20px;
          }
        }
        
        /* 小屏幕手机适配 */
        @media (max-width: 480px) {
          .benefit-title-container {
            font-size: 18px;
          }
          
          .benefit-number {
            font-size: 18px;
          }
          
          .benefit-description {
            font-size: 14px;
          }
        }
      </style>
      
      <div class="benefit-item">
        <div class="benefit-media">
          ${this.renderMedia(mediaType, media, previewImage)}
        </div>
        
        <div class="benefit-content">
          <div class="benefit-title-container">
            ${index ? `<span class="benefit-number">${index}.</span>` : ''}
            <span class="benefit-title-text">${title}</span>
          </div>
          
          <div class="benefit-description">
            <slot name="description"></slot>
          </div>
          
          <slot name="points"></slot>
        </div>
      </div>
    `;
    
    // 如果是视频，初始化视频功能
    if (mediaType === 'video') {
      this.setupVideoObserver();
      this.initVideo(media, previewImage);
    } else if (mediaType === 'youtube') {
      // 初始化YouTube视频
      this.setupYouTubeVideo();
    }
  }
  
  renderMedia(mediaType, media, previewImage) {
    if (!media) return '';
    
    if (mediaType === 'image') {
      return `<img src="${this.ensureProtocol(media)}" alt="">`;
    } else if (mediaType === 'video') {
      // 检查是否是HLS格式
      const isHLS = media.includes('.m3u8');
      
      // 创建视频容器
      let videoHtml = `
        <div class="video-loading">Loading...</div>
        <video loop muted playsinline ${previewImage ? `poster="${this.ensureProtocol(previewImage)}"` : ''}>
          <source src="${this.ensureProtocol(media)}" type="${isHLS ? 'application/x-mpegURL' : 'video/mp4'}">
        </video>
      `;
      
      return videoHtml;
    } else if (mediaType === 'youtube') {
      // 提取YouTube视频ID
      const videoId = this.getYouTubeId(media);
      
      if (videoId) {
        // 判断是否为shorts视频
        const isShorts = media.includes('/shorts/');
        
        // 创建自定义的YouTube播放器容器
        return `
          <div class="youtube-container" data-video-id="${videoId}" data-is-shorts="${isShorts}">
            ${previewImage ? 
              `<div class="youtube-preview" style="position:absolute; top:0; left:0; width:100%; height:100%; z-index:2; cursor:pointer;">
                <img src="${this.ensureProtocol(previewImage)}" alt="" style="width:100%; height:100%; object-fit:cover;">
                <div class="play-button" style="position:absolute; top:50%; left:50%; transform:translate(-50%, -50%); width:68px; height:48px; background-color:rgba(0,0,0,0.7); border-radius:10px; display:flex; justify-content:center; align-items:center;">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5V19L19 12L8 5Z" fill="white"/>
                  </svg>
                </div>
              </div>` : 
              `<div class="youtube-placeholder" style="position:absolute; top:0; left:0; width:100%; height:100%; background:#000; display:flex; justify-content:center; align-items:center; cursor:pointer; z-index:2;">
                <div class="play-button" style="width:68px; height:48px; background-color:rgba(255,0,0,0.8); border-radius:10px; display:flex; justify-content:center; align-items:center;">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5V19L19 12L8 5Z" fill="white"/>
                  </svg>
                </div>
              </div>`
            }
          </div>
        `;
      }
    }
    
    return '';
  }
  
  setupYouTubeVideo() {
    const container = this.shadowRoot.querySelector('.youtube-container');
    if (!container) return;
    
    const videoId = container.getAttribute('data-video-id');
    const isShorts = container.getAttribute('data-is-shorts') === 'true';
    
    // 添加点击事件处理
    const previewElement = container.querySelector('.youtube-preview') || 
                           container.querySelector('.youtube-placeholder');
    
    if (previewElement) {
      previewElement.addEventListener('click', () => {
        // 创建iframe元素
        const iframe = document.createElement('iframe');
        
        // 设置适当的嵌入URL
        if (isShorts) {
          // 对于shorts视频，使用特殊的嵌入参数
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&widgetid=1&controls=0`;
        } else {
          // 标准视频嵌入
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
        }
        
        // 设置iframe属性
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.style.position = 'absolute';
        iframe.style.top = '0';
        iframe.style.left = '0';
        iframe.style.zIndex = '1';
        iframe.style.border = 'none';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        
        // 添加iframe到容器
        container.appendChild(iframe);
        
        // 移除预览元素
        previewElement.remove();
      });
    }
  }
  
  setupVideoObserver() {
    // 创建Intersection Observer来控制视频播放
    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          // 视频可见时播放
          video.play().catch(e => console.log('Video play error:', e));
        } else {
          // 不可见时暂停
          video.pause();
        }
      });
    }, {
      threshold: 0.1 // 10% 的视频可见时触发
    });
  }
  
  initVideo(mediaUrl, previewImage) {
    // 获取视频元素
    const video = this.shadowRoot.querySelector('video');
    if (!video) return;
    
    // 检查是否是HLS格式
    const isHLS = mediaUrl.includes('.m3u8');
    
    // 设置预览图（如果有）
    if (previewImage && !video.hasAttribute('poster')) {
      video.setAttribute('poster', this.ensureProtocol(previewImage));
    }
    
    if (isHLS) {
      // 加载HLS.js并初始化视频
      this.loadHLSScript().then(() => {
        // 移除加载指示器
        const loadingIndicator = this.shadowRoot.querySelector('.video-loading');
        if (loadingIndicator) {
          loadingIndicator.remove();
        }
        
        if (window.Hls && window.Hls.isSupported()) {
          const hls = new window.Hls({
            maxBufferLength: 30,
            maxMaxBufferLength: 60
          });
          hls.loadSource(this.ensureProtocol(mediaUrl));
          hls.attachMedia(video);
          hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
            // 视频准备好后，添加到观察器
            video.muted = true;
            this.videoObserver.observe(video);
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari原生支持HLS
          video.src = this.ensureProtocol(mediaUrl);
          video.muted = true;
          this.videoObserver.observe(video);
        }
      });
    } else {
      // 标准视频格式，直接添加到观察器
      this.videoObserver.observe(video);
      
      // 移除加载指示器
      const loadingIndicator = this.shadowRoot.querySelector('.video-loading');
      if (loadingIndicator) {
        loadingIndicator.remove();
      }
      
      // 添加加载事件
      video.addEventListener('loadeddata', () => {
        console.log('视频已加载:', mediaUrl);
        if (video.paused) {
          video.play().catch(e => {
            console.log('自动播放失败，尝试静音播放:', e);
            video.muted = true;
            video.play().catch(e2 => console.log('静音播放也失败:', e2));
          });
        }
      });
      
      // 添加错误处理
      video.addEventListener('error', (e) => {
        console.error('视频加载失败:', e, video.error);
        // 如果有预览图，在视频加载失败时显示预览图
        if (previewImage) {
          const parent = video.parentNode;
          if (parent) {
            const errorImg = document.createElement('img');
            errorImg.src = this.ensureProtocol(previewImage);
            errorImg.alt = '视频无法播放';
            parent.appendChild(errorImg);
            video.style.display = 'none';
          }
        } else {
          // 没有预览图时显示默认错误图片
          const parent = video.parentNode;
          if (parent) {
            const errorImg = document.createElement('img');
            errorImg.src = 'https://via.placeholder.com/400x400?text=Video+Unavailable';
            errorImg.alt = '视频无法播放';
            parent.appendChild(errorImg);
            video.style.display = 'none';
          }
        }
      });
    }
  }
  
  // 确保URL有协议
  ensureProtocol(url) {
    if (!url) return '';
    
    if (url.startsWith('//')) {
      return `https:${url}`;
    }
    
    // 尝试修复不完整的URL
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/')) {
      return `https://${url}`;
    }
    
    return url;
  }
  
  // 加载HLS.js脚本
  loadHLSScript() {
    return new Promise((resolve) => {
      if (window.Hls) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }
  
  // 从YouTube URL提取视频ID，支持更多格式
  getYouTubeId(url) {
    if (!url) return null;
    
    try {
      // 处理短链接 (youtu.be)
      if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理标准链接 (youtube.com/watch?v=)
      if (url.includes('youtube.com/watch')) {
        // 确保URL有协议，以便可以创建URL对象
        const fullUrl = this.ensureProtocol(url);
        try {
          const urlObj = new URL(fullUrl);
          return urlObj.searchParams.get('v') || null;
        } catch (e) {
          console.error('解析YouTube URL失败:', e);
        }
      }
      
      // 处理嵌入链接 (youtube.com/embed/)
      if (url.includes('/embed/')) {
        const id = url.split('/embed/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理短视频链接 (youtube.com/shorts/)
      if (url.includes('/shorts/')) {
        const id = url.split('/shorts/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理直接带有视频ID的链接
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    } catch (error) {
      console.error('获取YouTube ID时出错:', error);
      return null;
    }
  }
  
  disconnectedCallback() {
    // 清理资源
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
  }
}
customElements.define('benefit-item-horizontal', BenefitItemHorizontal);


class BenefitsSection extends DataNode {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    this.shadowRoot.innerHTML = `
      <style>
        /* 重置样式 */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        /* 自定义元素样式 */
        :host {
          --theme-color: #e75a87;
          display: block;
        }
        
        .benefits-section {
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .benefits-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 40px;
          justify-content: center;
          width: 100%;
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .benefits-section {
            padding: 30px 15px;
          }
          
          .benefits-container {
            grid-template-columns: 1fr;
            margin-bottom: 40px;
            gap: 30px;
          }
        }
        
        /* 小屏幕手机适配 */
        @media (max-width: 480px) {
          .benefits-section {
            padding: 25px 12px;
          }
          
          .benefits-container {
            gap: 25px;
          }
        }
      </style>
      <div class="benefits-section">
        <div class="benefits-container">
          <slot></slot>
        </div>
      </div>
    `;
    
    // 获取元素
    this.sectionElement = this.shadowRoot.querySelector('.benefits-section');
    this.containerElement = this.shadowRoot.querySelector('.benefits-container');
  }
  
  connectedCallback() {
    // 设置主题色
    const themeColor = this.getAttribute('theme-color') || '#e75a87';
    this.style.setProperty('--theme-color', themeColor);
    
    // 添加窗口大小变化监听，以便动态调整布局
    this.handleResponsiveLayout();
    this._resizeHandler = () => this.handleResponsiveLayout();
    window.addEventListener('resize', this._resizeHandler);
    
    // 监听子元素变化，以便更新布局
    this._mutationObserver = new MutationObserver(() => {
      this.handleResponsiveLayout();
    });
    
    this._mutationObserver.observe(this, { 
      childList: true,
      subtree: false
    });
    
    // 传递主题色给所有子项
    this.updateChildrenThemeColor();
    
    // 防止重复初始化
    this.setAttribute('initialized', 'true');
  }
  
  // 处理响应式布局调整
  handleResponsiveLayout() {
    const isMobile = window.innerWidth <= 768;
    
    // 计算直接子元素中的benefit-item数量
    const benefitItems = Array.from(this.children).filter(
      child => child.tagName.toLowerCase() === 'benefit-item'
    );
    const itemCount = benefitItems.length;
    
    if (isMobile) {
      // 移动设备上始终使用单列布局
      this.containerElement.style.gridTemplateColumns = "1fr";
    } else {
      // 桌面设备上根据项目数量调整网格布局
      if (itemCount % 3 === 0) {
        this.containerElement.style.gridTemplateColumns = "repeat(3, 1fr)";
      } else if (itemCount % 2 === 0) {
        this.containerElement.style.gridTemplateColumns = "repeat(2, 1fr)";
      } else if (itemCount === 1) {
        this.containerElement.style.gridTemplateColumns = "1fr";
      } else {
        this.containerElement.style.gridTemplateColumns = "repeat(auto-fit, minmax(300px, 1fr))";
      }
    }
  }
  
  // 更新所有子benefit-item的主题色
  updateChildrenThemeColor() {
    const themeColor = this.getAttribute('theme-color') || '#e75a87';
    
    Array.from(this.children).forEach(child => {
      if (child.tagName.toLowerCase() === 'benefit-item') {
        child.setAttribute('theme-color', themeColor);
      }
    });
  }
  
  // 监听属性变化
  static get observedAttributes() {
    return ['theme-color'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    if (name === 'theme-color') {
      this.style.setProperty('--theme-color', newValue || '#e75a87');
      
      // 如果已初始化，则更新子元素主题色
      if (this.hasAttribute('initialized')) {
        this.updateChildrenThemeColor();
      }
    }
  }
  
  // 组件断开连接时清理事件监听和观察器
  disconnectedCallback() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
    
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
    }
  }
}
customElements.define('benefits-section', BenefitsSection);

class CustomerTestimonialsLanding extends HTMLElement {
constructor() {
  super();
  this.attachShadow({ mode: 'open' });
  
  // 默认状态
  this._activeTab = 'cat-satisfaction';
  this._currentPhotoIndex = 0;
  
  // 默认数据
  this._data = {};
  
  // 在构造函数中绑定事件处理函数
  this._handleVariantChange = this._handleVariantChange.bind(this);
  
  // 标记是否已设置事件监听器
  this._eventListenersInitialized = false;
}
  
// 变体变化处理函数
_handleVariantChange(event) {
  this.variant = event.detail.variant.id;
  this.render();
}
  
// 生命周期回调
connectedCallback() {
  this.render();
  this.setupEventListeners();
}

// 在组件断开连接时清理事件监听器
disconnectedCallback() {
  document.removeEventListener("on:variant:change", this._handleVariantChange);
  this._eventListenersInitialized = false;
}
  
// 设置数据的方法
setData(data) {
  this._data = { ...this._data, ...data };
  this.render();
}

// 设置事件监听
setupEventListeners() {
  // 只在第一次调用时设置全局事件监听器
  if (!this._eventListenersInitialized) {
    document.addEventListener("on:variant:change", this._handleVariantChange);
    this._eventListenersInitialized = true;
  }
  
  // 标签切换
  this.shadowRoot.querySelectorAll('.tab-button').forEach(button => {
    button.addEventListener('click', (e) => {
      this._activeTab = e.currentTarget.dataset.tabId;
      this.render();
    });
  });
}

// 渲染组件
render() {
  if(!this._data?.title){
    return '';
  }
  // 样式 - 改进的粉色主题
  const style = `
    :host {
      display: block;
      --main-color: #e75a87;
      --main-color-light: #f8d7e1;
      --main-color-lighter: #fdf4f7;
      --main-color-dark: #d03e6b;
      --text-primary: #333333;
      --text-secondary: #666666;
      --text-tertiary: #888888;
      --bg-color: #ffffff;
      --border-color: #f0f0f0;
      --shadow-sm: 0 2px 4px rgba(231, 90, 135, 0.08);
      --shadow-md: 0 4px 8px rgba(231, 90, 135, 0.12);
    }
    
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    
    .container {
      background-color: var(--bg-color);
      border-radius: 16px;
      box-shadow: 0 10px 25px -5px rgba(231, 90, 135, 0.1);
      width: 100%;
      overflow: hidden;
      position: relative;
    }
    
    .inner-container {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    
    .header {
      background: linear-gradient(135deg, var(--main-color), var(--main-color-dark));
      padding: 28px 24px;
      text-align: center;
      color: white;
      position: relative;
      overflow: hidden;
    }
    
    .header::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 60%);
      z-index: 0;
    }
    
    .title {
      font-size: 28px;
      font-weight: 700;
      margin-bottom: 0;
      position: relative;
      z-index: 1;
    }
    
    .content-container {
      display: flex;
      flex-direction: column;
      padding: 24px;
    }
    
    @media (min-width: 768px) {
      .content-container {
        flex-direction: row;
        gap: 24px;
      }
    }
    
    .sidebar {
      flex: 0 0 auto;
      margin-bottom: 20px;
    }
    
    @media (min-width: 768px) {
      .sidebar {
        width: 220px;
        margin-bottom: 0;
      }
    }
    
    .main-content {
      flex: 1;
    }
    
    .tabs-list {
      list-style: none;
      padding: 0;
      margin: 0;
      background-color: var(--main-color-lighter);
      border-radius: 12px;
      overflow: hidden;
    }
    
    @media (max-width: 767px) {
      .tabs-list {
        display: flex;
        justify-content: space-between;
      }
    }
    
    .tab-item {
      margin-bottom: 1px;
    }
    
    @media (max-width: 767px) {
      .tab-item {
        flex: 1;
        margin-bottom: 0;
        margin-right: 1px;
      }
      
      .tab-item:last-child {
        margin-right: 0;
      }
    }
    
    .tab-button {
      display: block;
      width: 100%;
      padding: 16px 20px;
      text-align: left;
      background: none;
      border: none;
      cursor: pointer;
      color: var(--text-secondary);
      font-weight: 500;
      transition: all 0.3s ease;
      position: relative;
      border-left: 3px solid transparent;
    }
    
    @media (max-width: 767px) {
      .tab-button {
        text-align: center;
        padding: 12px 8px;
        border-left: none;
        border-bottom: 3px solid transparent;
      }
    }
    
    .tab-button:hover {
      background-color: rgba(231, 90, 135, 0.08);
      color: var(--main-color);
    }
    
    .tab-button.active {
      background-color: white;
      color: var(--main-color);
      font-weight: 600;
      border-left-color: var(--main-color);
      box-shadow: 0 2px 4px rgba(231, 90, 135, 0.1);
    }
    
    @media (max-width: 767px) {
      .tab-button.active {
        border-left-color: transparent;
        border-bottom-color: var(--main-color);
      }
    }
    
    .tab-label-desktop {
      display: none;
    }
    
    .tab-label-mobile {
      display: inline;
    }
    
    @media (min-width: 768px) {
      .tab-label-desktop {
        display: inline;
      }
      
      .tab-label-mobile {
        display: none;
      }
    }
    
    .tab-content {
      background-color: var(--bg-color);
      border-radius: 12px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      border: 1px solid var(--border-color);
      padding: 24px;
      animation: fadeIn 0.3s ease;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .card {
      margin-bottom: 24px;
    }
    
    .card:last-child {
      margin-bottom: 0;
    }
    
    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--main-color);
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid var(--main-color-light);
    }
    
    .stats-item {
      margin-bottom: 20px;
    }
    
    .stats-item:last-child {
      margin-bottom: 0;
    }
    
    .stats-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }
    
    .stats-label {
      font-size: 14px;
      color: var(--text-secondary);
    }
    
    .stats-value {
      font-size: 14px;
      font-weight: 600;
      color: var(--main-color);
      background-color: var(--main-color-lighter);
      padding: 3px 10px;
      border-radius: 12px;
    }
    
    .progress-bar {
      background-color: #f0f0f0;
      height: 8px;
      border-radius: 4px;
      overflow: hidden;
      box-shadow: inset 0 1px 2px rgba(0,0,0,0.05);
    }
    
    .progress-fill {
      background: linear-gradient(90deg, var(--main-color), var(--main-color-dark));
      height: 100%;
      border-radius: 4px;
      transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }
    
    .daily-cost-container {
      background-color: var(--main-color-lighter);
      border-radius: 12px;
      margin-bottom: 20px;
      text-align: center;
      padding: 24px 20px;
      position: relative;
      overflow: hidden;
    }
    
    .daily-cost-container::before {
      content: '';
      position: absolute;
      top: -20px;
      right: -20px;
      width: 100px;
      height: 100px;
      background-color: rgba(231, 90, 135, 0.1);
      border-radius: 50%;
    }
    
    .daily-cost-value {
      font-size: 28px;
      font-weight: bold;
      color: var(--main-color);
      margin-bottom: 8px;
    }
    
    .cost-details {
      font-size: 13px;
      color: var(--text-secondary);
      line-height: 1.5;
    }

    .cost-details strong {
      color: var(--main-color);
    }
    
    .value-features {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    
    .feature-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 8px 12px;
      background-color: var(--bg-color);
      border-radius: 8px;
      border: 1px solid var(--main-color-light);
      transition: all 0.2s ease;
    }
    
    .feature-item:hover {
      transform: translateX(5px);
      box-shadow: var(--shadow-sm);
    }
    
    .feature-icon {
      color: white;
      font-weight: bold;
      background-color: var(--main-color);
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      flex-shrink: 0;
      font-size: 12px;
    }
    
    .feature-text {
      font-size: 14px;
      line-height: 1.5;
      color: var(--text-secondary);
    }
  `;
  
  // 渲染标签页
  const tabsHtml = this._data.tabs.map(tab => `
    <li class="tab-item">
      <button 
        class="tab-button ${this._activeTab === tab.id ? 'active' : ''}" 
        data-tab-id="${tab.id}"
      >
        <span class="tab-label-desktop">${tab.label}</span>
        <span class="tab-label-mobile">${tab.shortLabel}</span>
      </button>
    </li>
  `).join('');
  
  // 渲染内容
  let contentHtml = '';
  
  // 猫咪满意度标签页
  if (this._activeTab === 'cat-satisfaction') {
    const { catSatisfaction } = this._data;
    
    // 统计数据
    const statsHtml = catSatisfaction?.stats.map(stat => `
      <div class="stats-item">
        <div class="stats-header">
          <span class="stats-label">${stat.label}</span>
          <span class="stats-value">${stat.value}${stat.unit}</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${stat.value*100/stat.max}%"></div>
        </div>
      </div>
    `).join('');

    contentHtml = `
      <div class="card">
        <h3 class="card-title">${catSatisfaction.statsTitle}</h3>
        <div class="stats">
          ${statsHtml}
        </div>
      </div>
    `;
  }
  
  // 性价比标签页
  else if (this._activeTab === 'value-for-money') {
    const { valueForMoney, variantPrices, variant } = this._data;
    
    // 耐用性测试
    let durabilityHtml = '';
    if (valueForMoney.stats?.length > 0) {
      durabilityHtml = `
        <div class="card">
          <h3 class="card-title">${valueForMoney.statsTitle}</h3>
          <div class="stats">
            ${valueForMoney.stats.map(item => `
              <div class="stats-item">
                <div class="stats-header">
                  <span class="stats-label">${item.label}</span>
                  <span class="stats-value">${item.value}${item.unit}</span>
                </div>
                <div class="progress-bar">
                  <div class="progress-fill" style="width: ${item.value*100/item.max}%"></div>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }
    
    // 成本比较
    const price = variantPrices && (this.variant || variant) ? 
      variantPrices[this.variant || variant].finalPrice : 199;
    
    const dailyCost = (price/(365*10)).toFixed(2);
    
    const costInfoHtml = `
      <div class="card">
        <h3 class="card-title">${valueForMoney.costInfoTitle}</h3>
        <div class="daily-cost-container">
          <div class="daily-cost-value">≤$${dailyCost}/day</div>
          <div class="cost-details">Based on <strong>$${price}</strong> price, estimated <strong>10+ years</strong> lifespan</div>
        </div>
        
        <div class="value-features">
          <div class="feature-item">
            <div class="feature-icon">✓</div>
            <div class="feature-text">Premium materials for greater durability</div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">✓</div>
            <div class="feature-text">Superior craftsmanship for extended lifespan</div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">✓</div>
            <div class="feature-text">Thoughtful design for long-term use</div>
          </div>
        </div>
      </div>
    `;
    
    contentHtml = durabilityHtml + costInfoHtml;
  }
  
  // 易于组装标签页
  else if (this._activeTab === 'easy-assembly') {
    const { easyAssembly } = this._data;
    
    // 组装统计
    const statsHtml = easyAssembly.stats.map(stat => `
      <div class="stats-item">
        <div class="stats-header">
          <span class="stats-label">${stat.label}</span>
          <span class="stats-value">${stat.value}${stat.unit}</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${stat.value*100/stat.max}%"></div>
        </div>
      </div>
    `).join('');
    
    contentHtml = `
      <div class="card">
        <h3 class="card-title">${easyAssembly.statsTitle}</h3>
        <div class="stats">
          ${statsHtml}
        </div>
      </div>
    `;
  }
  
  // 完整HTML
  const html = `
    <style>${style}</style>
    <div class="container">
      <div class="inner-container">
        <div class="header">
          <h2 class="title">${this._data.title}</h2>
        </div>
        
        <div class="content-container">
          <div class="sidebar">
            <ul class="tabs-list">
              ${tabsHtml}
            </ul>
          </div>
          
          <div class="main-content">
            <div class="tab-content">
              ${contentHtml}
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
  
  this.shadowRoot.innerHTML = html;
  this.setupEventListeners();
}

// 监听属性变化
static get observedAttributes() {
  return ['data-config'];
}

// 属性变化时更新
attributeChangedCallback(name, oldValue, newValue) {
  if (name === 'data-config' && newValue) {
    try {
      const config = JSON.parse(newValue);
      this.setData(config);
    } catch (e) {
      console.error('Invalid JSON in data-config attribute', e);
    }
  }
}
}
customElements.define('customer-testimonials-landing', CustomerTestimonialsLanding);

class TestimonialCard extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    this.shadowRoot.innerHTML = `
      <style>
        /* 重置样式 */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        /* 评价卡片 */
        .testimonial-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }
        
        /* 用户图片容器 */
        .user-image-container {
          width: 80%;
          height: 80%;
          min-height: 80%;
          border-radius: 50%;
          overflow: hidden;
          margin-bottom: 20px;
        }
        
        .user-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        /* 评价内容 */
        .testimonial-text {
          font-size: 14px;
          line-height: 1.5;
          color: #000000;
          margin-bottom: 15px;
          font-style: italic;
          text-align: center;
          padding: 0 10px;
        }
        
        /* 用户名和地区 */
        .user-info {
          font-size: 14px;
          font-weight: 500;
          color: #000000;
          text-transform: uppercase;
        }
        
        /* 评分星星 */
        .rating {
          margin-bottom: 10px;
          color: var(--theme-color, #e75a87);
          font-size:18px;
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .user-image-container {
            width: 80%;
            height: 80%;
          }
        }
        
        @media (max-width: 480px) {
          .user-image-container {
            width: 80%;
            height: 80%;
          }
          
          .testimonial-text {
            font-size: 13px;
          }
      
          .user-info {
            font-size: 12px;
          }
        }
      </style>
      
      <div class="testimonial-card">
        <div class="user-image-container" id="image-container">
          <img class="user-image" id="user-image" alt="Customer" loading="lazy">
        </div>
        <div class="rating">★★★★★</div>
        <p class="testimonial-text" id="testimonial-text"></p>
        <div class="user-info" id="user-info"></div>
      </div>
    `;
  }
  
  connectedCallback() {
    this.render();
    
    // 应用主题色
    const themeColor = this.getAttribute('theme-color') || '#e75a87';
    this.shadowRoot.host.style.setProperty('--theme-color', themeColor);
  }
  
  render() {
    // 获取属性
    const image = this.getAttribute('image');
    const name = this.getAttribute('name');
    const location = this.getAttribute('location');
    const text = this.getAttribute('text');
    
    // 更新图片
    const imageContainer = this.shadowRoot.getElementById('image-container');
    const userImage = this.shadowRoot.getElementById('user-image');
    
    if (image) {
      userImage.src = image;
      userImage.alt = name || 'Customer';
      imageContainer.style.display = 'block';
    } else {
      imageContainer.style.display = 'none';
    }
    
    // 更新评价文本
    const testimonialText = this.shadowRoot.getElementById('testimonial-text');
    if (text) {
      testimonialText.textContent = `"${text}"`;
      testimonialText.style.display = 'block';
    } else {
      testimonialText.style.display = 'none';
    }
    
    // 更新用户信息
    const userInfo = this.shadowRoot.getElementById('user-info');
    if (name || location) {
      userInfo.textContent = `${name || ''}${location ? ', ' + location : ''}`;
      userInfo.style.display = 'block';
    } else {
      userInfo.style.display = 'none';
    }
  }
  
  static get observedAttributes() {
    return ['image', 'name', 'location', 'text', 'theme-color'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    if (name === 'theme-color') {
      this.shadowRoot.host.style.setProperty('--theme-color', newValue || '#e75a87');
    } else {
      this.render();
    }
  }
}

customElements.define('testimonial-card', TestimonialCard);


class TestimonialsSection extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    this.shadowRoot.innerHTML = `
    <style>
        /* 重置样式 */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        /* 主容器样式 */
        .testimonials-section {
          max-width: 1200px;
          margin: 0 auto;
          text-align: center;
        }
        
        /* 评价卡片容器 */
        .testimonials-container {
          display: flex;
          justify-content: center;
          gap: 30px;
          margin-bottom: 40px;
          flex-wrap: wrap;
        }
        
        /* 评价卡片插槽样式 */
        ::slotted(testimonial-card) {
          flex: 1;
          min-width: 250px;
          max-width: 350px;
        }
                
        /* 移动端轮播相关样式 */
        .carousel-container {
          width: 100%;
          overflow: hidden;
          position: relative;
          display: none; /* 默认隐藏，只在移动端显示 */
        }
        
        .carousel-track {
          display: flex;
          transition: transform 0.5s ease;
        }
        
        .carousel-slide {
          min-width: 100%;
          box-sizing: border-box;
          padding: 0 10px;
        }
        
        .carousel-indicators {
          display: flex;
          justify-content: center;
          margin-top: 15px;
          margin-bottom: 20px;
        }
        
        .carousel-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #ccc;
          margin: 0 5px;
          cursor: pointer;
        }
        
        .carousel-dot.active {
          background-color: var(--theme-color);
        }
        
        .carousel-nav {
          display: block;
        }
        
        .carousel-prev, .carousel-next {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background-color: rgba(255, 255, 255, 0.7);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-size: 18px;
          color: var(--theme-color);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }
        
        .carousel-prev {
          left: 5px;
        }
        
        .carousel-next {
          right: 5px;
        }
        
        /* 响应式设计增强 */
        @media (max-width: 768px) {
          .testimonials-container {
            display: none; /* 在移动端隐藏原始容器 */
          }
          
          .carousel-container {
            display: block; /* 在移动端显示轮播 */
          }
          
          ::slotted(testimonial-card) {
            max-width: 100%;
          }
        }
      </style>
    <div class="testimonials-section">
   
      <!-- 桌面版布局 -->
      <div class="testimonials-container">
        <slot name="testimonials"></slot>
      </div>
      
      <!-- 移动端轮播布局 -->
      <div class="carousel-container">
        <div class="carousel-track"></div>
        <div class="carousel-indicators"></div>
        <div class="carousel-nav">
          <button class="carousel-prev">❮</button>
          <button class="carousel-next">❯</button>
        </div>
      </div>
    </div>
    `;
    
    // 获取元素
    this.sectionElement = this.shadowRoot.querySelector('.testimonials-section');
    this.containerElement = this.shadowRoot.querySelector('.testimonials-container');
    
    // 轮播相关元素
    this.carouselContainer = this.shadowRoot.querySelector('.carousel-container');
    this.carouselTrack = this.shadowRoot.querySelector('.carousel-track');
    this.carouselIndicators = this.shadowRoot.querySelector('.carousel-indicators');
    this.carouselPrev = this.shadowRoot.querySelector('.carousel-prev');
    this.carouselNext = this.shadowRoot.querySelector('.carousel-next');
    
    // 轮播状态
    this.currentSlide = 0;
    this.slideCount = 0;
    this.autoplayInterval = null;
    this.isMobile = false; // 是否为移动端视图
    
    // 保存原始卡片引用
    this.testimonialCards = [];
  }
  
  connectedCallback() {
    // 设置主题色
    const themeColor = this.getAttribute('theme-color') || '#e75a87';
    this.sectionElement.style.setProperty('--theme-color', themeColor);
    
    // 设置轮播导航事件
    this.carouselPrev.addEventListener('click', () => this.prevSlide());
    this.carouselNext.addEventListener('click', () => this.nextSlide());
    
    // 添加触摸滑动支持
    this.setupTouchEvents();
    
    // 监听窗口大小变化
    this.resizeHandler = () => this.handleResize();
    window.addEventListener('resize', this.resizeHandler);
    
    // 设置MutationObserver来监听子元素变化
    this.setupMutationObserver();
    
    // 初始化轮播
    this.initCarousel();
    
    // 初始化时调用一次resize处理
    this.handleResize();
    
    // 防止重复渲染
    this.setAttribute('rendered', 'true');
  }
  
  initCarousel() {
    // 清空轮播容器
    this.carouselTrack.innerHTML = '';
    this.carouselIndicators.innerHTML = '';
    
    // 获取所有testimonial-card元素
    this.testimonialCards = Array.from(this.querySelectorAll('testimonial-card[slot="testimonials"]'));
    this.slideCount = this.testimonialCards.length;
    
    if (this.slideCount === 0) return;
    
    // 为每个testimonial创建轮播幻灯片
    this.testimonialCards.forEach((testimonial, index) => {
      // 克隆节点用于轮播
      const clone = testimonial.cloneNode(true);
      clone.removeAttribute('slot'); // 移除slot属性，避免被原始插槽捕获
      
      // 创建幻灯片容器
      const slide = document.createElement('div');
      slide.className = 'carousel-slide';
      slide.appendChild(clone);
      this.carouselTrack.appendChild(slide);
      
      // 创建指示器点
      const dot = document.createElement('div');
      dot.className = 'carousel-dot';
      if (index === 0) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToSlide(index));
      this.carouselIndicators.appendChild(dot);
    });
    
    // 重置当前幻灯片
    this.currentSlide = 0;
    this.updateCarousel();
  }
  
  // 轮播相关方法
  goToSlide(index) {
    this.currentSlide = index;
    this.updateCarousel();
  }
  
  nextSlide() {
    if (this.slideCount === 0) return;
    this.currentSlide = (this.currentSlide + 1) % this.slideCount;
    this.updateCarousel();
  }
  
  prevSlide() {
    if (this.slideCount === 0) return;
    this.currentSlide = (this.currentSlide - 1 + this.slideCount) % this.slideCount;
    this.updateCarousel();
  }
  
  updateCarousel() {
    if (this.slideCount === 0) return;
    
    // 更新轮播位置
    this.carouselTrack.style.transform = `translateX(-${this.currentSlide * 100}%)`;
    
    // 更新指示器
    const dots = this.carouselIndicators.querySelectorAll('.carousel-dot');
    dots.forEach((dot, index) => {
      if (index === this.currentSlide) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
    
    // 重置自动轮播计时器
    if (this.autoplayInterval) {
      this.startAutoplay();
    }
  }
  
  startAutoplay() {
    // 只在移动端启用自动轮播
    if (!this.isMobile || this.slideCount === 0) return;
    
    // 清除现有计时器
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }
    
    // 设置新计时器
    const interval = parseInt(this.getAttribute('autoplay-interval')) || 5000;
    this.autoplayInterval = setInterval(() => this.nextSlide(), interval);
  }
  
  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  setupTouchEvents() {
    let startX, moveX, diffX;
    const minSwipeDistance = 50; // 最小滑动距离
    
    this.carouselTrack.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      this.stopAutoplay(); // 触摸时暂停自动轮播
    }, { passive: true });
    
    this.carouselTrack.addEventListener('touchmove', (e) => {
      moveX = e.touches[0].clientX;
      diffX = moveX - startX;
      
      // 添加拖动效果
      if (this.slideCount > 0) {
        const translateX = -this.currentSlide * 100 + (diffX / this.carouselTrack.offsetWidth * 100);
        this.carouselTrack.style.transform = `translateX(${translateX}%)`;
        this.carouselTrack.style.transition = 'none';
      }
    }, { passive: true });
    
    this.carouselTrack.addEventListener('touchend', (e) => {
      if (this.slideCount === 0) return;
      
      this.carouselTrack.style.transition = 'transform 0.5s ease';
      
      if (Math.abs(diffX) > minSwipeDistance) {
        if (diffX > 0) {
          this.prevSlide();
        } else {
          this.nextSlide();
        }
      } else {
        // 如果滑动距离不够，回到当前幻灯片
        this.updateCarousel();
      }
      
      // 恢复自动轮播
      const autoplay = this.getAttribute('autoplay') !== 'false';
      if (autoplay && this.isMobile) {
        this.startAutoplay();
      }
    }, { passive: true });
  }
  
  handleResize() {
    // 检测是否为移动端视图
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 768;
    
    // 如果视图类型发生变化
    if (wasMobile !== this.isMobile) {
      if (this.isMobile) {
        // 切换到移动端：启用轮播
        const autoplay = this.getAttribute('autoplay') !== 'false';
        if (autoplay) {
          this.startAutoplay();
        }
        this.updateCarousel(); // 确保轮播正确显示
      } else {
        // 切换到桌面端：停用轮播
        this.stopAutoplay();
      }
    }
  }
  
  // 监听子元素变化
  setupMutationObserver() {
    // 创建MutationObserver来监听子元素变化
    this.observer = new MutationObserver((mutations) => {
      let needsUpdate = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          // 检查是否有testimonial-card元素被添加或移除
          const addedCards = Array.from(mutation.addedNodes).filter(
            node => node.nodeName.toLowerCase() === 'testimonial-card' && 
                  node.getAttribute('slot') === 'testimonials'
          );
          
          const removedCards = Array.from(mutation.removedNodes).filter(
            node => node.nodeName.toLowerCase() === 'testimonial-card' && 
                  node.getAttribute('slot') === 'testimonials'
          );
          
          if (addedCards.length > 0 || removedCards.length > 0) {
            needsUpdate = true;
          }
        }
      });
      
      if (needsUpdate) {
        this.initCarousel();
        
        // 如果在移动端，重新启动自动轮播
        if (this.isMobile && this.getAttribute('autoplay') !== 'false') {
          this.startAutoplay();
        }
      }
    });
    
    // 开始观察
    this.observer.observe(this, { childList: true, subtree: false });
  }
  
  // 监听属性变化
  static get observedAttributes() {
    return ['theme-color', 'autoplay', 'autoplay-interval'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    if (this.hasAttribute('rendered')) {
      if (name === 'theme-color') {
        const themeColor = newValue || '#e75a87';
        this.sectionElement.style.setProperty('--theme-color', themeColor);
        
        // 更新轮播指示器颜色
        const dots = this.carouselIndicators.querySelectorAll('.carousel-dot.active');
        dots.forEach(dot => {
          dot.style.backgroundColor = themeColor;
        });
        
        // 更新所有卡片的主题色（包括原始卡片和轮播中的克隆卡片）
        this.querySelectorAll('testimonial-card').forEach(card => {
          card.setAttribute('theme-color', themeColor);
        });
      }
      
      if (name === 'autoplay' && this.isMobile) {
        if (newValue === 'false') {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      }
      
      if (name === 'autoplay-interval' && this.autoplayInterval && this.isMobile) {
        this.startAutoplay(); // 重新启动自动轮播以应用新间隔
      }
    }
  }
  
  disconnectedCallback() {
    // 清理事件监听器
    window.removeEventListener('resize', this.resizeHandler);
    this.stopAutoplay();
    
    // 停止观察
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

customElements.define('testimonials-section', TestimonialsSection);





  // 评价卡片组件
  class SimpleTestimonialCard extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      
      // 获取模板
      this.shadowRoot.innerHTML = `
      <style>
    /* 评价卡片 - 左右布局 */
    :host {
      display: block;
      flex-shrink: 0;
      width: 100%;
      box-sizing: border-box;
      padding: 0 15px;
    }
    
    .testimonial-card {
      display: flex;
      align-items: center;
      text-align: left;
      gap: 30px;
      padding: 20px;
      max-width: 900px;
      margin: 0 auto;
      height: 100%;
      box-sizing: border-box;
    }

    /* 用户图片容器 - 左侧 */
    .user-image-container {
      width: 150px;
      height: 150px;
      min-width: 150px; /* 保持图片不被压缩 */
      border-radius: 50%;
      overflow: hidden;
      border: 1px solid #eee;
    }

    .user-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    /* 内容容器 - 右侧 */
    .content-container {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    /* 评分星星 */
    .rating {
      color: var(--theme-color, #e75a87);
      font-size: 18px;
      margin-bottom: 15px;
    }

    /* 评价内容 */
    .testimonial-text {
      font-size: 16px;
      line-height: 1.5;
      color: #000000;
      margin-bottom: 15px;
      font-style: italic;
    }

    /* 用户名和地区 */
    .user-info {
      font-size: 14px;
      font-weight: 500;
      color: #000000;
      text-transform: uppercase;
    }
    
    /* 响应式设计 */
    @media (max-width: 768px) {
      .testimonial-card {
        flex-direction: column;
        text-align: center;
        gap: 15px;
      }

      .user-image-container {
        width: 120px;
        height: 120px;
        min-width: 120px;
      }

      .content-container {
        align-items: center;
      }
    }

    @media (max-width: 480px) {
      .user-image-container {
        width: 100px;
        height: 100px;
        min-width: 100px;
      }

      .testimonial-text {
        font-size: 14px;
      }

      .rating {
        font-size: 16px;
      }

      .user-info {
        font-size: 12px;
      }
    }
  </style>
  <div class="testimonial-card">
    <div class="user-image-container">
      <img class="user-image" loading="lazy">
    </div>
    <div class="content-container">
      <div class="rating">★★★★★</div>
      <p class="testimonial-text"></p>
      <div class="user-info"></div>
    </div>
  </div>
      `;
      
      // 获取元素
      this.cardElement = this.shadowRoot.querySelector('.testimonial-card');
      this.imageElement = this.shadowRoot.querySelector('.user-image');
      this.textElement = this.shadowRoot.querySelector('.testimonial-text');
      this.userInfoElement = this.shadowRoot.querySelector('.user-info');
      this.ratingElement = this.shadowRoot.querySelector('.rating');
    }
    
    connectedCallback() {
      // 设置主题色
      const themeColor = this.getAttribute('theme-color') || '#e75a87';
      this.cardElement.style.setProperty('--theme-color', themeColor);
      
      // 设置图片
      const image = this.getAttribute('image');
      if (image) {
        this.imageElement.src = image;
        this.imageElement.alt = this.getAttribute('name') || 'Customer';
      } else {
        // 如果没有图片，隐藏图片容器
        this.shadowRoot.querySelector('.user-image-container').style.display = 'none';
      }
      
      // 设置评价文本
      const text = this.getAttribute('text');
      if (text) {
        this.textElement.textContent = `"${text}"`;
      } else {
        this.textElement.style.display = 'none';
      }
      
      // 设置用户信息
      const name = this.getAttribute('name');
      const location = this.getAttribute('location');
      if (name || location) {
        this.userInfoElement.textContent = `${name || ''}${location ? ', ' + location : ''}`;
      } else {
        this.userInfoElement.style.display = 'none';
      }
    }
    
    // 监听属性变化
    static get observedAttributes() {
      return ['theme-color', 'image', 'text', 'name', 'location'];
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      
      if (name === 'theme-color') {
        const themeColor = newValue || '#e75a87';
        this.cardElement.style.setProperty('--theme-color', themeColor);
      }
      
      if (name === 'image') {
        if (newValue) {
          this.imageElement.src = newValue;
          this.shadowRoot.querySelector('.user-image-container').style.display = '';
        } else {
          this.shadowRoot.querySelector('.user-image-container').style.display = 'none';
        }
      }
      
      if (name === 'text') {
        if (newValue) {
          this.textElement.textContent = `"${newValue}"`;
          this.textElement.style.display = '';
        } else {
          this.textElement.style.display = 'none';
        }
      }
      
      if (name === 'name' || name === 'location') {
        const name = this.getAttribute('name');
        const location = this.getAttribute('location');
        if (name || location) {
          this.userInfoElement.textContent = `${name || ''}${location ? ', ' + location : ''}`;
          this.userInfoElement.style.display = '';
        } else {
          this.userInfoElement.style.display = 'none';
        }
      }
    }
  }
  
  // 评价部分组件
  class SimpleTestimonialsSection extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      
      // 获取模板
      
      this.shadowRoot.innerHTML = `
      <style>
    /* 重置样式 */
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    /* 主容器样式 */
    .testimonials-section {
      margin: 0 auto;
      text-align: center;
      position: relative;
    }

    /* 轮播容器 */
    .carousel-container {
      width: 100%;
      overflow: hidden;
      position: relative;
      margin-bottom: 40px;
    }
    
    /* 轮播轨道 - 关键部分 */
    .carousel-track {
      display: flex;
      transition: transform 0.5s ease;
      width: 100%;
    }
    
    /* 轮播指示器 */
    .carousel-indicators {
      display: flex;
      justify-content: center;
      margin-top: 15px;
      margin-bottom: 20px;
    }

    .carousel-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #ccc;
      margin: 0 5px;
      cursor: pointer;
      transition: background-color 0.3s ease;
    }

    .carousel-dot.active {
      background-color: var(--theme-color, #e75a87);
    }

    /* 轮播导航按钮 */
    .carousel-prev, .carousel-next {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background-color: rgba(255, 255, 255, 0.7);
      border: none;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      font-size: 18px;
      color: var(--theme-color, #e75a87);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      transition: background-color 0.3s ease;
    }
    
    .carousel-prev:hover, .carousel-next:hover {
      background-color: rgba(255, 255, 255, 0.9);
    }

    .carousel-prev {
      left: 5px;
    }

    .carousel-next {
      right: 5px;
    }
  </style>
  <div class="testimonials-section">
    <div class="carousel-container">
      <!-- 轮播轨道 - 用于水平滑动 -->
      <div class="carousel-track">
        <slot name="items"></slot>
      </div>
      
      <div class="carousel-indicators"></div>
      <div class="carousel-nav">
        <button class="carousel-prev">❮</button>
        <button class="carousel-next">❯</button>
      </div>
    </div>
  </div>
      `;
      
      // 获取元素
      this.sectionElement = this.shadowRoot.querySelector('.testimonials-section');
      this.carouselContainer = this.shadowRoot.querySelector('.carousel-container');
      this.carouselTrack = this.shadowRoot.querySelector('.carousel-track');
      this.carouselIndicators = this.shadowRoot.querySelector('.carousel-indicators');
      this.carouselPrev = this.shadowRoot.querySelector('.carousel-prev');
      this.carouselNext = this.shadowRoot.querySelector('.carousel-next');
      this.slotElement = this.shadowRoot.querySelector('slot[name="items"]');
      
      // 轮播状态
      this.currentSlide = 0;
      this.cards = [];
      this.autoplayInterval = null;
      
      // 监听插槽变化
      this.slotElement.addEventListener('slotchange', () => {
        this.initializeCarousel();
      });
    }
    
    connectedCallback() {
      // 设置主题色
      const themeColor = this.getAttribute('theme-color') || '#e75a87';
      this.sectionElement.style.setProperty('--theme-color', themeColor);
      
      // 初始化轮播
      setTimeout(() => {
        this.initializeCarousel();
      }, 100);
      
      // 设置轮播导航事件
      this.carouselPrev.addEventListener('click', () => this.prevSlide());
      this.carouselNext.addEventListener('click', () => this.nextSlide());
      
      // 添加触摸滑动支持
      this.setupTouchEvents();
      
      // 添加窗口大小变化监听
      window.addEventListener('resize', () => {
        this.updateCarousel(false);
      });
      
      // 启动自动轮播
      const autoplay = this.getAttribute('autoplay') !== 'false';
      if (autoplay) {
        this.startAutoplay();
      }
    }
    
    initializeCarousel() {
      // 获取所有卡片元素
      this.cards = this.slotElement.assignedElements().filter(el => 
        el.tagName.toLowerCase() === 'simple-testimonial-card'
      );
      if (this.cards.length === 0) {
        console.warn('No testimonial cards found to display');
        return;
      }
      
      // 清空指示器
      this.carouselIndicators.innerHTML = '';
      
      // 创建指示器
      this.cards.forEach((card, index) => {
        // 创建指示器点
        const dot = document.createElement('div');
        dot.className = 'carousel-dot';
        if (index === 0) dot.classList.add('active');
        dot.addEventListener('click', () => this.goToSlide(index));
        this.carouselIndicators.appendChild(dot);
        
        // 设置卡片主题色
        const themeColor = this.getAttribute('theme-color');
        if (themeColor) {
          card.setAttribute('theme-color', themeColor);
        }
      });
      
      // 初始化显示第一张卡片
      this.currentSlide = 0;
      this.updateCarousel();
    }
    
    // 轮播相关方法
    goToSlide(index) {
      this.currentSlide = index;
      this.updateCarousel();
    }
    
    nextSlide() {
      if (this.cards.length === 0) return;
      this.currentSlide = (this.currentSlide + 1) % this.cards.length;
      this.updateCarousel();
    }
    
    prevSlide() {
      if (this.cards.length === 0) return;
      this.currentSlide = (this.currentSlide - 1 + this.cards.length) % this.cards.length;
      this.updateCarousel();
    }
    
    updateCarousel(animate = true) {
      if (this.cards.length === 0) return;
      
      // 计算轮播轨道的位移
      const slideWidth = this.carouselContainer.offsetWidth;
      const offset = -this.currentSlide * slideWidth;
      
      // 设置过渡动画
      if (animate) {
        this.carouselTrack.style.transition = 'transform 0.5s ease';
      } else {
        this.carouselTrack.style.transition = 'none';
      }
      
      // 移动轮播轨道
      this.carouselTrack.style.transform = `translateX(${offset}px)`;
      
      // 更新指示器
      const dots = this.carouselIndicators.querySelectorAll('.carousel-dot');
      dots.forEach((dot, index) => {
        if (index === this.currentSlide) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
      
      // 重置自动轮播计时器
      if (this.autoplayInterval) {
        this.startAutoplay();
      }
    }
    
    startAutoplay() {
      // 清除现有计时器
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
      }
      
      // 设置新计时器
      const interval = parseInt(this.getAttribute('autoplay-interval')) || 5000;
      this.autoplayInterval = setInterval(() => this.nextSlide(), interval);
    }
    
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }
    
    setupTouchEvents() {
      let startX, moveX, diffX;
      const minSwipeDistance = 50; // 最小滑动距离
      
      this.carouselContainer.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        this.stopAutoplay(); // 触摸时暂停自动轮播
        this.carouselTrack.style.transition = 'none'; // 触摸时移除过渡效果
      }, { passive: true });
      
      this.carouselContainer.addEventListener('touchmove', (e) => {
        moveX = e.touches[0].clientX;
        diffX = moveX - startX;
        
        // 计算当前偏移量
        const slideWidth = this.carouselContainer.offsetWidth;
        const baseOffset = -this.currentSlide * slideWidth;
        const newOffset = baseOffset + diffX;
        
        // 应用拖动效果，添加阻尼效果防止过度拖动
        this.carouselTrack.style.transform = `translateX(${newOffset}px)`;
      }, { passive: true });
      
      this.carouselContainer.addEventListener('touchend', (e) => {
        if (Math.abs(diffX) > minSwipeDistance) {
          if (diffX > 0) {
            this.prevSlide();
          } else {
            this.nextSlide();
          }
        } else {
          // 如果滑动距离不够，恢复到当前幻灯片
          this.updateCarousel();
        }
        
        // 恢复自动轮播
        const autoplay = this.getAttribute('autoplay') !== 'false';
        if (autoplay) {
          this.startAutoplay();
        }
      }, { passive: true });
    }
    
    // 监听属性变化
    static get observedAttributes() {
      return ['theme-color', 'autoplay', 'autoplay-interval'];
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      
      if (name === 'theme-color') {
        const themeColor = newValue || '#e75a87';
        this.sectionElement.style.setProperty('--theme-color', themeColor);
        
        // 更新所有卡片的主题色
        this.cards.forEach(card => {
          card.setAttribute('theme-color', themeColor);
        });
      }
      
      if (name === 'autoplay') {
        if (newValue === 'false') {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      }
      
      if (name === 'autoplay-interval' && this.autoplayInterval) {
        this.startAutoplay(); // 重新启动自动轮播以应用新间隔
      }
    }
  }
  
  // 注册自定义元素
  if (!customElements.get('simple-testimonial-card')) {
    customElements.define('simple-testimonial-card', SimpleTestimonialCard);
  }
  
  if (!customElements.get('simple-testimonials-section')) {
    customElements.define('simple-testimonials-section', SimpleTestimonialsSection);
  }


class SatisfactionGuarantee extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // 默认数据 - 已更新
    this._data = {
      title: "60-Day Satisfaction Guarantee & 1-Year Warranty", // 更新标题
      guaranteePoints: [
        {
          icon: "return",
          title: "Free Returns",
          description: "If you're not completely satisfied, return your product within 60 days for a full refund."
        },
        {
          icon: "warranty", // 更改图标名称
          title: "1-Year Warranty", // 更改为1年保修
          description: "Your product is covered by our comprehensive 1-year warranty against manufacturing defects." // 更新描述
        },
        {
          icon: "shipping",
          title: "Free Return Shipping",
          description: "We'll cover the shipping costs for returns and exchanges within the 60-day period."
        }
      ],
      steps: [
        {
          number: 1,
          title: "Contact Us",
          description: "Reach out through email, or chat to initiate your return or exchange."
        },
        {
          number: 2,
          title: "Get Return Label",
          description: "We'll email you a prepaid return shipping label within 24 hours."
        },
        {
          number: 3,
          title: "Carrier Pickup", // 更改为承运商上门取件
          description: "We'll arrange for a carrier to pick up your package directly from your location." // 更新描述
        },
        {
          number: 4,
          title: "Quick Resolution",
          description: "Refunds are processed within 3-5 business days of receiving your return."
        }
      ],
      finePrint: "Our guarantee covers normal use according to product instructions. Damage due to misuse or unauthorized modifications may void the guarantee. The 1-year warranty begins from the date of purchase. Please see our full terms and conditions for details." // 更新细则
    };
  }

  // 生命周期回调
  connectedCallback() {
    this.render();
  }

  // 设置数据的方法
  setData(data) {
    this._data = { ...this._data, ...data };
    this.render();
  }

  // 图标渲染 - 添加了warranty图标
  renderIcon(iconName) {
    switch(iconName) {
      case 'return':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24">
            <path d="M21 8v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            <polyline points="3 8 12 16 21 8"></polyline>
          </svg>
        `;
      case 'warranty': // 新增的warranty图标
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            <path d="M9 12l2 2 4-4"></path>
          </svg>
        `;
      case 'exchange':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24">
            <path d="M17 1l4 4-4 4"></path>
            <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
            <path d="M7 23l-4-4 4-4"></path>
            <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
          </svg>
        `;
      case 'shipping':
        return `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="24" height="24">
            <rect x="1" y="3" width="15" height="13"></rect>
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
            <circle cx="5.5" cy="18.5" r="2.5"></circle>
            <circle cx="18.5" cy="18.5" r="2.5"></circle>
          </svg>
        `;
      default:
        return '';
    }
  }

  // 渲染组件
  render() {
    // 样式
    const style = `
      :host {
        display: block;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
        --main-color: #e75a87;
        --main-color-light: #f4b9cc;
        --main-color-dark: #c41c52;
        --bg-color: #ffffff;
        --text-primary: #1f2937;
        --text-secondary: #4b5563;
        --text-tertiary: #6b7280;
        --border-color: #f3f4f6;
        --bg-light: #f9fafb;
      }
      
      .container {
        margin: 32px auto;
        background-color: var(--bg-color);
        border-radius: 12px;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        overflow: hidden;
      }
      
      .inner-container {
        padding: 32px 24px;
      }
      
      @media (max-width: 640px) {
        .inner-container {
          padding: 24px 16px;
        }
      }
      
      .section-title {
        font-size: 24px;
        font-weight: 700;
        text-align: center;
        margin-bottom: 32px;
        color: var(--text-primary);
      }
      
      .guarantee-points {
        display: grid;
        gap: 24px;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        margin-bottom: 48px;
      }
      
      .guarantee-item {
        background-color: var(--bg-light);
        border-radius: 8px;
        padding: 24px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        transition: transform 0.2s, box-shadow 0.2s;
      }
      
      .guarantee-item:hover {
        transform: translateY(-4px);
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03);
      }
      
      .guarantee-icon {
        width: 48px;
        height: 48px;
        background-color: var(--main-color-light);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 16px;
        color: white;
      }
      
      .guarantee-title {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 8px 0;
        color: var(--text-primary);
      }
      
      .guarantee-description {
        font-size: 14px;
        color: var(--text-secondary);
        margin: 0;
        line-height: 1.5;
      }
      
      .steps-container {
        margin-bottom: 32px;
      }
      
      .steps-title {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 24px;
        text-align: center;
        color: var(--text-primary);
      }
      
      .steps-grid {
        display: grid;
        gap: 16px;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      }
      
      .step-item {
        position: relative;
        padding: 24px 16px 16px;
        background-color: var(--bg-light);
        border-radius: 8px;
        border-top: 3px solid var(--main-color);
      }
      
      .step-number {
        position: absolute;
        top: -15px;
        left: 16px;
        width: 30px;
        height: 30px;
        background-color: var(--main-color);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
      }
      
      .step-title {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 8px 0;
        color: var(--text-primary);
      }
      
      .step-description {
        font-size: 14px;
        color: var(--text-secondary);
        margin: 0;
        line-height: 1.5;
      }
      
      .fine-print {
        font-size: 12px;
        color: var(--text-tertiary);
        text-align: center;
        max-width: 700px;
        margin: 0 auto 32px;
        padding: 0 16px;
        line-height: 1.5;
      }
      
    `;
    
    // 渲染保障点
    const guaranteePointsHtml = this._data.guaranteePoints.map(point => `
      <div class="guarantee-item">
        <div class="guarantee-icon">
          ${this.renderIcon(point.icon)}
        </div>
        <h3 class="guarantee-title">${point.title}</h3>
        <p class="guarantee-description">${point.description}</p>
      </div>
    `).join('');
    
    // 渲染步骤
    const stepsHtml = this._data.steps.map(step => `
      <div class="step-item">
        <div class="step-number">${step.number}</div>
        <h4 class="step-title">${step.title}</h4>
        <p class="step-description">${step.description}</p>
      </div>
    `).join('');
    
    // 完整HTML
    const html = `
      <style>${style}</style>
      <div class="container">
        <div class="inner-container">
     
          <div class="guarantee-points">
            ${guaranteePointsHtml}
          </div>
          
          <div class="steps-container">
            <h3 class="steps-title">How It Works</h3>
            <div class="steps-grid">
              ${stepsHtml}
            </div>
          </div>
          
          <p class="fine-print">${this._data.finePrint}</p>
          
        </div>
      </div>
    `;
    
    this.shadowRoot.innerHTML = html;
  }

  // 监听属性变化
  static get observedAttributes() {
    return ['data-config'];
  }

  // 属性变化时更新
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-config' && newValue) {
      try {
        const config = JSON.parse(newValue);
        this.setData(config);
      } catch (e) {
        console.error('Invalid JSON in data-config attribute', e);
      }
    }
  }
}

// 注册Web Component
customElements.define('satisfaction-guarantee', SatisfactionGuarantee);


  // 特性卡片组件
  class LandingFeatureCard extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      
      // 获取模板
      
      this.shadowRoot.innerHTML = `
        <style>
        :host {
          display: block;
        }
        
        .satisfaction-card {
          background-color: #fff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          cursor: pointer;
          position: relative;
          height: 100%;
        }
        
        .satisfaction-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(196, 28, 82, 0.15);
        }
        
        .card-image {
          height: 480px;
          overflow: hidden;
          position: relative;
        }
        
        .card-badge {
          position: absolute;
          top: 15px;
          right: 15px;
          background-color: var(--main-color, #FFA200);
          color: white;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }
        
        .card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        
        .satisfaction-card:hover .card-image img {
          transform: scale(1.05);
        }
        
        .card-content {
          padding: 30px;
        }
        
        .card-content h3 {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 12px 0;
          color: var(--main-color, #FFA200);
        }
        
        .card-content p {
          font-size: 16px;
          color: #666;
          margin: 0 0 20px 0;
          line-height: 1.5;
        }
        
        .feature-list {
          padding-left: 5px;
          margin: 20px 0;
          list-style-type: none;
        }
        
        .feature-list li {
          margin-bottom: 10px;
          color: #555;
          font-size: 15px;
          position: relative;
          padding-left: 25px;
          line-height: 1.5;
        }
        
        .feature-list li::before {
          content: "✓";
          color: var(--main-color, #FFA200);
          font-weight: bold;
          position: absolute;
          left: 0;
          top: 0;
        }
        
        .view-products {
          color: var(--main-color, #FFA200);
          font-weight: 600;
          font-size: 16px;
          margin-top: 25px;
          display: inline-block;
          position: relative;
          transition: all 0.2s;
          padding-bottom: 3px;
          border-bottom: 2px solid transparent;
        }
        
        .arrow {
          display: inline-block;
          transition: transform 0.2s ease;
          margin-left: 2px;
        }
        
        .satisfaction-card:hover .view-products {
          color: var(--main-color-dark, #E69200);
          border-bottom: 2px solid var(--main-color-dark, #E69200);
        }
        
        .satisfaction-card:hover .arrow {
          transform: translateX(5px);
        }
        
        @media (max-width: 768px) {
          .card-image {
            height: 325px;
          }
          
          .card-content h3 {
            font-size: 20px;
          }
          
          .card-content p {
            font-size: 15px;
          }
          
          .feature-list li {
            font-size: 14px;
          }
        }
      </style>
      
      <div class="satisfaction-card">
        <div class="card-image">
          <img alt="">
          <div class="card-badge"></div>
        </div>
        <div class="card-content">
          <h3></h3>
          <p></p>
          <ul class="feature-list"></ul>
          <div class="view-products">View Product Series <span class="arrow">→</span></div>
        </div>
      </div>
      `;
      
      // 获取元素
      this.cardElement = this.shadowRoot.querySelector('.satisfaction-card');
      this.imageElement = this.shadowRoot.querySelector('.card-image img');
      this.badgeElement = this.shadowRoot.querySelector('.card-badge');
      this.titleElement = this.shadowRoot.querySelector('h3');
      this.descriptionElement = this.shadowRoot.querySelector('p');
      this.featureListElement = this.shadowRoot.querySelector('.feature-list');
      this.viewProductsElement = this.shadowRoot.querySelector('.view-products');
      
      // 绑定点击事件
      this.cardElement.addEventListener('click', () => {
        const link = this.getAttribute('link') || '#product-series';
        window.location.href = link;
      });
    }
    
    connectedCallback() {
      // 设置主题色
      const mainColor = this.getAttribute('main-color') || '#FFA200';
      const mainColorDark = this.getAttribute('main-color-dark') || '#E69200';
      
      this.cardElement.style.setProperty('--main-color', mainColor);
      this.cardElement.style.setProperty('--main-color-dark', mainColorDark);
      
      // 设置图片
      const image = this.getAttribute('image');
      if (image) {
        this.imageElement.src = image;
        this.imageElement.alt = this.getAttribute('title') || 'Feature Image';
      }
      
      // 设置徽章
      const badge = this.getAttribute('badge');
      if (badge) {
        this.badgeElement.textContent = badge;
      } else {
        this.badgeElement.style.display = 'none';
      }
      
      // 设置标题
      const title = this.getAttribute('title');
      if (title) {
        this.titleElement.textContent = title;
      }
      
      // 设置描述
      const description = this.getAttribute('description');
      if (description) {
        this.descriptionElement.textContent = description;
      }
      
      // 设置特性列表
      const features = this.getAttribute('features');
      if (features) {
        try {
          const featureArray = JSON.parse(features);
          featureArray.forEach(feature => {
            const li = document.createElement('li');
            li.textContent = feature;
            this.featureListElement.appendChild(li);
          });
        } catch (e) {
          console.error('Invalid features JSON format', e);
        }
      }
      
      // 设置按钮文本
      const buttonText = this.getAttribute('button-text');
      if (buttonText) {
        this.viewProductsElement.textContent = buttonText + ' ';
        const arrow = document.createElement('span');
        arrow.className = 'arrow';
        arrow.textContent = '→';
        this.viewProductsElement.appendChild(arrow);
        this.viewProductsElement.style.display = "inline-block"
      }else{
        this.viewProductsElement.style.display = "none"
      }
    }
    
    // 监听属性变化
    static get observedAttributes() {
      return ['main-color', 'main-color-dark', 'image', 'badge', 'title', 'description', 'features', 'button-text', 'link'];
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      
      switch (name) {
        case 'main-color':
          this.cardElement.style.setProperty('--main-color', newValue || '#FFA200');
          break;
        case 'main-color-dark':
          this.cardElement.style.setProperty('--main-color-dark', newValue || '#E69200');
          break;
        case 'image':
          if (newValue) {
            this.imageElement.src = newValue;
          }
          break;
        case 'badge':
          if (newValue) {
            this.badgeElement.textContent = newValue;
            this.badgeElement.style.display = '';
          } else {
            this.badgeElement.style.display = 'none';
          }
          break;
        case 'title':
          this.titleElement.textContent = newValue || '';
          break;
        case 'description':
          this.descriptionElement.textContent = newValue || '';
          break;
        case 'features':
          if (newValue) {
            this.featureListElement.innerHTML = '';
            try {
              const featureArray = JSON.parse(newValue);
              featureArray.forEach(feature => {
                const li = document.createElement('li');
                li.textContent = feature;
                this.featureListElement.appendChild(li);
              });
            } catch (e) {
              console.error('Invalid features JSON format', e);
            }
          }
          break;
        case 'button-text':
          if (newValue) {
            this.viewProductsElement.textContent = newValue + ' ';
            const arrow = document.createElement('span');
            arrow.className = 'arrow';
            arrow.textContent = '→';
            this.viewProductsElement.appendChild(arrow);
          }
          break;
      }
    }
  }
  
  // 特性部分组件
  class LandingFeatureSection extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      
      // 获取模板
      
      this.shadowRoot.innerHTML = `
      <style>
      :host {
        display: block;
      }
      
      .satisfaction-container {
        margin: 0 auto;
        color: #333;
      }
      
      .satisfaction-header {
        text-align: center;
        margin-bottom: 60px;
      }
      
      .satisfaction-header h2 {
        font-size: 36px;
        font-weight: 700;
        margin-bottom: 20px;
        color: var(--main-color-dark, #E69200);
        letter-spacing: -0.5px;
      }
      
      .subtitle {
        font-size: 18px;
        color: #666;
        max-width: 700px;
        margin: 0 auto;
        line-height: 1.6;
        font-weight: 300;
      }
      
      .satisfaction-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(370px, 1fr));
        gap: 30px;
        margin-bottom: 70px;
      }
      
      .satisfaction-footer {
        margin-top: 40px;
      }
      
      .satisfaction-stats {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 60px;
        padding: 40px;
        background: linear-gradient(135deg, var(--main-color-light, #FFB733), var(--main-color, #FFA200));
        border-radius: 8px;
        box-shadow: 0 10px 30px rgba(231, 90, 135, 0.1);
      }
      
      .stat-item {
        text-align: center;
        min-width: 140px;
      }
      
      .stat-value {
        font-size: 42px;
        font-weight: 800;
        color: white; 
        margin-bottom: 10px;
        line-height: 1;
        position: relative;
      }
      
      .percent, .plus, .unit {
        font-size: 24px;
        font-weight: 600;
        position: relative;
        top: -5px;
        margin-left: 2px;
      }
      
      .stat-label {
        font-size: 16px;
        color: white;
        font-weight: 500;
      }
      
      @media (max-width: 768px) {
        .satisfaction-grid {
          grid-template-columns: 1fr;
        }
        
        .satisfaction-stats {
          gap: 40px;
          padding: 30px 20px;
        }
        
        .stat-value {
          font-size: 36px;
        }
        
        .percent, .plus, .unit {
          font-size: 20px;
        }
        
        .satisfaction-header h2 {
          font-size: 30px;
        }
      }
    </style>
    
    <div class="satisfaction-container">
      <div class="satisfaction-header">
        <h2 id="section-title"></h2>
        <p class="subtitle" id="section-subtitle"></p>
      </div>
      
      <div class="satisfaction-grid">
        <slot></slot>
      </div>
      
      <div class="satisfaction-footer">
        <div class="satisfaction-stats">
          <div class="stat-item">
            <div class="stat-value">98<span class="percent">%</span></div>
            <div class="stat-label">Customer Satisfaction</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">10<span class="plus">+</span></div>
            <div class="stat-label">Years Average Lifespan</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">44<span class="unit">lbs</span></div>
            <div class="stat-label">Maximum Weight Capacity</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">24<span class="unit">h</span></div>
            <div class="stat-label">Customer Service Response</div>
          </div>
        </div>
      </div>
    </div>
      `;
      
      // 获取元素
      this.containerElement = this.shadowRoot.querySelector('.satisfaction-container');
      this.titleElement = this.shadowRoot.querySelector('#section-title');
      this.subtitleElement = this.shadowRoot.querySelector('#section-subtitle');
      this.statsElement = this.shadowRoot.querySelector('.satisfaction-stats');
    }
    
    connectedCallback() {
      // 设置主题色
      const mainColor = this.getAttribute('main-color') || '#FFA200';
      const mainColorLight = this.getAttribute('main-color-light') || '#FFB733';
      const mainColorDark = this.getAttribute('main-color-dark') || '#E69200';
      
      this.containerElement.style.setProperty('--main-color', mainColor);
      this.containerElement.style.setProperty('--main-color-light', mainColorLight);
      this.containerElement.style.setProperty('--main-color-dark', mainColorDark);
      
      // 设置标题和副标题
      const title = this.getAttribute('title');
      if (title) {
        this.titleElement.textContent = title;
      } else {
        this.shadowRoot.querySelector('.satisfaction-header').style.display = 'none';
      }
      
      const subtitle = this.getAttribute('subtitle');
      if (subtitle) {
        this.subtitleElement.textContent = subtitle;
      } else if (!title) {
        this.shadowRoot.querySelector('.satisfaction-header').style.display = 'none';
      }
      
      // 设置统计数据
      const stats = this.getAttribute('stats');
      if (stats) {
        try {
          const statsArray = JSON.parse(stats);
          this.statsElement.innerHTML = '';
          
          statsArray.forEach(stat => {
            const statItem = document.createElement('div');
            statItem.className = 'stat-item';
            
            const statValue = document.createElement('div');
            statValue.className = 'stat-value';
            statValue.textContent = stat.value;
            
            if (stat.suffix) {
              const suffix = document.createElement('span');
              suffix.className = stat.suffixClass || 'unit';
              suffix.textContent = stat.suffix;
              statValue.appendChild(suffix);
            }
            
            const statLabel = document.createElement('div');
            statLabel.className = 'stat-label';
            statLabel.textContent = stat.label;
            
            statItem.appendChild(statValue);
            statItem.appendChild(statLabel);
            this.statsElement.appendChild(statItem);
          });
        } catch (e) {
          console.error('Invalid stats JSON format', e);
        }
      }
      
      // 传递颜色属性到所有卡片
      this.updateCardColors();
    }
    
    updateCardColors() {
      // 等待DOM更新完成
      setTimeout(() => {
        const mainColor = this.getAttribute('main-color') || '#FFA200';
        const mainColorDark = this.getAttribute('main-color-dark') || '#E69200';
        
        // 获取所有卡片并设置颜色
        const cards = this.querySelectorAll('feature-card');
        cards.forEach(card => {
          card.setAttribute('main-color', mainColor);
          card.setAttribute('main-color-dark', mainColorDark);
        });
      }, 0);
    }
    
    // 监听属性变化
    static get observedAttributes() {
      return ['main-color', 'main-color-light', 'main-color-dark', 'title', 'subtitle', 'stats'];
    }
    
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      
      switch (name) {
        case 'main-color':
          this.containerElement.style.setProperty('--main-color', newValue || '#FFA200');
          this.updateCardColors();
          break;
        case 'main-color-light':
          this.containerElement.style.setProperty('--main-color-light', newValue || '#FFB733');
          break;
        case 'main-color-dark':
          this.containerElement.style.setProperty('--main-color-dark', newValue || '#E69200');
          this.updateCardColors();
          break;
        case 'title':
          if (newValue) {
            this.titleElement.textContent = newValue;
            this.shadowRoot.querySelector('.satisfaction-header').style.display = '';
          } else {
            this.shadowRoot.querySelector('.satisfaction-header').style.display = 'none';
          }
          break;
        case 'subtitle':
          this.subtitleElement.textContent = newValue || '';
          break;
        case 'stats':
          if (newValue) {
            try {
              const statsArray = JSON.parse(newValue);
              this.statsElement.innerHTML = '';
              
              statsArray.forEach(stat => {
                const statItem = document.createElement('div');
                statItem.className = 'stat-item';
                
                const statValue = document.createElement('div');
                statValue.className = 'stat-value';
                statValue.textContent = stat.value;
                
                if (stat.suffix) {
                  const suffix = document.createElement('span');
                  suffix.className = stat.suffixClass || 'unit';
                  suffix.textContent = stat.suffix;
                  statValue.appendChild(suffix);
                }
                
                const statLabel = document.createElement('div');
                statLabel.className = 'stat-label';
                statLabel.textContent = stat.label;
                
                statItem.appendChild(statValue);
                statItem.appendChild(statLabel);
                this.statsElement.appendChild(statItem);
              });
            } catch (e) {
              console.error('Invalid stats JSON format', e);
            }
          }
          break;
      }
    }
  }
  
  // 注册自定义元素
  if (!customElements.get('landing-feature-card')) {
    customElements.define('landing-feature-card', LandingFeatureCard);
  }
  
  if (!customElements.get('landing-feature-section')) {
    customElements.define('landing-feature-section', LandingFeatureSection);
  }





class OldGalleryCard extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }
  
  connectedCallback() {
    // 获取属性
    const mediaType = this.getAttribute('media-type');
    const media = this.getAttribute('media');
    const previewImage = this.getAttribute('preview-image');
    
    // 获取标签数据（支持多种格式）
    const tagsAttr = this.getAttribute('tags');
    let tags = [];
    
    if (tagsAttr) {
      // 尝试解析为JSON（适用于复杂标签对象）
      try {
        const parsedTags = JSON.parse(tagsAttr);
        if (Array.isArray(parsedTags)) {
          tags = parsedTags;
        }
      } catch (e) {
        // 如果不是有效的JSON，则按逗号分隔的字符串处理
        tags = tagsAttr.split(',').map(tag => tag.trim()).filter(tag => tag);
      }
    }
    
    // 构建基础结构
    this.shadowRoot.innerHTML = `
      <style>
        /* 基础样式 */
        :host {
          display: block;
        }
        
        .gallery-card {
          display: flex;
          flex-direction: column;
          max-width: 100%;
          position: relative;
        }
        
        .card-media {
          width: 100%;
          height: auto;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 9/16;
        }
        
        .card-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        
        .card-media img:hover {
          transform: scale(1.03);
        }
        
        .card-media video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .youtube-container {
          width: 100%;
          height: 100%;
          position: relative;
        }
        
        .youtube-container iframe {
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          border: none;
        }
        
        /* 视频加载指示器 */
        .video-loading {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 14px;
          background: rgba(0, 0, 0, 0.5);
          padding: 8px 16px;
          border-radius: 4px;
        }
        
        /* 播放按钮悬停效果 */
        .play-button {
          transition: background-color 0.3s ease, transform 0.2s ease;
        }
        
        .youtube-preview:hover .play-button,
        .youtube-placeholder:hover .play-button {
          background-color: rgba(255, 0, 0, 0.9) !important;
          transform: translate(-50%, -50%) scale(1.1);
        }
        
        /* 标签样式 */
        .tags-overlay {
          position: absolute;
          bottom: 12px;
          left: 12px;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          z-index: 5;
        }
        
        .tag {
          background-color: rgba(0, 0, 0, 0.6);
          color: white;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          backdrop-filter: blur(4px);
          transition: transform 0.2s ease, background-color 0.2s ease;
        }
        
        .tag:hover {
          transform: translateY(-2px);
          background-color: rgba(0, 0, 0, 0.8);
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .tag {
            font-size: 11px;
            padding: 3px 8px;
          }
          
          .tags-overlay {
            bottom: 10px;
            left: 10px;
            gap: 5px;
          }
          
          .card-media {
            aspect-ratio: 9/16;
          }
        }
        
        /* 小屏幕手机适配 */
        @media (max-width: 480px) {
          .tag {
            font-size: 10px;
            padding: 2px 6px;
          }
          
          .tags-overlay {
            bottom: 8px;
            left: 8px;
            gap: 4px;
          }
        }
      </style>
      
      <div class="gallery-card">
        <div class="card-media">
          ${this.renderMedia(mediaType, media, previewImage)}
          
          <!-- 标签覆盖层 -->
          <div class="tags-overlay">
            ${this.renderTags(tags)}
          </div>
        </div>
        
        <!-- 保留自定义插槽，以便用户可以添加额外内容 -->
        <slot></slot>
      </div>
    `;
    
    // 如果是视频，初始化视频功能
    if (mediaType === 'video') {
      this.setupVideoObserver();
      this.initVideo(media, previewImage);
    } else if (mediaType === 'youtube') {
      // 初始化YouTube视频
      this.setupYouTubeVideo();
    }
  }
  
  renderTags(tags) {
    if (!tags || !Array.isArray(tags) || tags.length === 0) {
      return '';
    }
    
    return tags.map(tag => {
      // 支持简单字符串标签或对象标签（带颜色）
      if (typeof tag === 'string') {
        return `<div class="tag">${tag}</div>`;
      } else if (typeof tag === 'object' && tag.text) {
        const bgColor = tag.color ? `background-color: ${tag.color};` : '';
        const textColor = tag.textColor ? `color: ${tag.textColor};` : '';
        return `<div class="tag" style="${bgColor} ${textColor}">${tag.text}</div>`;
      }
      return '';
    }).join('');
  }
  
  renderMedia(mediaType, media, previewImage) {
    if (!media) return '';
    
    if (mediaType === 'image') {
      return `<img src="${this.ensureProtocol(media)}" alt="">`;
    } else if (mediaType === 'video') {
      // 检查是否是HLS格式
      const isHLS = media.includes('.m3u8');
      
      // 创建视频容器
      let videoHtml = `
        <div class="video-loading">Loading...</div>
        <video loop muted playsinline ${previewImage ? `poster="${this.ensureProtocol(previewImage)}"` : ''}>
          <source src="${this.ensureProtocol(media)}" type="${isHLS ? 'application/x-mpegURL' : 'video/mp4'}">
        </video>
      `;
      
      return videoHtml;
    } else if (mediaType === 'youtube') {
      // 提取YouTube视频ID
      const videoId = this.getYouTubeId(media);
      
      if (videoId) {
        // 判断是否为shorts视频
        const isShorts = media.includes('/shorts/');
        
        // 创建自定义的YouTube播放器容器
        return `
          <div class="youtube-container" data-video-id="${videoId}" data-is-shorts="${isShorts}">
            ${previewImage ? 
              `<div class="youtube-preview" style="position:absolute; top:0; left:0; width:100%; height:100%; z-index:2; cursor:pointer;">
                <img src="${this.ensureProtocol(previewImage)}" alt="" style="width:100%; height:100%; object-fit:cover;">
                <div class="play-button" style="position:absolute; top:50%; left:50%; transform:translate(-50%, -50%); width:68px; height:48px; background-color:rgba(0,0,0,0.7); border-radius:10px; display:flex; justify-content:center; align-items:center;">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5V19L19 12L8 5Z" fill="white"/>
                  </svg>
                </div>
              </div>` : 
              `<div class="youtube-placeholder" style="position:absolute; top:0; left:0; width:100%; height:100%; background:#000; display:flex; justify-content:center; align-items:center; cursor:pointer; z-index:2;">
                <div class="play-button" style="width:68px; height:48px; background-color:rgba(255,0,0,0.8); border-radius:10px; display:flex; justify-content:center; align-items:center;">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 5V19L19 12L8 5Z" fill="white"/>
                  </svg>
                </div>
              </div>`
            }
          </div>
        `;
      }
    }
    
    return '';
  }
  
  setupYouTubeVideo() {
    const container = this.shadowRoot.querySelector('.youtube-container');
    if (!container) return;
    
    const videoId = container.getAttribute('data-video-id');
    const isShorts = container.getAttribute('data-is-shorts') === 'true';
    
    // 添加点击事件处理
    const previewElement = container.querySelector('.youtube-preview') || 
                           container.querySelector('.youtube-placeholder');
    
    if (previewElement) {
      previewElement.addEventListener('click', () => {
        // 创建iframe元素
        const iframe = document.createElement('iframe');
        
        // 设置适当的嵌入URL
        if (isShorts) {
          // 对于shorts视频，使用特殊的嵌入参数
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&widgetid=1&controls=0`;
        } else {
          // 标准视频嵌入
          iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
        }
        
        // 设置iframe属性
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.style.position = 'absolute';
        iframe.style.top = '0';
        iframe.style.left = '0';
        iframe.style.zIndex = '1';
        iframe.style.border = 'none';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        
        // 添加iframe到容器
        container.appendChild(iframe);
        
        // 移除预览元素
        previewElement.remove();
      });
    }
  }
  
  setupVideoObserver() {
    // 创建Intersection Observer来控制视频播放
    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          // 视频可见时播放
          video.play().catch(e => console.log('Video play error:', e));
        } else {
          // 不可见时暂停
          video.pause();
        }
      });
    }, {
      threshold: 0.1 // 10% 的视频可见时触发
    });
  }
  
  initVideo(mediaUrl, previewImage) {
    // 获取视频元素
    const video = this.shadowRoot.querySelector('video');
    if (!video) return;
    
    // 检查是否是HLS格式
    const isHLS = mediaUrl.includes('.m3u8');
    
    // 设置预览图（如果有）
    if (previewImage && !video.hasAttribute('poster')) {
      video.setAttribute('poster', this.ensureProtocol(previewImage));
    }
    
    if (isHLS) {
      // 加载HLS.js并初始化视频
      this.loadHLSScript().then(() => {
        // 移除加载指示器
        const loadingIndicator = this.shadowRoot.querySelector('.video-loading');
        if (loadingIndicator) {
          loadingIndicator.remove();
        }
        
        if (window.Hls && window.Hls.isSupported()) {
          const hls = new window.Hls({
            maxBufferLength: 30,
            maxMaxBufferLength: 60
          });
          hls.loadSource(this.ensureProtocol(mediaUrl));
          hls.attachMedia(video);
          hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
            // 视频准备好后，添加到观察器
            video.muted = true;
            this.videoObserver.observe(video);
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari原生支持HLS
          video.src = this.ensureProtocol(mediaUrl);
          video.muted = true;
          this.videoObserver.observe(video);
        }
      });
    } else {
      // 标准视频格式，直接添加到观察器
      this.videoObserver.observe(video);
      
      // 移除加载指示器
      const loadingIndicator = this.shadowRoot.querySelector('.video-loading');
      if (loadingIndicator) {
        loadingIndicator.remove();
      }
      
      // 添加加载事件
      video.addEventListener('loadeddata', () => {
        console.log('视频已加载:', mediaUrl);
        if (video.paused) {
          video.play().catch(e => {
            console.log('自动播放失败，尝试静音播放:', e);
            video.muted = true;
            video.play().catch(e2 => console.log('静音播放也失败:', e2));
          });
        }
      });
      
      // 添加错误处理
      video.addEventListener('error', (e) => {
        console.error('视频加载失败:', e, video.error);
        // 如果有预览图，在视频加载失败时显示预览图
        if (previewImage) {
          const parent = video.parentNode;
          if (parent) {
            const errorImg = document.createElement('img');
            errorImg.src = this.ensureProtocol(previewImage);
            errorImg.alt = '视频无法播放';
            parent.appendChild(errorImg);
            video.style.display = 'none';
          }
        } else {
          // 没有预览图时显示默认错误图片
          const parent = video.parentNode;
          if (parent) {
            const errorImg = document.createElement('img');
            errorImg.src = 'https://via.placeholder.com/400x400?text=Video+Unavailable';
            errorImg.alt = '视频无法播放';
            parent.appendChild(errorImg);
            video.style.display = 'none';
          }
        }
      });
    }
  }
  
  // 确保URL有协议
  ensureProtocol(url) {
    if (!url) return '';
    
    if (url.startsWith('//')) {
      return `https:${url}`;
    }
    
    // 尝试修复不完整的URL
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/')) {
      return `https://${url}`;
    }
    
    return url;
  }
  
  // 加载HLS.js脚本
  loadHLSScript() {
    return new Promise((resolve) => {
      if (window.Hls) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }
  
  // 从YouTube URL提取视频ID，支持更多格式
  getYouTubeId(url) {
    if (!url) return null;
    
    try {
      // 处理短链接 (youtu.be)
      if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理标准链接 (youtube.com/watch?v=)
      if (url.includes('youtube.com/watch')) {
        // 确保URL有协议，以便可以创建URL对象
        const fullUrl = this.ensureProtocol(url);
        try {
          const urlObj = new URL(fullUrl);
          return urlObj.searchParams.get('v') || null;
        } catch (e) {
          console.error('解析YouTube URL失败:', e);
        }
      }
      
      // 处理嵌入链接 (youtube.com/embed/)
      if (url.includes('/embed/')) {
        const id = url.split('/embed/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理短视频链接 (youtube.com/shorts/)
      if (url.includes('/shorts/')) {
        const id = url.split('/shorts/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理直接带有视频ID的链接
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    } catch (error) {
      console.error('获取YouTube ID时出错:', error);
      return null;
    }
  }
  
  disconnectedCallback() {
    // 清理资源
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
  }
}

customElements.define('gallery-card-o', OldGalleryCard);
















class GalleryCardSlider extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.currentPage = 0;
  }
  
  connectedCallback() {
    // 构建基础结构
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          overflow: hidden;
          position: relative;
          padding: 10px 0;
          max-width: 1200px; /* 设置最大宽度 */
          margin: 0 auto; /* 居中显示 */
        }
        
        .slider-container {
          width: 100%;
          overflow: hidden;
        }
        
        .slider-track {
          display: flex;
          transition: transform 0.3s ease;
        }
        
        /* 卡片样式 */
        ::slotted(gallery-card) {
          flex-shrink: 0;
          width: 100%; /* 移动端占满整个宽度 */
          display: flex;
          justify-content: center;
        }
        
        /* 桌面端样式 */
        @media (min-width: 768px) {
          ::slotted(gallery-card) {
            width: calc((100% - 40px) / 3); /* 三个卡片均分宽度，减去两个间隙的宽度 */
            margin-right: 0;
            margin-left: 0;
          }
          
          /* 每组中的中间卡片有左右间距 */
          ::slotted(gallery-card:nth-child(3n-1)) {
            margin-left: 20px;
            margin-right: 20px;
          }
        }
        
        /* 导航按钮 */
        .nav-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.8);
          border: none;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
          cursor: pointer;
          z-index: 10;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .nav-button.prev {
          left: 10px;
        }
        
        .nav-button.next {
          right: 10px;
        }
        
        .nav-button svg {
          width: 20px;
          height: 20px;
          fill: #333;
        }
        
        /* 分页指示器 */
        .pagination {
          display: flex;
          justify-content: center;
          margin-top: 15px;
          gap: 8px;
        }
        
        .pagination-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #ccc;
          cursor: pointer;
        }
        
        .pagination-dot.active {
          background-color: #333;
        }
      </style>
      
      <div class="slider-container">
        <div class="slider-track">
          <slot></slot>
        </div>
      </div>
      
      <button class="nav-button prev">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
        </svg>
      </button>
      
      <button class="nav-button next">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </button>
      
      <div class="pagination"></div>
    `;
    
    // 初始化引用
    this.sliderContainer = this.shadowRoot.querySelector('.slider-container');
    this.sliderTrack = this.shadowRoot.querySelector('.slider-track');
    this.pagination = this.shadowRoot.querySelector('.pagination');
    
    // 获取 slot 元素
    const slot = this.shadowRoot.querySelector('slot');
    
    // 监听 slotchange 事件
    slot.addEventListener('slotchange', () => {
      this.setupCards();
    });
    
    // 设置导航按钮
    this.setupNavigation();
    
    // 监听窗口大小变化
    window.addEventListener('resize', () => {
      this.updatePagination();
      this.scrollToPage(this.currentPage);
    });
    
    // 初始化卡片
    setTimeout(() => {
      this.setupCards();
    }, 100);
  }
  
  setupCards() {
    const cards = this.getGalleryCards();
    if (cards.length === 0) return;
    
    // 为卡片设置索引属性，用于CSS选择器
    cards.forEach((card, index) => {
      card.setAttribute('data-index', index);
    });
    
    // 更新分页指示器
    this.updatePagination();
    
    // 滚动到第一页
    this.scrollToPage(0);
  }
  
  updatePagination() {
    // 清空分页指示器
    this.pagination.innerHTML = '';
    
    const cards = this.getGalleryCards();
    
    // 计算页数
    const cardsPerPage = this.getCardsPerPage();
    const pageCount = Math.ceil(cards.length / cardsPerPage);
    
    // 创建分页指示器
    for (let i = 0; i < pageCount; i++) {
      const dot = document.createElement('div');
      dot.className = 'pagination-dot';
      if (i === this.currentPage) dot.classList.add('active');
      
      dot.addEventListener('click', () => {
        this.scrollToPage(i);
      });
      
      this.pagination.appendChild(dot);
    }
  }
  
  setupNavigation() {
    const prevButton = this.shadowRoot.querySelector('.nav-button.prev');
    const nextButton = this.shadowRoot.querySelector('.nav-button.next');
    
    prevButton.addEventListener('click', () => {
      this.scrollToPage(Math.max(0, this.currentPage - 1));
    });
    
    nextButton.addEventListener('click', () => {
      const cards = this.getGalleryCards();
      const cardsPerPage = this.getCardsPerPage();
      const pageCount = Math.ceil(cards.length / cardsPerPage);
      this.scrollToPage(Math.min(pageCount - 1, this.currentPage + 1));
    });
  }
  
  getGalleryCards() {
    const slot = this.shadowRoot.querySelector('slot');
    const assignedNodes = slot.assignedNodes();
    return assignedNodes.filter(node => 
      node.nodeType === Node.ELEMENT_NODE && 
      node.tagName && node.tagName.toLowerCase() === 'gallery-card'
    );
  }
  
  getCardsPerPage() {
    return window.innerWidth >= 768 ? 3 : 1;
  }
  
  scrollToPage(pageIndex) {
    const cards = this.getGalleryCards();
    const cardsPerPage = this.getCardsPerPage();
    
    // 确保页索引有效
    const pageCount = Math.ceil(cards.length / cardsPerPage);
    pageIndex = Math.max(0, Math.min(pageIndex, pageCount - 1));
    
    // 计算要滚动到的第一个卡片的索引
    const firstCardIndex = pageIndex * cardsPerPage;
    
    // 如果第一个卡片不存在，不进行滚动
    if (!cards[firstCardIndex]) return;
    
    // 滚动到指定卡片
    if (window.innerWidth >= 768) {
      // 桌面端：需要精确计算每个卡片的位置
      let scrollAmount = 0;
      for (let i = 0; i < firstCardIndex; i++) {
        const cardWidth = cards[i].getBoundingClientRect().width;
        scrollAmount += cardWidth;
        
        // 考虑卡片之间的间距
        // 如果当前卡片是每组的第一个或第二个，需要考虑后面的间距
        if (i % 3 === 0 || i % 3 === 1) {
          scrollAmount += 20; // 添加20px的间距
        }
      }
      this.sliderTrack.style.transform = `translateX(-${scrollAmount}px)`;
    } else {
      // 移动端：直接滚动到卡片位置
      const scrollAmount = firstCardIndex * cards[0].getBoundingClientRect().width;
      this.sliderTrack.style.transform = `translateX(-${scrollAmount}px)`;
    }
    
    // 更新当前页
    this.currentPage = pageIndex;
    
    // 更新分页指示器
    const dots = this.pagination.querySelectorAll('.pagination-dot');
    dots.forEach((dot, index) => {
      if (index === pageIndex) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }
}

customElements.define('gallery-card-slider', GalleryCardSlider);
















class NewBenefitItem extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    // 保存组件唯一标识，用于区分事件来源
    this.componentId = Math.random().toString(36).substring(2, 15);
    
    // 绑定方法到实例，以便正确解绑事件
    this.handleVideoPlayStarted = this.handleVideoPlayStarted.bind(this);
    
    // 标记是否是用户交互触发的播放
    this.userInitiatedPlay = false;
  }
  
  connectedCallback() {
    // 获取属性
    const index = this.getAttribute('index');
    const title = this.getAttribute('title');
    const mediaType = this.getAttribute('media-type');
    const media = this.getAttribute('media');
    const previewImage = this.getAttribute('preview-image');
    const autoplay = this.hasAttribute('autoplay');
    const loop = this.hasAttribute('loop');
    
    // 构建基础结构
    this.shadowRoot.innerHTML = `
      <style>
        /* 基础样式 */
        :host {
          display: block;
        }
        
        .benefit-item {
          display: flex;
          flex-direction: column;
          padding: 10px;
          max-width: 100%;
        }
        
        .benefit-media {
          width: 100%;
          height: auto;
          border-radius: 8px;
          margin-bottom: 25px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 1/1;
        }
        
        .benefit-media img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        
        .benefit-media img:hover {
          transform: scale(1.03);
        }
        
        .video-container {
          position: relative;
          width: 100%;
          height: 100%;
        }
        
        .benefit-media video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .youtube-container {
          width: 100%;
          height: 100%;
          position: relative;
        }
        
        .youtube-container iframe {
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 8px;
        }
        
        .benefit-number {
          font-size: 22px;
          font-weight: 600;
          margin-right: 4px;
          color: var(--theme-color, #FFA200);
        }
        
        .benefit-title-container {
          font-size: 22px;
          font-weight: 600;
          margin-bottom: 15px;
          display: flex;
          align-items: flex-start;
        }
        
        .benefit-title-text {
          flex: 1;
          line-height: 1.3;
          color: var(--theme-color, #FFA200);
          display: flex;
          justify-content: flex-start;
          transform: translateY(2px);
        }
        
        .benefit-description {
          font-size: 16px;
          line-height: 1.6;
          color: #333;
          margin-bottom: 15px;
        }
        
        /* 视频控制按钮 - 右下角，默认可见 */
        .play-pause-btn {
          position: absolute;
          bottom: 15px;
          right: 15px;
          background: rgba(0, 0, 0, 0.5);
          border: none;
          color: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.8; /* 默认可见但略微透明 */
          transition: opacity 0.3s ease, background-color 0.2s ease;
        }
        
        .video-container:hover .play-pause-btn {
          opacity: 1; /* 鼠标悬停时完全不透明 */
        }
        
        .play-pause-btn:hover {
          background: rgba(0, 0, 0, 0.7);
        }
        
        .play-pause-btn svg {
          width: 24px;
          height: 24px;
        }
        
        /* 循环指示器 */
        .loop-indicator {
          position: absolute;
          bottom: 15px;
          left: 15px;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 12px;
          padding: 4px 8px;
          color: white;
          font-size: 12px;
          opacity: 0.8;
          display: flex;
          align-items: center;
        }
        
        .loop-indicator svg {
          width: 14px;
          height: 14px;
          margin-right: 4px;
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .benefit-title-container {
            font-size: 20px;
          }
          
          .benefit-number {
            font-size: 20px;
          }
          
          .benefit-description {
            font-size: 15px;
          }
          
          .benefit-media {
            aspect-ratio: 1/1;
            margin-bottom: 20px;
          }
          
          .play-pause-btn {
            width: 36px;
            height: 36px;
            bottom: 12px;
            right: 12px;
          }
          
          .play-pause-btn svg {
            width: 20px;
            height: 20px;
          }
        }
        
        /* 小屏幕手机适配 */
        @media (max-width: 480px) {
          .benefit-title-container {
            font-size: 18px;
          }
          
          .benefit-number {
            font-size: 18px;
          }
          
          .benefit-description {
            font-size: 14px;
          }
          
          .play-pause-btn {
            width: 32px;
            height: 32px;
            bottom: 10px;
            right: 10px;
          }
        }
      </style>
      
      <div class="benefit-item">
        <div class="benefit-media">
          ${this.renderMedia(mediaType, media, previewImage, autoplay, loop)}
        </div>
        
        <div class="benefit-title-container">
          <span class="benefit-number">${index}.</span>
          <span class="benefit-title-text">${title}</span>
        </div>
        
        <div class="benefit-description">
          <slot name="description"></slot>
        </div>
        
        <slot name="points"></slot>
      </div>
    `;
    
    // 存储循环属性和媒体类型
    this._loopEnabled = loop;
    this._mediaType = mediaType;
    
    // 如果是视频，初始化视频控制功能
    if (mediaType === 'video') {
      this.initVideoControls();
    } else if (mediaType === 'youtube') {
      this.initYouTubeControls();
    }
    
    // 添加自定义事件监听器
    this.addCustomEventListeners();
  }
  
  // 添加自定义事件监听器
  addCustomEventListeners() {
    // 监听其他视频播放事件
    document.addEventListener('video-play-started', this.handleVideoPlayStarted);
  }
  
  // 处理视频播放事件
  handleVideoPlayStarted(event) {
    // 确保不是自己发出的事件
    if (event.detail.componentId !== this.componentId) {
      // 仅暂停媒体，不发送新事件
      this.pauseMedia(false);
    }
  }
  
  // 发送视频开始播放事件
  dispatchPlayEvent() {
    // 只在用户交互触发的播放时发送事件
    if (this.userInitiatedPlay) {
      const event = new CustomEvent('video-play-started', {
        bubbles: true,
        composed: true, // 确保事件可以穿越Shadow DOM边界
        detail: {
          componentId: this.componentId,
          timestamp: Date.now()
        }
      });
      this.dispatchEvent(event);
      
      // 重置标记
      this.userInitiatedPlay = false;
    }
  }
  
  // 暂停当前媒体
  // shouldDispatch参数控制是否需要发送事件
  pauseMedia(shouldDispatch = true) {
    if (this._mediaType === 'video') {
      const video = this.shadowRoot.querySelector('video');
      if (video && !video.paused) {
        // 如果需要发送事件，设置标记
        this.userInitiatedPlay = shouldDispatch;
        video.pause();
      }
    } else if (this._mediaType === 'youtube') {
      const iframe = this.shadowRoot.querySelector('iframe.js-youtube');
      if (iframe) {
        try {
          // 向YouTube iframe发送暂停命令
          iframe.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: 'pauseVideo',
            args: ''
          }), '*');
        } catch (e) {
          console.warn('无法暂停YouTube视频:', e);
        }
      }
    }
  }
  
  renderMedia(mediaType, media, previewImage, autoplay, loop) {
    if (!media) return '';
    
    if (mediaType === 'image') {
      return `<img src="${this.ensureProtocol(media)}" alt="">`;
    } else if (mediaType === 'video') {
      // 检查是否是HLS格式
      const isHLS = media.includes('.m3u8');
      
      // 创建视频容器，添加loop属性
      let videoHtml = `
        <div class="video-container">
          <video ${autoplay ? 'autoplay' : ''} ${autoplay ? 'muted' : ''} ${loop ? 'loop' : ''} playsinline ${previewImage ? `poster="${this.ensureProtocol(previewImage)}"` : ''}>
            <source src="${this.ensureProtocol(media)}" type="${isHLS ? 'application/x-mpegURL' : 'video/mp4'}">
          </video>
          <button class="play-pause-btn">
            <svg viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </button>
          ${loop ? `
            <div class="loop-indicator">
              <svg viewBox="0 0 24 24" fill="white">
                <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
              </svg>
              循环播放
            </div>
          ` : ''}
        </div>
      `;
      
      return videoHtml;
    } else if (mediaType === 'youtube') {
      // 提取YouTube视频ID
      const videoId = this.getYouTubeId(media);
      
      if (videoId) {
        // 为YouTube iframe添加loop参数
        const loopParam = loop ? '&loop=1&playlist=' + videoId : '';
        
        return `
          <div class="youtube-container">
            <iframe 
              class="js-youtube"
              src="https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1${autoplay ? '&autoplay=1&mute=1' : ''}${loopParam}" 
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen>
            </iframe>
            ${loop ? `
              <div class="loop-indicator">
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                </svg>
                循环播放
              </div>
            ` : ''}
          </div>
        `;
      }
    }
    
    return '';
  }
  
  initVideoControls() {
    const video = this.shadowRoot.querySelector('video');
    const playPauseBtn = this.shadowRoot.querySelector('.play-pause-btn');
    
    if (!video || !playPauseBtn) return;
    
    // 检查是否是HLS格式
    const source = video.querySelector('source');
    const isHLS = source && source.src.includes('.m3u8');
    
    // 如果是HLS格式，加载HLS.js
    if (isHLS) {
      this.loadHLSScript().then(() => {
        this.setupHLS(video, source.src);
      });
    }
    
    // 更新播放/暂停按钮图标
    const updatePlayPauseIcon = () => {
      if (video.paused) {
        playPauseBtn.innerHTML = `
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        `;
      } else {
        playPauseBtn.innerHTML = `
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
          </svg>
        `;
      }
    };
    
    // 播放/暂停按钮点击事件
    playPauseBtn.addEventListener('click', () => {
      if (video.paused) {
        // 标记这是用户交互触发的播放
        this.userInitiatedPlay = true;
        video.play();
        // 发送播放事件通知其他组件
        this.dispatchPlayEvent();
      } else {
        video.pause();
      }
      updatePlayPauseIcon();
    });
    
    // 视频播放状态变化事件
    video.addEventListener('play', updatePlayPauseIcon);
    video.addEventListener('pause', updatePlayPauseIcon);
    video.addEventListener('ended', () => {
      // 如果没有设置循环，视频结束时更新图标
      if (!this._loopEnabled) {
        updatePlayPauseIcon();
      }
    });
    
    // 初始化按钮状态
    updatePlayPauseIcon();
  }
  
  initYouTubeControls() {
    // 为YouTube iframe添加事件监听
    const iframe = this.shadowRoot.querySelector('iframe.js-youtube');
    if (!iframe) return;
    
    // 确保iframe加载完成后再添加事件监听
    iframe.addEventListener('load', () => {
      // 监听来自iframe的消息
      this.youtubeMessageHandler = (event) => {
        try {
          // 尝试解析消息数据
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          
          // 检查是否是来自当前iframe的YouTube事件
          if (data.event === 'onStateChange' && event.source === iframe.contentWindow) {
            // 状态1表示正在播放
            if (data.info === 1 && this.userInitiatedPlay) {
              // 发送播放事件通知其他组件
              this.dispatchPlayEvent();
            }
          }
        } catch (e) {
          // 忽略无法解析的消息
        }
      };
      
      window.addEventListener('message', this.youtubeMessageHandler);
      
      // 当iframe被点击时
      iframe.addEventListener('click', () => {
        // 标记这是用户交互触发的播放
        this.userInitiatedPlay = true;
      });
    });
  }
  
  setupHLS(video, src) {
    if (window.Hls && window.Hls.isSupported()) {
      const hls = new window.Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari原生支持HLS
      video.src = src;
    }
  }
  
  // 确保URL有协议
  ensureProtocol(url) {
    if (!url) return '';
    
    if (url.startsWith('//')) {
      return `https:${url}`;
    }
    
    // 尝试修复不完整的URL
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/')) {
      return `https://${url}`;
    }
    
    return url;
  }
  
  // 加载HLS.js脚本
  loadHLSScript() {
    return new Promise((resolve) => {
      if (window.Hls) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }
  
  // 从YouTube URL提取视频ID
  getYouTubeId(url) {
    if (!url) return null;
    
    try {
      // 处理短链接 (youtu.be)
      if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理标准链接 (youtube.com/watch?v=)
      if (url.includes('youtube.com/watch')) {
        const fullUrl = this.ensureProtocol(url);
        try {
          const urlObj = new URL(fullUrl);
          return urlObj.searchParams.get('v') || null;
        } catch (e) {
          console.error('解析YouTube URL失败:', e);
        }
      }
      
      // 处理嵌入链接 (youtube.com/embed/)
      if (url.includes('/embed/')) {
        const id = url.split('/embed/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理短视频链接 (youtube.com/shorts/)
      if (url.includes('/shorts/')) {
        const id = url.split('/shorts/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理直接带有视频ID的链接
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    } catch (error) {
      console.error('获取YouTube ID时出错:', error);
      return null;
    }
  }
  
  // 组件断开连接时清理事件监听器
  disconnectedCallback() {
    // 移除事件监听器，避免内存泄漏
    document.removeEventListener('video-play-started', this.handleVideoPlayStarted);
    
    // 清理YouTube消息监听器
    if (this.youtubeMessageHandler) {
      window.removeEventListener('message', this.youtubeMessageHandler);
    }
  }
}

customElements.define('benefit-item-n', NewBenefitItem);














/**
 * GalleryCard - 重构版本
 * 使用自定义媒体元素处理多媒体内容
 */
class GalleryCard extends HTMLElement {
constructor() {
  super();
  this.attachShadow({ mode: 'open' });
}

connectedCallback() {
  // 获取属性
  const mediaType = this.getAttribute('media-type') || 'image';
  const media = this.getAttribute('media');
  const previewImage = this.getAttribute('preview-image');
  
  // 获取标签数据（支持多种格式）
  const tagsAttr = this.getAttribute('tags');
  let tags = [];
  
  if (tagsAttr) {
    // 尝试解析为JSON（适用于复杂标签对象）
    try {
      const parsedTags = JSON.parse(tagsAttr);
      if (Array.isArray(parsedTags)) {
        tags = parsedTags;
      }
    } catch (e) {
      // 如果不是有效的JSON，则按逗号分隔的字符串处理
      tags = tagsAttr.split(',').map(tag => tag.trim()).filter(tag => tag);
    }
  }
  
  // 创建媒体元素
  let mediaElementTag;
  switch (mediaType) {
    case 'video':
      mediaElementTag = 'lazy-video-element';
      break;
    case 'youtube':
      mediaElementTag = 'youtube-element';
      break;
    default:
      mediaElementTag = 'image-element';
  }
  
  // 构建基础结构
  this.shadowRoot.innerHTML = `
    <style>
      /* 基础样式 */
      :host {
        display: block;
      }
      
      .gallery-card {
        display: flex;
        flex-direction: column;
        max-width: 100%;
        position: relative;
      }
      
      .card-media {
        width: 100%;
        height: auto;
        border-radius: 8px;
        overflow: hidden;
        position: relative;
        aspect-ratio: 9/16;
      }
      
      /* 标签样式 */
      .tags-overlay {
        position: absolute;
        bottom: 12px;
        left: 12px;
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        z-index: 5;
      }
      
      .tag {
        background-color: rgba(0, 0, 0, 0.6);
        color: white;
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        backdrop-filter: blur(4px);
        transition: transform 0.2s ease, background-color 0.2s ease;
      }
      
      .tag:hover {
        transform: translateY(-2px);
        background-color: rgba(0, 0, 0, 0.8);
      }
      
      /* 响应式设计 */
      @media (max-width: 768px) {
        .tag {
          font-size: 11px;
          padding: 3px 8px;
        }
        
        .tags-overlay {
          bottom: 10px;
          left: 10px;
          gap: 5px;
        }
        
        .card-media {
          aspect-ratio: 9/16;
        }
      }
      
      /* 小屏幕手机适配 */
      @media (max-width: 480px) {
        .tag {
          font-size: 10px;
          padding: 2px 6px;
        }
        
        .tags-overlay {
          bottom: 8px;
          left: 8px;
          gap: 4px;
        }
      }
    </style>
    
    <div class="gallery-card">
      <div class="card-media">
        <${mediaElementTag} 
          id="media-element" 
          src="${media}"
          ${previewImage ? `poster="${previewImage}"` : ''}
          ${this.hasAttribute('loop') ? 'loop' : ''}
          ${this.hasAttribute('autoplay') ? 'autoplay' : ''}
          ${this.hasAttribute('muted') ? 'muted' : ''}
          control-position="center"
        ></${mediaElementTag}>
        
        <!-- 标签覆盖层 -->
        <div class="tags-overlay">
          ${this.renderTags(tags)}
        </div>
      </div>
      
      <!-- 保留自定义插槽，以便用户可以添加额外内容 -->
      <slot></slot>
    </div>
  `;
}

renderTags(tags) {
  if (!tags || !Array.isArray(tags) || tags.length === 0) {
    return '';
  }
  
  return tags.map(tag => {
    // 支持简单字符串标签或对象标签（带颜色）
    if (typeof tag === 'string') {
      return `<div class="tag">${tag}</div>`;
    } else if (typeof tag === 'object' && tag.text) {
      const bgColor = tag.color ? `background-color: ${tag.color};` : '';
      const textColor = tag.textColor ? `color: ${tag.textColor};` : '';
      return `<div class="tag" style="${bgColor} ${textColor}">${tag.text}</div>`;
    }
    return '';
  }).join('');
}

// 确保URL有协议 - 保留这个工具方法以防组件内部需要使用
ensureProtocol(url) {
  if (!url) return '';
  
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  
  // 尝试修复不完整的URL
  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/')) {
    return `https://${url}`;
  }
  
  return url;
}
}

customElements.define('gallery-card', GalleryCard);


















/**
 * BaseMediaElement - 基础媒体元素，专注于媒体渲染
 */
class BaseMediaElement extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    // 保存组件唯一标识，用于区分事件来源
    this.componentId = Math.random().toString(36).substring(2, 15);
    
    // 绑定方法到实例，以便正确解绑事件
    this.handleVideoPlayStarted = this.handleVideoPlayStarted.bind(this);
    
    // 标记是否是用户交互触发的播放
    this.userInitiatedPlay = false;
  }
  
  connectedCallback() {
    const loop = this.hasAttribute('loop');
    
    // 构建基础结构 - 只包含媒体渲染，不包含业务元素
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          height: 100%;
        }
        
        .media-container {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
        }
        
        /* 循环指示器 */
        .loop-indicator {
          position: absolute;
          bottom: 15px;
          left: 15px;
          background: rgba(0, 0, 0, 0.5);
          border-radius: 12px;
          padding: 4px 8px;
          color: white;
          font-size: 12px;
          opacity: 0.8;
          display: flex;
          align-items: center;
          z-index: 2;
        }
        
        .loop-indicator svg {
          width: 14px;
          height: 14px;
          margin-right: 4px;
        }
        
        ${this.getAdditionalStyles()}
      </style>
      
      <div class="media-container">
        ${this.renderMedia()}
      </div>
    `;
    
    // 存储循环属性
    this._loopEnabled = loop;
    
    // 初始化媒体控制功能
    this.initMediaControls();
    
    // 添加自定义事件监听器
    this.addCustomEventListeners();
  }
  
  // 子类需要实现的方法
  getAdditionalStyles() {
    return '';
  }
  
  renderMedia() {
    return '';
  }
  
  initMediaControls() {
    // 子类实现具体逻辑
  }
  
  // 添加自定义事件监听器
  addCustomEventListeners() {
    // 监听其他视频播放事件
    document.addEventListener('video-play-started', this.handleVideoPlayStarted);
  }
  
  // 处理视频播放事件
  handleVideoPlayStarted(event) {
    // 确保不是自己发出的事件
    if (event.detail.componentId !== this.componentId) {
      // 仅暂停媒体，不发送新事件
      this.pauseMedia(false);
    }
  }
  
  // 发送视频开始播放事件
  dispatchPlayEvent() {
    // 只在用户交互触发的播放时发送事件
    if (this.userInitiatedPlay) {
      const event = new CustomEvent('video-play-started', {
        bubbles: true,
        composed: true, // 确保事件可以穿越Shadow DOM边界
        detail: {
          componentId: this.componentId,
          timestamp: Date.now()
        }
      });
      this.dispatchEvent(event);
      
      // 重置标记
      this.userInitiatedPlay = false;
    }
  }
  
  // 暂停当前媒体 - 子类需要实现
  pauseMedia(shouldDispatch = true) {
    // 子类实现具体逻辑
  }
  
  // 确保URL有协议
  ensureProtocol(url) {
    if (!url) return '';
    
    if (url.startsWith('//')) {
      return `https:${url}`;
    }
    
    // 尝试修复不完整的URL
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/')) {
      return `https://${url}`;
    }
    
    return url;
  }
  
  // 组件断开连接时清理事件监听器
  disconnectedCallback() {
    // 移除事件监听器，避免内存泄漏
    document.removeEventListener('video-play-started', this.handleVideoPlayStarted);
  }
}

/**
 * ImageElement - 处理图片的基础组件
 */
class ImageElement extends BaseMediaElement {
  renderMedia() {
    const src = this.getAttribute('src');
    const alt = this.getAttribute('alt') || '';
    
    if (!src) return '';
    
    return `<img src="${this.ensureProtocol(src)}" alt="${alt}">`;
  }
  
  getAdditionalStyles() {
    return `
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
      }
      
      img:hover {
        transform: scale(1.03);
      }
    `;
  }
  
  // 图片不需要暂停
  pauseMedia() {
    // 图片没有暂停逻辑
  }
  
  // 图片不需要初始化控制
  initMediaControls() {
    // 图片不需要控制逻辑
  }
}

/**
 * VideoElement - 处理自托管视频的基础组件
 */
class VideoElement extends BaseMediaElement {
  renderMedia() {
    const src = this.getAttribute('src');
    const poster = this.getAttribute('poster');
    const autoplay = this.hasAttribute('autoplay');
    const loop = this.hasAttribute('loop');
    const muted = this.hasAttribute('muted');
    const controls = this.hasAttribute('controls');
    
    if (!src) return '';
    
    // 检查是否是HLS格式
    const isHLS = src.includes('.m3u8');
    
    // 创建视频容器，添加loop属性
    let videoHtml = `
      <div class="video-wrapper">
        <video 
          ${autoplay ? 'autoplay' : ''} 
          ${muted || autoplay ? 'muted' : ''} 
          ${loop ? 'loop' : ''} 
          ${controls ? 'controls' : ''} 
          playsinline 
          ${poster ? `poster="${this.ensureProtocol(poster)}"` : ''}>
          <source src="${this.ensureProtocol(src)}" type="${isHLS ? 'application/x-mpegURL' : 'video/mp4'}">
        </video>
        ${!controls ? `
          <button class="play-pause-btn">
            <svg viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </button>
        ` : ''}
        ${loop ? `
          <div class="loop-indicator">
            <svg viewBox="0 0 24 24" fill="white">
              <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
            </svg>
            循环播放
          </div>
        ` : ''}
      </div>
    `;
    
    return videoHtml;
  }
  
  getAdditionalStyles() {
    return `
      .video-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
      }
      
      video {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      
      /* 视频控制按钮 - 右下角，默认可见 */
      .play-pause-btn {
        position: absolute;
        bottom: 15px;
        right: 15px;
        background: rgba(0, 0, 0, 0.5);
        border: none;
        color: white;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.8; /* 默认可见但略微透明 */
        transition: opacity 0.3s ease, background-color 0.2s ease;
        z-index: 2;
      }
      
      .video-wrapper:hover .play-pause-btn {
        opacity: 1; /* 鼠标悬停时完全不透明 */
      }
      
      .play-pause-btn:hover {
        background: rgba(0, 0, 0, 0.7);
      }
      
      .play-pause-btn svg {
        width: 24px;
        height: 24px;
      }
      
      @media (max-width: 768px) {
        .play-pause-btn {
          width: 36px;
          height: 36px;
          bottom: 12px;
          right: 12px;
        }
        
        .play-pause-btn svg {
          width: 20px;
          height: 20px;
        }
      }
      
      @media (max-width: 480px) {
        .play-pause-btn {
          width: 32px;
          height: 32px;
          bottom: 10px;
          right: 10px;
        }
      }
    `;
  }
  
  pauseMedia(shouldDispatch = true) {
    const video = this.shadowRoot.querySelector('video');
    if (video && !video.paused) {
      // 如果需要发送事件，设置标记
      this.userInitiatedPlay = shouldDispatch;
      video.pause();
    }
  }
  
  initMediaControls() {
    // 如果使用原生控件，不需要自定义控制
    if (this.hasAttribute('controls')) return;
    
    const video = this.shadowRoot.querySelector('video');
    const playPauseBtn = this.shadowRoot.querySelector('.play-pause-btn');
    
    if (!video || !playPauseBtn) return;
    
    // 检查是否是HLS格式
    const source = video.querySelector('source');
    const isHLS = source && source.src.includes('.m3u8');
    
    // 如果是HLS格式，加载HLS.js
    if (isHLS) {
      this.loadHLSScript().then(() => {
        this.setupHLS(video, source.src);
      });
    }
    
    // 更新播放/暂停按钮图标
    const updatePlayPauseIcon = () => {
      if (video.paused) {
        playPauseBtn.innerHTML = `
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        `;
      } else {
        playPauseBtn.innerHTML = `
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
          </svg>
        `;
      }
    };
    
    // 播放/暂停按钮点击事件
    playPauseBtn.addEventListener('click', () => {
      if (video.paused) {
        // 标记这是用户交互触发的播放
        this.userInitiatedPlay = true;
        video.play();
        // 发送播放事件通知其他组件
        this.dispatchPlayEvent();
      } else {
        video.pause();
      }
      updatePlayPauseIcon();
    });
    
    // 视频播放状态变化事件
    video.addEventListener('play', updatePlayPauseIcon);
    video.addEventListener('pause', updatePlayPauseIcon);
    video.addEventListener('ended', () => {
      // 如果没有设置循环，视频结束时更新图标
      if (!this._loopEnabled) {
        updatePlayPauseIcon();
      }
    });
    
    // 初始化按钮状态
    updatePlayPauseIcon();
  }
  
  setupHLS(video, src) {
    if (window.Hls && window.Hls.isSupported()) {
      const hls = new window.Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari原生支持HLS
      video.src = src;
    }
  }
  
  // 加载HLS.js脚本
  loadHLSScript() {
    return new Promise((resolve) => {
      if (window.Hls) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }
}

/**
 * YouTubeElement - 处理YouTube视频的基础组件
 */
class YouTubeElement extends BaseMediaElement {
  renderMedia() {
    const src = this.getAttribute('src');
    const autoplay = this.hasAttribute('autoplay');
    const loop = this.hasAttribute('loop');
    const muted = this.hasAttribute('muted');
    const controls = !this.hasAttribute('no-controls');
    
    if (!src) return '';
    
    // 提取YouTube视频ID
    const videoId = this.getYouTubeId(src);
    
    if (videoId) {
      // 为YouTube iframe添加参数
      const loopParam = loop ? '&loop=1&playlist=' + videoId : '';
      const controlsParam = controls ? '' : '&controls=0';
      
      return `
        <div class="youtube-wrapper">
          <iframe 
            class="js-youtube"
            src="https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1${autoplay ? '&autoplay=1' : ''}${muted || autoplay ? '&mute=1' : ''}${loopParam}${controlsParam}" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen>
          </iframe>
          ${loop ? `
            <div class="loop-indicator">
              <svg viewBox="0 0 24 24" fill="white">
                <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
              </svg>
              循环播放
            </div>
          ` : ''}
        </div>
      `;
    }
    
    return '';
  }
  
  getAdditionalStyles() {
    return `
      .youtube-wrapper {
        width: 100%;
        height: 100%;
        position: relative;
      }
      
      iframe {
        width: 100%;
        height: 100%;
        border: none;
      }
    `;
  }
  
  pauseMedia() {
    const iframe = this.shadowRoot.querySelector('iframe.js-youtube');
    if (iframe) {
      try {
        // 向YouTube iframe发送暂停命令
        iframe.contentWindow.postMessage(JSON.stringify({
          event: 'command',
          func: 'pauseVideo',
          args: ''
        }), '*');
      } catch (e) {
        console.warn('无法暂停YouTube视频:', e);
      }
    }
  }
  
  initMediaControls() {
    // 为YouTube iframe添加事件监听
    const iframe = this.shadowRoot.querySelector('iframe.js-youtube');
    if (!iframe) return;
    
    // 确保iframe加载完成后再添加事件监听
    iframe.addEventListener('load', () => {
      // 监听来自iframe的消息
      this.youtubeMessageHandler = (event) => {
        try {
          // 尝试解析消息数据
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          
          // 检查是否是来自当前iframe的YouTube事件
          if (data.event === 'onStateChange' && event.source === iframe.contentWindow) {
            // 状态1表示正在播放
            if (data.info === 1 && this.userInitiatedPlay) {
              // 发送播放事件通知其他组件
              this.dispatchPlayEvent();
            }
          }
        } catch (e) {
          // 忽略无法解析的消息
        }
      };
      
      window.addEventListener('message', this.youtubeMessageHandler);
      
      // 当iframe被点击时
      iframe.addEventListener('click', () => {
        // 标记这是用户交互触发的播放
        this.userInitiatedPlay = true;
      });
    });
  }
  
  // 从YouTube URL提取视频ID
  getYouTubeId(url) {
    if (!url) return null;
    
    try {
      // 处理短链接 (youtu.be)
      if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理标准链接 (youtube.com/watch?v=)
      if (url.includes('youtube.com/watch')) {
        const fullUrl = this.ensureProtocol(url);
        try {
          const urlObj = new URL(fullUrl);
          return urlObj.searchParams.get('v') || null;
        } catch (e) {
          console.error('解析YouTube URL失败:', e);
        }
      }
      
      // 处理嵌入链接 (youtube.com/embed/)
      if (url.includes('/embed/')) {
        const id = url.split('/embed/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理短视频链接 (youtube.com/shorts/)
      if (url.includes('/shorts/')) {
        const id = url.split('/shorts/')[1].split(/[?&#]/)[0];
        return id || null;
      }
      
      // 处理直接带有视频ID的链接
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    } catch (error) {
      console.error('获取YouTube ID时出错:', error);
      return null;
    }
  }
  
  // 组件断开连接时清理事件监听器
  disconnectedCallback() {
    // 调用父类方法
    super.disconnectedCallback();
    
    // 清理YouTube消息监听器
    if (this.youtubeMessageHandler) {
      window.removeEventListener('message', this.youtubeMessageHandler);
    }
  }
}

/**
 * MediaItem - 通用媒体项，业务组件，包含标题、描述等
 */
class MediaItem extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }
  
  connectedCallback() {
    // 获取属性
    const type = this.getAttribute('type') || 'image';
    const src = this.getAttribute('src');
    const title = this.getAttribute('title');
    const subtitle = this.getAttribute('subtitle');
    const aspectRatio = this.getAttribute('aspect-ratio') || '16/9';
    
    // 创建媒体元素
    let mediaElementTag;
    switch (type) {
      case 'video':
        mediaElementTag = 'video-element';
        break;
      case 'youtube':
        mediaElementTag = 'youtube-element';
        break;
      default:
        mediaElementTag = 'image-element';
    }
    
    // 构建HTML结构
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }
        
        .media-item {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        
        .media-container {
          width: 100%;
          height: auto;
          border-radius: var(--media-border-radius, 8px);
          margin-bottom: var(--media-margin-bottom, 15px);
          overflow: hidden;
          position: relative;
          aspect-ratio: ${aspectRatio};
        }
        
        .media-title {
          font-size: var(--title-font-size, 18px);
          font-weight: 600;
          margin-bottom: 5px;
          color: var(--title-color, #333);
          line-height: 1.3;
        }
        
        .media-subtitle {
          font-size: var(--subtitle-font-size, 14px);
          color: var(--subtitle-color, #666);
          margin-bottom: 10px;
          line-height: 1.4;
        }
        
        .media-description {
          font-size: var(--description-font-size, 16px);
          line-height: 1.6;
          color: var(--description-color, #333);
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .media-title {
            font-size: var(--title-font-size-mobile, 16px);
          }
          
          .media-subtitle {
            font-size: var(--subtitle-font-size-mobile, 13px);
          }
          
          .media-description {
            font-size: var(--description-font-size-mobile, 14px);
          }
        }
      </style>
      
      <div class="media-item">
        <div class="media-container">
          <${mediaElementTag} id="media-element" src="${src}"></${mediaElementTag}>
        </div>
        
        ${title ? `<div class="media-title">${title}</div>` : ''}
        ${subtitle ? `<div class="media-subtitle">${subtitle}</div>` : ''}
        
        <div class="media-description">
          <slot name="description"></slot>
        </div>
        
        <slot></slot>
      </div>
    `;
    
    // 获取媒体元素并转发属性
    const mediaElement = this.shadowRoot.getElementById('media-element');
    if (mediaElement) {
      // 转发相关属性
      if (this.hasAttribute('loop')) mediaElement.setAttribute('loop', '');
      if (this.hasAttribute('autoplay')) mediaElement.setAttribute('autoplay', '');
      if (this.hasAttribute('muted')) mediaElement.setAttribute('muted', '');
      if (this.hasAttribute('controls')) mediaElement.setAttribute('controls', '');
      if (this.hasAttribute('poster')) mediaElement.setAttribute('poster', this.getAttribute('poster'));
    }
  }
}

/**
 * BenefitItem - 特定业务场景的媒体项
 */
class NNBenefitItem extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }
  
  connectedCallback() {
    // 获取属性
    const index = this.getAttribute('index');
    const title = this.getAttribute('title');
    const mediaType = this.getAttribute('media-type') || 'image';
    const media = this.getAttribute('media');
    
    // 创建媒体元素
    let mediaElementTag;
    switch (mediaType) {
      case 'video':
        mediaElementTag = 'video-element';
        break;
      case 'youtube':
        mediaElementTag = 'youtube-element';
        break;
      default:
        mediaElementTag = 'image-element';
    }
    
    // 构建HTML结构
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }
        
        .benefit-item {
          display: flex;
          flex-direction: column;
          padding: 10px;
          max-width: 100%;
        }
        
        .benefit-media {
          width: 100%;
          height: auto;
          border-radius: 8px;
          margin-bottom: 25px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 1/1;
        }
        
        .benefit-number {
          font-size: 22px;
          font-weight: 600;
          margin-right: 4px;
          color: var(--theme-color, #FFA200);
        }
        
        .benefit-title-container {
          font-size: 22px;
          font-weight: 600;
          margin-bottom: 15px;
          display: flex;
          align-items: flex-start;
        }
        
        .benefit-title-text {
          flex: 1;
          line-height: 1.3;
          color: var(--theme-color, #FFA200);
          display: flex;
          justify-content: flex-start;
          transform: translateY(2px);
        }
        
        .benefit-description {
          font-size: 16px;
          line-height: 1.6;
          color: #333;
          margin-bottom: 15px;
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .benefit-title-container {
            font-size: 20px;
          }
          
          .benefit-number {
            font-size: 20px;
          }
          
          .benefit-description {
            font-size: 15px;
          }
          
          .benefit-media {
            aspect-ratio: 1/1;
            margin-bottom: 20px;
          }
        }
        
        /* 小屏幕手机适配 */
        @media (max-width: 480px) {
          .benefit-title-container {
            font-size: 18px;
          }
          
          .benefit-number {
            font-size: 18px;
          }
          
          .benefit-description {
            font-size: 14px;
          }
        }
      </style>
      
      <div class="benefit-item">
        <div class="benefit-media">
          <${mediaElementTag} id="media-element" src="${media}"></${mediaElementTag}>
        </div>
        
        <div class="benefit-title-container">
          <span class="benefit-number">${index}.</span>
          <span class="benefit-title-text">${title}</span>
        </div>
        
        <div class="benefit-description">
          <slot name="description"></slot>
        </div>
        
        <slot name="points"></slot>
      </div>
    `;
    
    // 获取媒体元素并转发属性
    const mediaElement = this.shadowRoot.getElementById('media-element');
    if (mediaElement) {
      // 转发相关属性
      if (this.hasAttribute('loop')) mediaElement.setAttribute('loop', '');
      if (this.hasAttribute('autoplay')) mediaElement.setAttribute('autoplay', '');
      if (this.hasAttribute('preview-image')) mediaElement.setAttribute('poster', this.getAttribute('preview-image'));
    }
  }
}

// 注册所有组件
customElements.define('image-element', ImageElement);
customElements.define('video-element', VideoElement);
customElements.define('youtube-element', YouTubeElement);
customElements.define('media-element', MediaItem);
customElements.define('benefit-item-n-n', NNBenefitItem);








/**
 * VideoElement - 增强版视频基础组件
 * 支持延迟加载、自动播放、封面图、播放控制等功能
 */
class LazyVideoElement extends BaseMediaElement {
constructor() {
  super();
  
  // 创建 IntersectionObserver 用于监测可见性
  this._observer = null;
  
  // 标记视频是否已加载
  this._videoLoaded = false;
  
  // 标记是否已经初始化
  this._initialized = false;
  
  // 标记视频是否正在播放
  this._isPlaying = false;
  
  // 调试模式
  this._debug = false;
}

/**
 * 定义需要观察的属性
 */
static get observedAttributes() {
  return ['src', 'poster', 'width', 'height', 'autoplay', 'loop', 'muted', 'controls', 'preload', 'control-position'];
}

/**
 * 当观察的属性变化时调用
 */
attributeChangedCallback(name, oldValue, newValue) {
  // 如果组件已初始化，则更新相关属性
  if (this._initialized && oldValue !== newValue) {
    if (name === 'src' && this._videoLoaded) {
      // 如果视频已加载，则更新视频源
      const video = this.shadowRoot.querySelector('video');
      if (video) {
        const source = video.querySelector('source');
        if (source) {
          source.src = this.ensureProtocol(newValue);
          video.load();
        }
      }
    } else if (name === 'poster') {
      // 更新封面图片
      const posterImg = this.shadowRoot.querySelector('.video-poster img');
      if (posterImg) {
        posterImg.src = this.ensureProtocol(newValue);
      }
    } else if (name === 'width' || name === 'height') {
      // 更新视频容器样式
      this.updateContainerStyle();
    } else if (name === 'control-position') {
      // 更新控制按钮位置
      this.updateControlPosition();
    }
  }
}

/**
 * 渲染媒体元素
 */
renderMedia() {
  // 获取属性
  const src = this.getAttribute('src');
  const poster = this.getAttribute('poster');
  const width = this.getAttribute('width');
  const height = this.getAttribute('height');
  const autoplay = this.hasAttribute('autoplay');
  const loop = this.hasAttribute('loop');
  const muted = this.hasAttribute('muted');
  const controls = this.hasAttribute('controls');
  const preload = this.getAttribute('preload') || 'none'; // 默认不预加载
  const controlPosition = this.getAttribute('control-position') || 'center'; // 默认中央位置
  
  this._log('渲染媒体元素:', { src, poster, width, height, autoplay, loop, muted, controls, preload, controlPosition });
  
  // 计算宽高比，用于占位
  let aspectRatio = '16/9'; // 默认宽高比
  if (width && height) {
    aspectRatio = `${width}/${height}`;
  }
  
  // 构建HTML结构 - 注意这里不直接加载视频
  return `
    <div class="video-wrapper control-position-${controlPosition}" style="--aspect-ratio: ${aspectRatio}">
      <!-- 封面图片层 -->
      <div class="video-poster ${!poster ? 'no-poster' : ''}">
        ${poster ? `<img src="${this.ensureProtocol(poster)}" alt="视频封面" loading="lazy">` : ''}
      </div>
      
      <!-- 视频容器 - 初始为空 -->
      <div class="video-container"></div>
      
      <!-- 统一的播放/暂停按钮 -->
      ${!controls ? `
        <button class="media-control-btn play-btn">
          <svg class="play-icon" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
          <svg class="pause-icon" viewBox="0 0 24 24" fill="white">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
          </svg>
        </button>
      ` : ''}
      
      <!-- 循环播放指示器 -->
      ${loop ? `
        <div class="loop-indicator">
          <svg viewBox="0 0 24 24" fill="white">
            <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
          </svg>
          循环播放
        </div>
      ` : ''}
    </div>
  `;
}

/**
 * 实现父类的 pauseMedia 方法 - 关键修复点
 */
pauseMedia(shouldDispatch = true) {
  const video = this.shadowRoot.querySelector('video');
  if (video && !video.paused) {
    this._log('暂停媒体 - shouldDispatch:', shouldDispatch);
    
    // 如果需要发送事件，设置标记
    if (shouldDispatch) {
      this.userInitiatedPlay = true;
    }
    
    video.pause();
    this._isPlaying = false;
    
    // 更新控制按钮状态
    const mediaControlBtn = this.shadowRoot.querySelector('.media-control-btn');
    if (mediaControlBtn) {
      mediaControlBtn.classList.remove('playing');
    }
  }
}

/**
 * 组件连接到DOM时调用
 */
connectedCallback() {
  super.connectedCallback();
  
  // 初始化延迟加载
  this.initLazyLoading();
  
  // 标记已初始化
  this._initialized = true;
  
  // 如果设置了预加载，则立即加载视频
  if (this.getAttribute('preload') === 'auto') {
    this._log('预加载设置为auto，立即加载视频');
    this.loadVideo();
  }
  
  // 更新容器样式
  this.updateContainerStyle();
  
  // 更新控制按钮位置
  this.updateControlPosition();
}

/**
 * 更新控制按钮位置
 */
updateControlPosition() {
  const wrapper = this.shadowRoot.querySelector('.video-wrapper');
  if (!wrapper) return;
  
  // 移除所有位置类
  wrapper.classList.remove('control-position-center', 'control-position-corner');
  
  // 添加当前位置类
  const position = this.getAttribute('control-position') || 'center';
  wrapper.classList.add(`control-position-${position}`);
}

/**
 * 组件从DOM断开时调用
 */
disconnectedCallback() {
  // 调用父类的断开连接方法
  super.disconnectedCallback();
  
  // 停止观察
  if (this._observer) {
    this._observer.disconnect();
    this._observer = null;
  }
  
  // 清理HLS实例
  if (this._hls) {
    this._hls.destroy();
    this._hls = null;
  }
  
  // 清理视频资源
  const video = this.shadowRoot.querySelector('video');
  if (video) {
    video.pause();
    video.src = '';
    video.load();
  }
  
  this._log('组件已断开连接');
}

/**
 * 更新容器样式
 */
updateContainerStyle() {
  const width = this.getAttribute('width');
  const height = this.getAttribute('height');
  
  if (width && height) {
    const wrapper = this.shadowRoot.querySelector('.video-wrapper');
    if (wrapper) {
      wrapper.classList.add('has-dimensions');
      wrapper.style.setProperty('--video-width', `${width}px`);
      wrapper.style.setProperty('--video-height', `${height}px`);
    }
  }
}

/**
 * 加载视频
 */
loadVideo() {
  if (this._videoLoaded) return;
  
  // 获取属性
  const src = this.getAttribute('src');
  if (!src) {
    this._log('错误: 没有设置src属性');
    return;
  }
  this._log('预加载视频控件');
  const autoplay = this.hasAttribute('autoplay');
  const loop = this.hasAttribute('loop');
  const muted = this.hasAttribute('muted');
  const controls = this.hasAttribute('controls');
  const preload = this.getAttribute('preload') || 'metadata'; // 默认加载元数据
  
  // 检查是否是HLS格式
  const isHLS = src.includes('.m3u8');
  
  // 创建视频元素
  const videoContainer = this.shadowRoot.querySelector('.video-container');
  if (!videoContainer) {
    this._log('错误: 找不到视频容器');
    return;
  }
  
  // 获取封面图片URL
  const poster = this.getAttribute('poster');
  
  this._log('加载视频:', {src, poster, autoplay, loop, muted, controls, preload});
  
  // 创建视频HTML - 设置preload属性以便开始缓冲
  const videoHtml = `
    <video 
      ${autoplay ? 'autoplay' : ''} 
      ${muted || autoplay ? 'muted' : ''} 
      ${loop ? 'loop' : ''} 
      ${controls ? 'controls' : ''} 
      preload="${preload}"
      ${poster ? `poster="${this.ensureProtocol(poster)}"` : ''}
      playsinline>
      <source src="${this.ensureProtocol(src)}" type="${isHLS ? 'application/x-mpegURL' : 'video/mp4'}">
    </video>
  `;
  
  // 添加视频元素
  videoContainer.innerHTML = videoHtml;
  
  // 获取视频元素
  const video = videoContainer.querySelector('video');
  if (!video) {
    this._log('错误: 视频元素创建失败');
    return;
  }
  
  // 检查是否需要HLS支持
  if (isHLS) {
    this.loadHLSScript().then(() => {
      this.setupHLS(video, src);
    });
  }
  
  // 添加事件监听器
  this._addVideoEventListeners(video);
  
  // 标记视频已加载
  this._videoLoaded = true;
  
  // 如果设置了自动播放，尝试播放
  if (autoplay) {
    this._log('尝试自动播放');
    this.playVideo();
  }
}

/**
 * 为视频添加事件监听器 - 修复状态同步
 */
_addVideoEventListeners(video) {
  if (!video) return;
  
  const mediaControlBtn = this.shadowRoot.querySelector('.media-control-btn');
  const poster = this.shadowRoot.querySelector('.video-poster');
  
  // 视频元数据加载完成时
  video.addEventListener('loadedmetadata', () => {
    this._log('视频元数据已加载');
  });
  
  // 视频可以播放时
  video.addEventListener('canplay', () => {
    this._log('视频可以播放');
    
    // 如果设置了自动播放且视频正在播放，尝试隐藏封面
    if (this.hasAttribute('autoplay') && !video.paused) {
      if (poster) {
        poster.classList.add('hidden');
      }
    }
  });
  
  // 视频开始缓冲时
  video.addEventListener('waiting', () => {
    this._log('视频缓冲中');
  });
  
  // 视频播放时 - 关键修复：正确调用父类方法
  video.addEventListener('play', () => {
    this._log('视频开始播放');
    this._isPlaying = true;
    
    // 隐藏封面 - 只有在真正播放时才隐藏
    if (poster) {
      poster.classList.add('hidden');
    }
    
    // 更新控制按钮状态
    if (mediaControlBtn) {
      mediaControlBtn.classList.add('playing');
      this._log('更新控制按钮状态为播放中');
    }
    
    // 调用父类的 dispatchPlayEvent 方法发送播放事件
    this.dispatchPlayEvent();
  });
  
  // 视频暂停时
  video.addEventListener('pause', () => {
    this._log('视频暂停');
    this._isPlaying = false;
    
    // 更新控制按钮状态
    if (mediaControlBtn) {
      mediaControlBtn.classList.remove('playing');
      this._log('更新控制按钮状态为暂停');
    }
  });
  
  // 视频结束时
  video.addEventListener('ended', () => {
    this._log('视频结束');
    this._isPlaying = false;
    
    if (!this.hasAttribute('loop')) {
      // 显示封面
      if (poster) {
        poster.classList.remove('hidden');
      }
      
      // 更新控制按钮状态
      if (mediaControlBtn) {
        mediaControlBtn.classList.remove('playing');
        this._log('更新控制按钮状态为暂停 (视频结束)');
      }
    }
  });
  
  // 视频错误时
  video.addEventListener('error', (e) => {
    this._log('视频错误:', e.target.error);
  });
  
  // 添加视频点击事件（如果没有控件）
  if (!this.hasAttribute('controls')) {
    video.addEventListener('click', () => {
      this._log('点击视频');
      this.togglePlayPause();
    });
  }
}

/**
 * 切换播放/暂停状态
 */
togglePlayPause() {
  const video = this.shadowRoot.querySelector('video');
  if (!video) {
    this._log('错误: 找不到视频元素');
    return;
  }
  
  if (video.paused) {
    this._log('尝试播放视频');
    this.playVideo();
  } else {
    this._log('尝试暂停视频');
    // 调用 pauseMedia 方法，传入 true 表示这是用户交互
    this.pauseMedia(true);
  }
}

/**
 * 播放视频 - 修复状态同步
 */
playVideo() {
  const video = this.shadowRoot.querySelector('video');
  if (!video) {
    this._log('错误: 找不到视频元素');
    return;
  }
  
  // 标记这是用户交互触发的播放 - 关键修复点
  this.userInitiatedPlay = true;
  
  this._log('尝试播放视频');
  
  // 尝试播放视频
  const playPromise = video.play();
  
  // 处理自动播放策略限制
  if (playPromise !== undefined) {
    playPromise.then(() => {
      this._log('播放成功');
      this._isPlaying = true;
      
      // 隐藏封面 - 只在播放成功后隐藏
      const poster = this.shadowRoot.querySelector('.video-poster');
      if (poster) {
        poster.classList.add('hidden');
      }
      
      // 更新控制按钮状态
      const mediaControlBtn = this.shadowRoot.querySelector('.media-control-btn');
      if (mediaControlBtn) {
        mediaControlBtn.classList.add('playing');
        this._log('更新控制按钮状态为播放中 (播放成功)');
      }
    }).catch(error => {
      // 自动播放被阻止，添加静音并重试
      this._log('播放被阻止，尝试静音播放:', error);
      video.muted = true;
      
      video.play().then(() => {
        this._log('静音播放成功');
        this._isPlaying = true;
        
        // 隐藏封面 - 只在播放成功后隐藏
        const poster = this.shadowRoot.querySelector('.video-poster');
        if (poster) {
          poster.classList.add('hidden');
        }
        
        // 更新控制按钮状态
        const mediaControlBtn = this.shadowRoot.querySelector('.media-control-btn');
        if (mediaControlBtn) {
          mediaControlBtn.classList.add('playing');
          this._log('更新控制按钮状态为播放中 (静音播放成功)');
        }
      }).catch(err => {
        this._log('静音播放也被阻止:', err);
        this._isPlaying = false;
        
        // 重置用户交互标记
        this.userInitiatedPlay = false;
        
        // 确保封面保持显示
        const poster = this.shadowRoot.querySelector('.video-poster');
        if (poster) {
          poster.classList.remove('hidden');
        }
        
        // 更新控制按钮状态
        const mediaControlBtn = this.shadowRoot.querySelector('.media-control-btn');
        if (mediaControlBtn) {
          mediaControlBtn.classList.remove('playing');
        }
      });
    });
  }
}

/**
 * 初始化延迟加载 - 修复视频加载逻辑
 */
initLazyLoading() {
  // 初始化 IntersectionObserver
  this._observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && !this._videoLoaded) {
      // 视频进入视口，总是加载视频以开始缓冲
      this._log('视频进入视口，开始加载');
      this.loadVideo();
    }
  }, {
    threshold: 0.1 // 当10%的元素可见时触发
  });
  
  // 开始观察
  this._observer.observe(this);
  
  // 添加控制按钮点击事件
  const mediaControlBtn = this.shadowRoot.querySelector('.media-control-btn');
  if (mediaControlBtn) {
    mediaControlBtn.addEventListener('click', (e) => {
      e.stopPropagation(); // 阻止事件冒泡
      this._log('点击控制按钮');
      
      // 如果视频尚未加载，则加载视频
      if (!this._videoLoaded) {
        this._log('视频尚未加载，开始加载');
        this.loadVideo();
        
        // 给视频一点时间加载
        setTimeout(() => {
          this._log('延迟播放');
          this.playVideo();
        }, 50);
      } else {
        // 如果已加载，则切换播放/暂停状态
        this._log('视频已加载，切换播放/暂停状态');
        this.togglePlayPause();
      }
    });
  }
  
  // 添加封面点击事件
  const poster = this.shadowRoot.querySelector('.video-poster');
  if (poster) {
    poster.addEventListener('click', () => {
      this._log('点击封面');
      
      // 如果视频尚未加载，则加载视频
      if (!this._videoLoaded) {
        this._log('视频尚未加载，开始加载');
        this.loadVideo();
        
        // 给视频一点时间加载
        setTimeout(() => {
          this._log('延迟播放');
          this.playVideo();
        }, 50);
      } else {
        // 如果已加载，则播放视频
        this._log('视频已加载，直接播放');
        this.playVideo();
      }
    });
  }
}

/**
 * 加载HLS脚本
 */
loadHLSScript() {
  return new Promise((resolve, reject) => {
    // 检查是否已加载
    if (window.Hls) {
      resolve();
      return;
    }
    
    // 创建脚本元素
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
    script.async = true;
    
    script.onload = () => {
      this._log('HLS.js 加载成功');
      resolve();
    };
    
    script.onerror = () => {
      this._log('HLS.js 加载失败');
      reject(new Error('Failed to load HLS.js'));
    };
    
    document.head.appendChild(script);
  });
}

/**
 * 设置HLS播放器
 */
setupHLS(video, src) {
  if (!window.Hls) {
    this._log('错误: HLS.js 未加载');
    return;
  }
  
  if (window.Hls.isSupported()) {
    const hls = new window.Hls({
      maxBufferLength: 30,
      maxMaxBufferLength: 60
    });
    
    hls.loadSource(this.ensureProtocol(src));
    hls.attachMedia(video);
    
    hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
      this._log('HLS 清单解析完成');
      
      // 如果设置了自动播放，尝试播放
      if (this.hasAttribute('autoplay')) {
        this.playVideo();
      }
    });
    
    hls.on(window.Hls.Events.ERROR, (event, data) => {
      if (data.fatal) {
        switch(data.type) {
          case window.Hls.ErrorTypes.NETWORK_ERROR:
            this._log('HLS 网络错误，尝试恢复...');
            hls.startLoad();
            break;
          case window.Hls.ErrorTypes.MEDIA_ERROR:
            this._log('HLS 媒体错误，尝试恢复...');
            hls.recoverMediaError();
            break;
          default:
            this._log('HLS 致命错误，无法恢复:', data);
            hls.destroy();
            break;
        }
      }
    });
    
    // 保存HLS实例以便清理
    this._hls = hls;
  } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
    // 原生支持HLS (iOS Safari)
    video.src = this.ensureProtocol(src);
  }
}

  
  /**
   * 获取额外的样式（续）
   */
  getAdditionalStyles() {
    return `
      .video-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }
      
      /* 视频封面 */
      .video-poster {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.1);
        z-index: 1;
        transition: opacity 0.3s ease;
        cursor: pointer;
      }
      
      .video-poster.no-poster {
        background-color: rgba(0, 0, 0, 0.2);
      }
      
      .video-poster.hidden {
        opacity: 0;
        pointer-events: none;
      }
      
      .video-poster img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        max-width: 100%;
        max-height: 100%;
      }
      
      /* 视频容器 */
      .video-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
      
      /* 视频元素 */
      video {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        object-fit: contain;
        cursor: pointer;
      }
      
      /* 自定义宽高时的视频样式 */
      .video-wrapper.has-dimensions video {
        width: var(--video-width);
        height: var(--video-height);
      }
      
      /* 媒体控制按钮 - 统一的播放/暂停按钮 */
      .media-control-btn {
        position: absolute;
        background: rgba(0, 0, 0, 0.6);
        border: none;
        color: white;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.9;
        transition: opacity 0.3s ease, transform 0.2s ease, background-color 0.2s ease;
        z-index: 2;
      }
      
      /* 中央位置的控制按钮 */
      .control-position-center .media-control-btn {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      
      /* 角落位置的控制按钮 */
      .control-position-corner .media-control-btn {
        bottom: 15px;
        right: 15px;
      }
      
      .media-control-btn:hover {
        opacity: 1;
        background: rgba(0, 0, 0, 0.8);
      }
      
      .control-position-center .media-control-btn:hover {
        transform: translate(-50%, -50%) scale(1.05);
      }
      
      .control-position-center .media-control-btn:active {
        transform: translate(-50%, -50%) scale(0.95);
      }
      
      .control-position-corner .media-control-btn:hover {
        transform: scale(1.05);
      }
      
      .control-position-corner .media-control-btn:active {
        transform: scale(0.95);
      }
      
      .media-control-btn svg {
        width: 30px;
        height: 30px;
      }
      
      /* 播放/暂停图标切换 */
      .media-control-btn .play-icon {
        display: block;
      }
      
      .media-control-btn .pause-icon {
        display: none;
      }
      
      .media-control-btn.playing .play-icon {
        display: none;
      }
      
      .media-control-btn.playing .pause-icon {
        display: block;
      }
      
      /* 视频播放时控制按钮的显示/隐藏 */
      .video-wrapper:not(:hover) .media-control-btn.playing {
        opacity: 0.1;
      }
      
      .video-wrapper:hover .media-control-btn {
        opacity: 1;
      }
      
      /* 循环播放指示器 */
      .loop-indicator {
        position: absolute;
        bottom: 15px;
        left: 15px;
        background: rgba(0, 0, 0, 0.5);
        color: white;
        padding: 5px 10px;
        border-radius: 15px;
        font-size: 12px;
        display: flex;
        align-items: center;
        opacity: 0.8;
        z-index: 2;
      }
      
      .loop-indicator svg {
        width: 14px;
        height: 14px;
        margin-right: 5px;
      }
      
      /* 响应式设计 */
      @media (max-width: 768px) {
        .media-control-btn {
          width: 50px;
          height: 50px;
        }
        
        .media-control-btn svg {
          width: 24px;
          height: 24px;
        }
        
        .control-position-corner .media-control-btn {
          width: 40px;
          height: 40px;
          bottom: 12px;
          right: 12px;
        }
        
        .control-position-corner .media-control-btn svg {
          width: 20px;
          height: 20px;
        }
        
        .loop-indicator {
          font-size: 11px;
          padding: 4px 8px;
          bottom: 12px;
          left: 12px;
        }
        
        .loop-indicator svg {
          width: 12px;
          height: 12px;
        }
      }
      
      @media (max-width: 480px) {
        .media-control-btn {
          width: 44px;
          height: 44px;
        }
        
        .media-control-btn svg {
          width: 20px;
          height: 20px;
        }
        
        .control-position-corner .media-control-btn {
          width: 36px;
          height: 36px;
          bottom: 10px;
          right: 10px;
        }
        
        .loop-indicator {
          font-size: 10px;
          padding: 3px 6px;
          bottom: 10px;
          left: 10px;
        }
      }
    `;
  }
  
  /**
   * 调试日志辅助方法
   */
  _log(...args) {
    if (this._debug) {
      console.log('[VideoElement]', ...args);
    }
  }
}

// 注册自定义元素
customElements.define('lazy-video-element', LazyVideoElement);



/**
 * BenefitItem - 特定业务场景的媒体项
 */
class LazyNNBenefitItem extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }
  
  connectedCallback() {
    // 获取属性
    const index = this.getAttribute('index');
    const title = this.getAttribute('title');
    const mediaType = this.getAttribute('media-type') || 'image';
    const media = this.getAttribute('media');
    const previewImage = this.getAttribute('preview-image');
    
    // 创建媒体元素
    let mediaElementTag;
    switch (mediaType) {
      case 'video':
        mediaElementTag = 'lazy-video-element';
        break;
      case 'youtube':
        mediaElementTag = 'youtube-element';
        break;
      default:
        mediaElementTag = 'image-element';
    }
    
    // 构建HTML结构
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }
        
        .benefit-item {
          display: flex;
          flex-direction: column;
          padding: 10px;
          max-width: 100%;
        }
        
        .benefit-media {
          width: 100%;
          height: auto;
          border-radius: 8px;
          margin-bottom: 25px;
          overflow: hidden;
          position: relative;
          aspect-ratio: 1/1;
        }
        
        .benefit-number {
          font-size: 22px;
          font-weight: 600;
          margin-right: 4px;
          color: var(--theme-color, #FFA200);
        }
        
        .benefit-title-container {
          font-size: 22px;
          font-weight: 600;
          margin-bottom: 15px;
          display: flex;
          align-items: flex-start;
        }
        
        .benefit-title-text {
          flex: 1;
          line-height: 1.3;
          color: var(--theme-color, #FFA200);
          display: flex;
          justify-content: flex-start;
          transform: translateY(2px);
        }
        
        .benefit-description {
          font-size: 16px;
          line-height: 1.6;
          color: #333;
          margin-bottom: 15px;
        }
        
        /* 响应式设计 */
        @media (max-width: 768px) {
          .benefit-title-container {
            font-size: 20px;
          }
          
          .benefit-number {
            font-size: 20px;
          }
          
          .benefit-description {
            font-size: 15px;
          }
          
          .benefit-media {
            aspect-ratio: 1/1;
            margin-bottom: 20px;
          }
        }
        
        /* 小屏幕手机适配 */
        @media (max-width: 480px) {
          .benefit-title-container {
            font-size: 18px;
          }
          
          .benefit-number {
            font-size: 18px;
          }
          
          .benefit-description {
            font-size: 14px;
          }
        }
      </style>
      
      <div class="benefit-item">
        <div class="benefit-media">
          <${mediaElementTag} 
            id="media-element" 
            src="${media}"
            ${previewImage ? `poster="${previewImage}"` : ''}
            ${this.hasAttribute('loop') ? 'loop' : ''}
            ${this.hasAttribute('autoplay') ? 'autoplay' : ''}
            ${this.hasAttribute('muted') ? 'muted' : ''}
          ></${mediaElementTag}>
        </div>
        
        <div class="benefit-title-container">
          <span class="benefit-number">${index}.</span>
          <span class="benefit-title-text">${title}</span>
        </div>
        
        <div class="benefit-description">
          <slot name="description"></slot>
        </div>
        
        <slot name="points"></slot>
      </div>
    `;
  }
}

customElements.define('lazy-benefit-item-n-n', LazyNNBenefitItem);





/**
 * OldBenefitItemHorizontal - 水平布局的特定业务场景媒体项
 * 逻辑与LazyNNBenefitItem保持一致，但保留原有样式
 */
class LazyOldBenefitItemHorizontal extends HTMLElement {
constructor() {
  super();
  this.attachShadow({ mode: 'open' });
}

connectedCallback() {
  // 获取属性
  const index = this.getAttribute('index') || '';
  const title = this.getAttribute('title');
  const mediaType = this.getAttribute('media-type') || 'image';
  const media = this.getAttribute('media');
  const previewImage = this.getAttribute('preview-image');
  
  // 创建媒体元素
  let mediaElementTag;
  switch (mediaType) {
    case 'video':
      mediaElementTag = 'lazy-video-element';
      break;
    case 'youtube':
      mediaElementTag = 'youtube-element';
      break;
    default:
      mediaElementTag = 'image-element';
  }
  
  // 构建HTML结构
  this.shadowRoot.innerHTML = `
    <style>
      /* 保留原有样式 */
      :host {
        display: block;
      }
      
      .benefit-item {
        display: flex;
        flex-direction: column;
        padding: 10px;
        max-width: 100%;
      }
      
      .benefit-media {
        width: 100%;
        height: auto;
        border-radius: 8px;
        margin-bottom: 25px;
        overflow: hidden;
        position: relative;
        display:flex;
        justify-content:flex-end;
      }
      
      .benefit-media img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
      }
      
      .benefit-media img:hover {
        transform: scale(1.03);
      }
      
      .benefit-number {
        font-size: 22px;
        font-weight: 600;
        margin-right: 4px;
        color: var(--theme-color, #FFA200);
      }
      
      .benefit-title-container {
        font-size: 22px;
        font-weight: 600;
        margin-bottom: 15px;
        display: flex;
        justify-content: center;
      }
      
      .benefit-title-text {
        line-height: 1.3;
        color: var(--theme-color, #FFA200);
        display: flex;
        justify-content: flex-start;
        transform: translateY(2px);
      }
      
      .benefit-description {
        font-size: 16px;
        line-height: 1.6;
        color: #333;
        margin-bottom: 15px;
      }
      
      /* 水平布局样式 */
      @media (min-width: 769px) {
        .benefit-item {
          flex-direction: row;
          align-items: center;
          gap: 40px;
        }
        
        .benefit-media {
          flex: 0 0 50%;
          margin-bottom: 0;
          max-width: 50%;
        }
        .benefit-media lazy-video-element{
          max-width:80%;
        }
        
        .benefit-content {
          flex: 0 0 45%;
        }
      }
      
      /* 响应式设计 */
      @media (max-width: 768px) {
        .benefit-title-container {
          font-size: 20px;
        }
        
        .benefit-number {
          font-size: 20px;
        }
        
        .benefit-description {
          font-size: 15px;
        }
        
        .benefit-media {
          aspect-ratio: 9/16;
          margin-bottom: 20px;
          justify-content:center;
        }
      }
      
      /* 小屏幕手机适配 */
      @media (max-width: 480px) {
        .benefit-title-container {
          font-size: 18px;
        }
        
        .benefit-number {
          font-size: 18px;
        }
        
        .benefit-description {
          font-size: 14px;
        }
      }
    </style>
    
    <div class="benefit-item">
      <div class="benefit-media">
        <${mediaElementTag} 
          id="media-element" 
          src="${media}"
          ${previewImage ? `poster="${previewImage}"` : ''}
          ${this.hasAttribute('loop') ? 'loop' : ''}
          ${this.hasAttribute('autoplay') ? 'autoplay' : ''}
          ${this.hasAttribute('muted') ? 'muted' : ''}
          control-position="center"
        ></${mediaElementTag}>
      </div>
      
      <div class="benefit-content">
        <div class="benefit-title-container">
          ${index ? `<span class="benefit-number">${index}.</span>` : ''}
          <span class="benefit-title-text">${title}</span>
        </div>
        
        <div class="benefit-description">
          <slot name="description"></slot>
        </div>
        
        <slot name="points"></slot>
      </div>
    </div>
  `;
}

// 确保URL有协议 - 保留这个工具方法以防组件内部需要使用
ensureProtocol(url) {
  if (!url) return '';
  
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  
  // 尝试修复不完整的URL
  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/')) {
    return `https://${url}`;
  }
  
  return url;
}
}

customElements.define('lazy-old-benefit-item-horizontal', LazyOldBenefitItemHorizontal);























class PetomgCustomerReviewsV0 extends HTMLElement {
      constructor() {
          super();
          this.attachShadow({ mode: 'open' });
      }
      
      connectedCallback() {
          this.render();
      }
      
      static get observedAttributes() {
          return ['primary-color', 'secondary-color', 'accent-color', 'shop-url', 'target'];
      }
      
      attributeChangedCallback(name, oldValue, newValue) {
          if (oldValue !== newValue) {
              this.render();
          }
      }
      
      getThemeColors() {
          return {
              primaryColor: this.getAttribute('primary-color') || '#3498db',
              primaryDark: this.darkenColor(this.getAttribute('primary-color') || '#3498db', 20),
              secondaryColor: this.getAttribute('secondary-color') || '#e67e22',
              secondaryDark: this.darkenColor(this.getAttribute('secondary-color') || '#e67e22', 20),
              accentColor: this.getAttribute('accent-color') || '#27ae60',
              accentLight: this.lightenColor(this.getAttribute('accent-color') || '#27ae60', 20)
          };
      }
      
      darkenColor(color, percent) {
          const num = parseInt(color.replace("#", ""), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) - amt;
          const G = (num >> 8 & 0x00FF) - amt;
          const B = (num & 0x0000FF) - amt;
          return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
              (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
              (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
      }
      
      lightenColor(color, percent) {
          const num = parseInt(color.replace("#", ""), 16);
          const amt = Math.round(2.55 * percent);
          const R = (num >> 16) + amt;
          const G = (num >> 8 & 0x00FF) + amt;
          const B = (num & 0x0000FF) + amt;
          return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
              (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
              (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
      }
      
      handleShopNowClick() {
        
          const shopUrl = this.getAttribute('shop-url');
          const target = this.getAttribute('target') || '_self';
          
          if (shopUrl) {
            console.log("handleShopNowClick->",shopUrl);
              // 如果设置了shop-url属性，直接跳转
              window.open(shopUrl, target);
          } else {
              // 如果没有设置shop-url，发送自定义事件
              this.dispatchEvent(new CustomEvent('shop-now', {
                  bubbles: true,
                  detail: {
                      component: this,
                      message: 'Shop Now button clicked'
                  }
              }));
          }
      }
      
      render() {
          const colors = this.getThemeColors();
          
          this.shadowRoot.innerHTML = `
              <style>
                  :host {
                      display: block;
                      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                      line-height: 1.6;
                      color: #2c3e50;
                      --primary-color: ${colors.primaryColor};
                      --primary-dark: ${colors.primaryDark};
                      --secondary-color: ${colors.secondaryColor};
                      --secondary-dark: ${colors.secondaryDark};
                      --accent-color: ${colors.accentColor};
                      --accent-light: ${colors.accentLight};
                      --text-primary: #2c3e50;
                      --text-secondary: #7f8c8d;
                      --background-light: #f8f9fa;
                      --white: #ffffff;
                      --shadow: rgba(0, 0, 0, 0.1);
                      --shadow-hover: rgba(0, 0, 0, 0.15);
                  }
                  
                  * {
                      margin: 0;
                      padding: 0;
                      box-sizing: border-box;
                  }
                  
                  .container {
                      max-width: 1200px;
                      margin: 0 auto;
                      padding: 15px;
                      background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                      min-height: 100vh;
                  }
                  
                  .hero {
                      text-align: center;
                      padding: 40px 20px;
                      background: var(--white);
                      border-radius: 20px;
                      margin-bottom: 30px;
                      box-shadow: 0 10px 30px var(--shadow);
                  }
                  
                  .hero h1 {
                      font-size: 48px;
                      color: var(--text-primary);
                      margin-bottom: 20px;
                      font-weight: 700;
                  }
                  
                  .hero p {
                      font-size: 20px;
                      color: var(--text-secondary);
                      margin-bottom: 30px;
                      max-width: 600px;
                      margin-left: auto;
                      margin-right: auto;
                  }
                  
                  .trust-badges {
                      display: grid;
                      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
                      gap: 20px;
                      margin-top: 30px;
                      max-width: 800px;
                      margin-left: auto;
                      margin-right: auto;
                  }
                  
                  .badge {
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      padding: 20px 10px;
                  }
                  
                  .badge-number {
                      font-size: 40px;
                      font-weight: bold;
                      color: var(--secondary-color);
                      margin-bottom: 5px;
                  }
                  
                  .badge-text {
                      color: var(--text-secondary);
                      font-weight: 600;
                      text-transform: uppercase;
                      letter-spacing: 1px;
                      font-size: 14px;
                      text-align: center;
                  }
                  
                  .satisfaction-section {
                      background: var(--white);
                      border-radius: 20px;
                      padding: 30px 20px;
                      margin-bottom: 30px;
                      box-shadow: 0 10px 30px var(--shadow);
                  }
                  
                  .section-title {
                      text-align: center;
                      font-size: 35px;
                      color: var(--text-primary);
                      margin-bottom: 30px;
                      font-weight: 600;
                  }
                  
                  .satisfaction-grid {
                      display: grid;
                      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                      gap: 20px;
                      margin-bottom: 30px;
                  }
                  
                  .satisfaction-card {
                      text-align: center;
                      padding: 25px 15px;
                      background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
                      color: var(--white);
                      border-radius: 15px;
                      transform: translateY(0);
                      transition: transform 0.3s ease;
                  }
                  
                  .satisfaction-card:hover {
                      transform: translateY(-5px);
                  }
                  
                  .satisfaction-emoji {
                      font-size: 48px;
                      margin-bottom: 15px;
                  }
                  
                  .satisfaction-percent {
                      font-size: 40px;
                      font-weight: bold;
                      margin-bottom: 10px;
                  }
                  
                  .satisfaction-label {
                      font-size: 18px;
                      opacity: 0.9;
                  }
                  
                  .testimonials-section {
                      margin-bottom: 30px;
                  }
                  
                  .testimonials-grid {
                      display: grid;
                      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                      gap: 20px;
                  }
                  
                  .testimonial {
                      background: var(--white);
                      padding: 25px;
                      border-radius: 15px;
                      box-shadow: 0 5px 20px var(--shadow);
                      position: relative;
                      transition: transform 0.3s ease;
                  }
                  
                  .testimonial:hover {
                      transform: translateY(-3px);
                      box-shadow: 0 8px 25px var(--shadow-hover);
                  }
                  
                  .testimonial::before {
                      content: '"';
                      font-size: 48px;
                      color: var(--primary-color);
                      position: absolute;
                      top: -5px;
                      left: 20px;
                      font-family: serif;
                  }
                  
                  .testimonial-text {
                      font-size: 17px;
                      line-height: 1.7;
                      margin-bottom: 20px;
                      padding-top: 15px;
                      color: var(--text-primary);
                  }
                  
                  .testimonial-author {
                      display: flex;
                      align-items: center;
                      gap: 15px;
                  }
                  
                  .author-avatar {
                      width: 45px;
                      height: 45px;
                      border-radius: 50%;
                      background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      color: var(--white);
                      font-weight: bold;
                      font-size: 18px;
                      flex-shrink: 0;
                  }
                  
                  .author-info {
                      flex: 1;
                      min-width: 0;
                  }
                  
                  .author-name {
                      font-weight: 600;
                      color: var(--text-primary);
                      font-size: 16px;
                  }
                  
                  .author-location {
                      color: var(--text-secondary);
                      font-size: 14px;
                  }
                  
                  .stars {
                      color: #f39c12;
                      font-size: 19px;
                      flex-shrink: 0;
                  }
                  
                  .why-choose-section {
                      background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
                      color: var(--white);
                      padding: 40px 30px;
                      border-radius: 20px;
                      margin-bottom: 30px;
                  }
                  
                  .features-grid {
                      display: grid;
                      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                      gap: 25px;
                      margin-top: 30px;
                  }
                  
                  .feature {
                      text-align: center;
                      padding: 20px 15px;
                  }
                  
                  .feature-icon {
                      font-size: 48px;
                      margin-bottom: 15px;
                  }
                  
                  .feature-title {
                      font-size: 21px;
                      font-weight: 600;
                      margin-bottom: 10px;
                  }
                  
                  .feature-desc {
                      opacity: 0.9;
                      line-height: 1.6;
                      font-size: 16px;
                  }
                  
                  .cta-section {
                      background: var(--white);
                      padding: 40px 30px;
                      border-radius: 20px;
                      text-align: center;
                      box-shadow: 0 10px 30px var(--shadow);
                  }
                  
                  .cta-section h2 {
                      font-size: 32px;
                      margin-bottom: 15px;
                      color: var(--text-primary);
                  }
                  
                  .cta-section p {
                      font-size: 18px;
                      color: var(--text-secondary);
                      margin-bottom: 15px;
                      max-width: 600px;
                      margin-left: auto;
                      margin-right: auto;
                  }
                  
                  .cta-button {
                      display: inline-block;
                      background: linear-gradient(135deg, var(--secondary-color), var(--secondary-dark));
                      color: var(--white);
                      padding: 15px 35px;
                      border-radius: 50px;
                      text-decoration: none;
                      font-size: 19px;
                      font-weight: 600;
                      transition: all 0.3s ease;
                      margin-top: 20px;
                      border: none;
                      cursor: pointer;
                  }
                  
                  .cta-button:hover {
                      transform: translateY(-2px);
                      box-shadow: 0 5px 15px rgba(230, 126, 34, 0.4);
                  }
                  
                  .guarantee {
                      background: var(--background-light);
                      padding: 25px;
                      border-radius: 15px;
                      margin: 25px 0;
                      text-align: center;
                      border: 2px dashed var(--accent-color);
                  }
                  
                  .guarantee-icon {
                      font-size: 40px;
                      color: var(--accent-color);
                      margin-bottom: 15px;
                  }
                  
                  .guarantee h3 {
                      font-size: 22px;
                      margin-bottom: 10px;
                      color: var(--text-primary);
                  }
                  
                  .guarantee p {
                      font-size: 16px;
                      color: var(--text-secondary);
                  }
                  
                  /* 平板适配 */
                  @media (max-width: 1024px) {
                      .hero h1 { font-size: 40px; }
                      .hero p { font-size: 18px; }
                      .section-title { font-size: 30px; }
                      .badge-number { font-size: 36px; }
                      .satisfaction-emoji { font-size: 40px; }
                      .satisfaction-percent { font-size: 36px; }
                      .feature-icon { font-size: 40px; }
                  }
                  
                  /* 手机适配 */
                  @media (max-width: 768px) {
                      .container { padding: 10px; }
                      .hero { padding: 30px 15px; margin-bottom: 20px; }
                      .hero h1 { font-size: 32px; }
                      .hero p { font-size: 16px; }
                      .section-title { font-size: 26px; }
                      .badge-number { font-size: 28px; }
                      .badge-text { font-size: 12px; }
                      .satisfaction-section, .why-choose-section, .cta-section { padding: 25px 20px; margin-bottom: 20px; }
                      .satisfaction-emoji { font-size: 32px; }
                      .satisfaction-percent { font-size: 28px; }
                      .satisfaction-label { font-size: 16px; }
                      .testimonials-grid { grid-template-columns: 1fr; gap: 15px; }
                      .testimonial { padding: 20px; }
                      .testimonial::before { font-size: 36px; }
                      .testimonial-text { font-size: 15px; }
                      .trust-badges { grid-template-columns: repeat(2, 1fr); gap: 15px; }
                      .satisfaction-grid { grid-template-columns: 1fr; gap: 15px; }
                      .features-grid { grid-template-columns: 1fr; gap: 20px; }
                      .feature-icon { font-size: 36px; }
                      .feature-title { font-size: 18px; }
                      .feature-desc { font-size: 14px; }
                      .testimonial-author { gap: 10px; }
                      .author-avatar { width: 40px; height: 40px; font-size: 16px; }
                      .author-name { font-size: 14px; }
                      .author-location { font-size: 12px; }
                      .stars { font-size: 16px; }
                      .cta-section h2 { font-size: 24px; }
                      .cta-section p { font-size: 16px; }
                      .cta-button { font-size: 16px; padding: 12px 30px; }
                      .guarantee-icon { font-size: 32px; }
                      .guarantee h3 { font-size: 18px; }
                      .guarantee p { font-size: 14px; }
                  }
                  
                  /* 小屏手机适配 */
                  @media (max-width: 480px) {
                      .hero { padding: 25px 10px; }
                      .hero h1 { font-size: 28px; }
                      .hero p { font-size: 14px; }
                      .section-title { font-size: 22px; }
                      .satisfaction-section, .why-choose-section, .cta-section { padding: 20px 15px; }
                      .testimonial { padding: 15px; }
                      .testimonial-text { font-size: 14px; }
                      .trust-badges { grid-template-columns: 1fr; gap: 10px; }
                      .badge { padding: 15px 5px; }
                      .badge-number { font-size: 24px; }
                      .badge-text { font-size: 11px; }
                      .cta-section h2 { font-size: 20px; }
                      .cta-section p { font-size: 14px; }
                      .cta-button { font-size: 14px; padding: 10px 25px; }
                  }
              </style>
              
              <div class="container">
                  <div class="hero">
                      <h1>🐱 Real Customer Reviews</h1>
                      <p>Authentic feedback from cat parents worldwide who trust Petomg quality</p>
                      
                      <div class="trust-badges">
                          <div class="badge">
                              <div class="badge-number">37+</div>
                              <div class="badge-text">Real Reviews</div>
                          </div>
                          <div class="badge">
                              <div class="badge-number">92%</div>
                              <div class="badge-text">Satisfied</div>
                          </div>
                          <div class="badge">
                              <div class="badge-number">84%</div>
                              <div class="badge-text">Recommend</div>
                          </div>
                          <div class="badge">
                              <div class="badge-number">98%</div>
                              <div class="badge-text">Participation</div>
                          </div>
                      </div>
                  </div>
                  
                  <div class="satisfaction-section">
                      <h2 class="section-title">Customer Satisfaction</h2>
                      <div class="satisfaction-grid">
                          <div class="satisfaction-card">
                              <div class="satisfaction-emoji">😍</div>
                              <div class="satisfaction-percent">68%</div>
                              <div class="satisfaction-label">Very Satisfied</div>
                          </div>
                          <div class="satisfaction-card">
                              <div class="satisfaction-emoji">😊</div>
                              <div class="satisfaction-percent">24%</div>
                              <div class="satisfaction-label">Quite Satisfied</div>
                          </div>
                          <div class="satisfaction-card">
                              <div class="satisfaction-emoji">👍</div>
                              <div class="satisfaction-percent">84%</div>
                              <div class="satisfaction-label">Would Recommend</div>
                          </div>
                      </div>
                  </div>
                  
                  <div class="testimonials-section">
                      <h2 class="section-title">What Our Customers Say</h2>
                      <div class="testimonials-grid">
                          <div class="testimonial">
                              <div class="testimonial-text">
                                  I absolutely love this cat furniture! It's beautiful, well-made, and the cats started climbing immediately!
                              </div>
                              <div class="testimonial-author">
                                  <div class="author-avatar">L</div>
                                  <div class="author-info">
                                      <div class="author-name">Lisa M.</div>
                                      <div class="author-location">California, USA</div>
                                  </div>
                                  <div class="stars">⭐⭐⭐⭐⭐</div>
                              </div>
                          </div>
                          
                          <div class="testimonial">
                              <div class="testimonial-text">
                                  The wooden design is fantastic, much better for the home than all those fabric cat trees that are hard to clean. Keep it up!
                              </div>
                              <div class="testimonial-author">
                                  <div class="author-avatar">D</div>
                                  <div class="author-info">
                                      <div class="author-name">Damian K.</div>
                                      <div class="author-location">Texas, USA</div>
                                  </div>
                                  <div class="stars">⭐⭐⭐⭐⭐</div>
                              </div>
                          </div>
                          
                          <div class="testimonial">
                              <div class="testimonial-text">
                                  After installation, my cats absolutely love their new "habitat". The hammocks are especially popular - they hardly leave them!
                              </div>
                              <div class="testimonial-author">
                                  <div class="author-avatar">B</div>
                                  <div class="author-info">
                                      <div class="author-name">Bobbi R.</div>
                                      <div class="author-location">Florida, USA</div>
                                  </div>
                                  <div class="stars">⭐⭐⭐⭐⭐</div>
                              </div>
                          </div>
                          
                          <div class="testimonial">
                              <div class="testimonial-text">
                                  Excellent product, well-made, easy to install! My granddaughter's cats all love this cat castle!
                              </div>
                              <div class="testimonial-author">
                                  <div class="author-avatar">M</div>
                                  <div class="author-info">
                                      <div class="author-name">Mary S.</div>
                                      <div class="author-location">New York, USA</div>
                                  </div>
                                  <div class="stars">⭐⭐⭐⭐⭐</div>
                              </div>
                          </div>
                          
                          <div class="testimonial">
                              <div class="testimonial-text">
                                  Customer service made me a lifetime customer. Not only did they reply to my email immediately, but they were so professional and my issue was resolved instantly.
                              </div>
                              <div class="testimonial-author">
                                  <div class="author-avatar">M</div>
                                  <div class="author-info">
                                      <div class="author-name">Ming L.</div>
                                      <div class="author-location">Vancouver, Canada</div>
                                  </div>
                                  <div class="stars">⭐⭐⭐⭐⭐</div>
                              </div>
                          </div>
                          
                          <div class="testimonial">
                              <div class="testimonial-text">
                                  Excellent customer service! Always satisfied with your products and my cats love their huge cat tree.
                              </div>
                              <div class="testimonial-author">
                                  <div class="author-avatar">D</div>
                                  <div class="author-info">
                                      <div class="author-name">Derek W.</div>
                                      <div class="author-location">Sydney, Australia</div>
                                  </div>
                                  <div class="stars">⭐⭐⭐⭐⭐</div>
                              </div>
                          </div>
                      </div>
                  </div>
                  
                  <div class="why-choose-section">
                      <h2 class="section-title">Why Choose Petomg?</h2>
                      <div class="features-grid">
                          <div class="feature">
                              <div class="feature-icon">🎨</div>
                              <div class="feature-title">Unique Design</div>
                              <div class="feature-desc">92% of customers choose us for our distinctive, modern designs that blend perfectly with your home decor</div>
                          </div>
                          <div class="feature">
                              <div class="feature-icon">🏗️</div>
                              <div class="feature-title">Premium Materials</div>
                              <div class="feature-desc">65% of customers appreciate our high-quality wood construction - durable, safe, and eco-friendly</div>
                          </div>
                          <div class="feature">
                              <div class="feature-icon">💰</div>
                              <div class="feature-title">Fair Pricing</div>
                              <div class="feature-desc">49% of customers find our pricing reasonable - exceptional value for premium quality furniture</div>
                          </div>
                          <div class="feature">
                              <div class="feature-icon">🛠️</div>
                              <div class="feature-title">Easy Assembly</div>
                              <div class="feature-desc">Complete installation guides and tools included - create your cat's paradise with ease</div>
                          </div>
                      </div>
                  </div>
                  
                  <div class="guarantee">
                      <div class="guarantee-icon">🛡️</div>
                      <h3>Quality Guarantee</h3>
                      <p>We're confident in our product quality and offer comprehensive after-sales service and return policy for worry-free shopping</p>
                  </div>
                  
                  <div class="cta-section">
                      <h2>Join Our Satisfied Customers</h2>
                      <p>Over 90% of our customers are satisfied with our products, and 84% would recommend us to friends</p>
                      <p>Give your cat the best furniture they deserve!</p>
                      <button class="cta-button" onclick="this.getRootNode().host.handleShopNowClick()">Shop Now</button>
                      
                      <div style="margin-top: 30px; color: var(--text-secondary);">
                          <small>* Based on 37 authentic customer reviews | Data collected through April 2025</small>
                      </div>
                  </div>
              </div>
          `;
      }
  }

  customElements.define('petomg-customer-reviews-v0', PetomgCustomerReviewsV0);
  







  class PetomgCustomerReviewsV1 extends HTMLElement {
      constructor() {
          super();
          this.attachShadow({ mode: 'open' });
      }

      static get observedAttributes() {
          return ['primary-color', 'secondary-color', 'accent-color', 'success-color'];
      }

      attributeChangedCallback(name, oldValue, newValue) {
          if (oldValue !== newValue) {
              this.updateTheme();
          }
      }

      connectedCallback() {
          this.render();
          this.updateTheme();
      }

      getThemeColors() {
          return {
              primary: this.getAttribute('primary-color') || '#667eea',
              secondary: this.getAttribute('secondary-color') || '#764ba2',
              accent: this.getAttribute('accent-color') || '#27ae60',
              success: this.getAttribute('success-color') || '#2ecc71'
          };
      }

      updateTheme() {
          if (!this.shadowRoot) return;

          const colors = this.getThemeColors();
          const root = this.shadowRoot.querySelector('.survey-container');

          if (root) {
              root.style.setProperty('--primary-color', colors.primary);
              root.style.setProperty('--secondary-color', colors.secondary);
              root.style.setProperty('--accent-color', colors.accent);
              root.style.setProperty('--success-color', colors.success);
          }
      }

      render() {
          const colors = this.getThemeColors();
          this.shadowRoot.innerHTML = `
              <style>
                  :host {
                      display: block;
                      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                      line-height: 1.6;
                      color: #333;
                  }
                  
                  .survey-container {
                      --primary-color: ${colors.primary};
                      --secondary-color: ${colors.secondary};
                      --accent-color: ${colors.accent};
                      --success-color: ${colors.success};
                      
                      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
                      border-radius: 20px;
                      overflow: hidden;
                      box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                  }
                  
                  .header {
                      text-align: center;
                      color: white;
                      padding: 40px 20px;
                  }
                  
                  .header h1 {
                      font-size: 48px;
                      margin: 0 0 10px 0;
                      font-weight: 700;
                      text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
                  }
                  
                  .header p {
                      font-size: 19px;
                      opacity: 0.9;
                      margin: 0 0 20px 0;
                  }
                  
                  .survey-stats {
                      display: flex;
                      justify-content: center;
                      gap: 30px;
                      margin-top: 20px;
                      flex-wrap: wrap;
                  }
                  
                  .stat-item {
                      background: rgba(255,255,255,0.2);
                      padding: 15px 25px;
                      border-radius: 10px;
                      backdrop-filter: blur(10px);
                      text-align: center;
                      min-width: 120px;
                  }
                  
                  .stat-number {
                      font-size: 32px;
                      font-weight: bold;
                      display: block;
                  }
                  
                  .stat-label {
                      font-size: 14px;
                      opacity: 0.8;
                  }
                  
                  .main-content {
                      background: white;
                  }
                  
                  .section {
                      padding: 40px;
                      border-bottom: 1px solid #eee;
                  }
                  
                  .section:last-child {
                      border-bottom: none;
                  }
                  
                  .section-title {
                      font-size: 32px;
                      color: #2c3e50;
                      margin: 0 0 30px 0;
                      text-align: center;
                      position: relative;
                  }
                  
                  .section-title::after {
                      content: '';
                      position: absolute;
                      bottom: -10px;
                      left: 50%;
                      transform: translateX(-50%);
                      width: 60px;
                      height: 3px;
                      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
                      border-radius: 2px;
                  }
                  
                  .hero-stats {
                      display: grid;
                      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                      gap: 25px;
                      margin-bottom: 40px;
                  }
                  
                  .hero-stat {
                      background: linear-gradient(135deg, var(--accent-color) 0%, var(--success-color) 100%);
                      color: white;
                      padding: 30px;
                      border-radius: 15px;
                      text-align: center;
                      box-shadow: 0 10px 25px rgba(0,0,0,0.2);
                      transition: transform 0.3s ease, box-shadow 0.3s ease;
                  }
                  
                  .hero-stat:hover {
                      transform: translateY(-5px);
                      box-shadow: 0 15px 35px rgba(0,0,0,0.3);
                  }
                  
                  .hero-number {
                      font-size: 48px;
                      font-weight: bold;
                      margin: 0 0 10px 0;
                      text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
                  }
                  
                  .hero-label {
                      font-size: 18px;
                      opacity: 0.95;
                      font-weight: 500;
                      margin: 0;
                  }
                  
                  .testimonial-section {
                      background: linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%);
                      padding: 50px 40px;
                      text-align: center;
                  }
                  
                  .testimonial-grid {
                      display: grid;
                      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                      gap: 30px;
                      margin-top: 30px;
                  }
                  
                  .testimonial-card {
                      background: white;
                      padding: 30px;
                      border-radius: 15px;
                      box-shadow: 0 10px 25px rgba(0,0,0,0.08);
                      position: relative;
                      border-top: 4px solid var(--accent-color);
                  }
                  
                  .testimonial-text {
                      font-size: 18px;
                      color: #2c3e50;
                      line-height: 1.6;
                      margin: 0 0 20px 0;
                      font-style: italic;
                  }
                  
                  .testimonial-author {
                      color: #7f8c8d;
                      font-weight: 500;
                      margin: 0;
                  }
                  
                  .quote-icon {
                      position: absolute;
                      top: -10px;
                      left: 20px;
                      background: var(--accent-color);
                      color: white;
                      width: 40px;
                      height: 40px;
                      border-radius: 50%;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: 24px;
                  }
                  
                  .success-metrics {
                      display: grid;
                      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                      gap: 25px;
                      margin-bottom: 40px;
                  }
                  
                  .success-card {
                      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
                      color: white;
                      padding: 30px;
                      border-radius: 15px;
                      text-align: center;
                      position: relative;
                      overflow: hidden;
                  }
                  
                  .success-card::before {
                      content: '';
                      position: absolute;
                      top: -50%;
                      right: -50%;
                      width: 100%;
                      height: 100%;
                      background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
                      pointer-events: none;
                  }
                  
                  .success-number {
                      font-size: 40px;
                      font-weight: bold;
                      margin: 0 0 10px 0;
                  }
                  
                  .success-label {
                      font-size: 16px;
                      opacity: 0.95;
                      margin: 0 0 8px 0;
                  }
                  
                  .success-desc {
                      font-size: 14px;
                      opacity: 0.8;
                      line-height: 1.4;
                      margin: 0;
                  }
                  
                  .data-insights {
                      display: grid;
                      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                      gap: 30px;
                      margin-top: 30px;
                  }
                  
                  .insight-card {
                      background: #fff;
                      padding: 30px;
                      border-radius: 15px;
                      box-shadow: 0 10px 25px rgba(0,0,0,0.05);
                      border: 1px solid #f0f0f0;
                  }
                  
                  .insight-title {
                      font-size: 21px;
                      color: #2c3e50;
                      margin: 0 0 20px 0;
                      text-align: center;
                      font-weight: 600;
                  }
                  
                  .data-points {
                      display: flex;
                      flex-direction: column;
                      gap: 15px;
                  }
                  
                  .data-point {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      padding: 15px;
                      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                      border-radius: 10px;
                      border-left: 4px solid var(--data-color);
                  }
                  
                  .data-point.love {
                      --data-color: var(--accent-color);
                  }
                  
                  .data-point.like {
                      --data-color: var(--primary-color);
                  }
                  
                  .data-point.neutral {
                      --data-color: #f39c12;
                  }
                  
                  .data-point.value {
                      --data-color: var(--secondary-color);
                  }
                  
                  .data-label {
                      font-size: 16px;
                      font-weight: 500;
                      color: #2c3e50;
                      margin: 0;
                  }
                  
                  .data-value {
                      font-size: 19px;
                      font-weight: bold;
                      color: var(--data-color);
                      margin: 0;
                  }
                  
                  .insight-summary {
                      margin-top: 20px;
                      padding: 15px;
                      background: linear-gradient(135deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.05) 100%);
                      border-radius: 10px;
                      text-align: center;
                      border-left: 4px solid var(--accent-color);
                  }
                  
                  .summary-text {
                      font-size: 16px;
                      font-weight: 600;
                      color: var(--accent-color);
                      margin: 0;
                  }
                  
                  .cta-section {
                      background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
                      color: white;
                      padding: 50px 40px;
                      text-align: center;
                      margin: 30px 0;
                      border-radius: 20px;
                  }
                  
                  .cta-title {
                      font-size: 40px;
                      margin: 0 0 20px 0;
                      font-weight: 700;
                  }
                  
                  .cta-subtitle {
                      font-size: 19px;
                      margin: 0 0 30px 0;
                      opacity: 0.9;
                  }
                  
                  .cta-button {
                      display: inline-block;
                      background: var(--accent-color);
                      color: white;
                      padding: 15px 40px;
                      border-radius: 50px;
                      text-decoration: none;
                      font-size: 18px;
                      font-weight: 600;
                      transition: all 0.3s ease;
                      box-shadow: 0 10px 25px rgba(0,0,0,0.2);
                  }
                  
                  .cta-button:hover {
                      background: var(--success-color);
                      transform: translateY(-3px);
                      box-shadow: 0 15px 35px rgba(0,0,0,0.3);
                  }
                  
                  .trust-badges {
                      display: flex;
                      justify-content: center;
                      gap: 40px;
                      margin-top: 40px;
                      flex-wrap: wrap;
                  }
                  
                  .trust-badge {
                      display: flex;
                      align-items: center;
                      gap: 10px;
                      color: white;
                      opacity: 0.9;
                  }
                  
                  .trust-icon {
                      font-size: 24px;
                  }
                  
                  .guarantee-section {
                      background: linear-gradient(135deg, var(--success-color) 0%, var(--accent-color) 100%);
                      color: white;
                      padding: 40px;
                      border-radius: 15px;
                      text-align: center;
                      margin: 30px 0;
                  }
                  
                  .guarantee-title {
                      font-size: 29px;
                      margin: 0 0 15px 0;
                      font-weight: 600;
                  }
                  
                  .guarantee-text {
                      font-size: 18px;
                      opacity: 0.95;
                      line-height: 1.5;
                      margin: 0;
                  }
                  
                  .footer {
                      text-align: center;
                      padding: 40px;
                      color: white;
                      background: rgba(0,0,0,0.1);
                      margin-top: 40px;
                      border-radius: 15px;
                  }
                  
                  .footer p {
                      margin: 0 0 5px 0;
                  }
                  
                  @media (max-width: 768px) {
                      .header h1 {
                          font-size: 32px;
                      }
                      
                      .survey-stats {
                          flex-direction: column;
                          align-items: center;
                      }
                      
                      .data-insights {
                          grid-template-columns: 1fr;
                      }
                      
                      .section {
                          padding: 20px;
                      }
                      
                      .testimonial-section {
                          padding: 30px 20px;
                      }
                      
                      .trust-badges {
                          flex-direction: column;
                          align-items: center;
                          gap: 20px;
                      }
                      
                      .cta-title {
                          font-size: 32px;
                      }
                      
                      .cta-section {
                          padding: 30px 20px;
                      }
                  }
              </style>
              <div class="survey-container">
                  <div class="header">
                      <h1>Our Customers LOVE Us!</h1>
                      <p>Real Results from Happy Cat Owners - Q2 2025</p>
                      <div class="survey-stats">
                          <div class="stat-item">
                              <span class="stat-number">26</span>
                              <span class="stat-label">Happy Customers</span>
                          </div>
                          <div class="stat-item">
                              <span class="stat-number">88%</span>
                              <span class="stat-label">Cats Love It</span>
                          </div>
                          <div class="stat-item">
                              <span class="stat-number">81%</span>
                              <span class="stat-label">Smooth Experience</span>
                          </div>
                      </div>
                  </div>
                  
                  <div class="main-content">
                      <div class="section">
                          <h2 class="section-title">Outstanding Customer Satisfaction</h2>
                          <div class="hero-stats">
                              <div class="hero-stat">
                                  <div class="hero-number">88%</div>
                                  <div class="hero-label">Cats Love Our Product</div>
                              </div>
                              <div class="hero-stat">
                                  <div class="hero-number">73%</div>
                                  <div class="hero-label">Expectations Met</div>
                              </div>
                              <div class="hero-stat">
                                  <div class="hero-number">81%</div>
                                  <div class="hero-label">Smooth Purchase</div>
                              </div>
                              <div class="hero-stat">
                                  <div class="hero-number">85%</div>
                                  <div class="hero-label">Problem-Free</div>
                              </div>
                          </div>
                      </div>
                      
                      <div class="testimonial-section">
                          <h2 class="section-title">What Our Customers Say</h2>
                          <div class="testimonial-grid">
                              <div class="testimonial-card">
                                  <div class="quote-icon">"</div>
                                  <div class="testimonial-text">
                                      "My cat absolutely loves this product! She uses it every day and seems so much happier. 
                                      The quality exceeded my expectations."
                                  </div>
                                  <div class="testimonial-author">- Sarah M., Verified Customer</div>
                              </div>
                              <div class="testimonial-card">
                                  <div class="quote-icon">"</div>
                                  <div class="testimonial-text">
                                      "Super easy to set up and my cats took to it immediately. 
                                      Great value for money and excellent customer service!"
                                  </div>
                                  <div class="testimonial-author">- Mike R., Verified Customer</div>
                              </div>
                              <div class="testimonial-card">
                                  <div class="quote-icon">"</div>
                                  <div class="testimonial-text">
                                      "I was amazed at how quickly my shy cat started using it. 
                                      The purchasing process was smooth and delivery was fast."
                                  </div>
                                  <div class="testimonial-author">- Jennifer L., Verified Customer</div>
                              </div>
                          </div>
                      </div>
                      
                      <div class="section">
                          <h2 class="section-title">Proven Results That Matter</h2>
                          <div class="success-metrics">
                              <div class="success-card">
                                  <div class="success-number">57%</div>
                                  <div class="success-label">Cats Absolutely Love It</div>
                                  <div class="success-desc">More than half of cats show immediate enthusiasm</div>
                              </div>
                              <div class="success-card">
                                  <div class="success-number">73%</div>
                                  <div class="success-label">Expectations Perfectly Met</div>
                                  <div class="success-desc">Customers get exactly what they expected</div>
                              </div>
                              <div class="success-card">
                                  <div class="success-number">69%</div>
                                  <div class="success-label">Great Value for Money</div>
                                  <div class="success-desc">Customers feel they made a smart purchase</div>
                              </div>
                              <div class="success-card">
                                  <div class="success-number">35%</div>
                                  <div class="success-label">Super Easy Setup</div>
                                  <div class="success-desc">Many customers found installation effortless</div>
                              </div>
                          </div>
                          
                          <div class="data-insights">
                              <div class="insight-card">
                                  <h3 class="insight-title">Cat Happiness Levels</h3>
                                  <div class="data-points">
                                      <div class="data-point love">
                                          <span class="data-label">Absolutely Love It!</span>
                                          <span class="data-value">57.7%</span>
                                      </div>
                                      <div class="data-point like">
                                          <span class="data-label">Like It</span>
                                          <span class="data-value">30.8%</span>
                                      </div>
                                      <div class="data-point neutral">
                                          <span class="data-label">Getting Used to It</span>
                                          <span class="data-value">11.5%</span>
                                      </div>
                                  </div>
                                  <div class="insight-summary">
                                      <div class="summary-text">88% of cats show positive response!</div>
                                  </div>
                              </div>
                              
                              <div class="insight-card">
                                  <h3 class="insight-title">Customer Value Perception</h3>
                                  <div class="data-points">
                                      <div class="data-point love">
                                          <span class="data-label">Great Value</span>
                                          <span class="data-value">73.1%</span>
                                      </div>
                                      <div class="data-point like">
                                          <span class="data-label">Worth the Investment</span>
                                          <span class="data-value">19.2%</span>
                                      </div>
                                      <div class="data-point value">
                                          <span class="data-label">Premium Quality</span>
                                          <span class="data-value">7.7%</span>
                                      </div>
                                  </div>
                                  <div class="insight-summary">
                                      <div class="summary-text">92% see great value in our products!</div>
                                  </div>
                              </div>
                          </div>
                      </div>
                      
                      <div class="guarantee-section">
                          <div class="guarantee-title">🛡️ 100% Satisfaction Guarantee</div>
                          <div class="guarantee-text">
                              Join thousands of happy customers! If your cat doesn't love our product within 30 days, 
                              we'll refund every penny. No questions asked.
                          </div>
                      </div>
                      
                      <div class="cta-section">
                          <div class="cta-title">Ready to Make Your Cat Happy?</div>
                          <div class="cta-subtitle">
                              Join 88% of cats who absolutely love our products. 
                              Your feline friend deserves the best!
                          </div>
                          <a href="/" class="cta-button">Shop Now - Free Shipping</a>
                          
                          <div class="trust-badges">
                              <div class="trust-badge">
                                  <span class="trust-icon">🚚</span>
                                  <span>Free Shipping</span>
                              </div>
                              <div class="trust-badge">
                                  <span class="trust-icon">↩️</span>
                                  <span>30-Day Returns</span>
                              </div>
                              <div class="trust-badge">
                                  <span class="trust-icon">⭐</span>
                                  <span>5-Star Reviews</span>
                              </div>
                              <div class="trust-badge">
                                  <span class="trust-icon">🔒</span>
                                  <span>Secure Checkout</span>
                              </div>
                          </div>
                      </div>
                  </div>
                  
                  <div class="footer">
                      <p>Based on survey of 26 verified customers • Q2 2025</p>
                      <p>Results may vary. Individual cat preferences differ.</p>
                  </div>
              </div>
          `;
      }
  }

  // 注册Web Component
  customElements.define('petomg-customer-reviews-v1', PetomgCustomerReviewsV1);